import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import {
  Trophy, TrendingUp, Share2, Users, Zap, Crown, X,
  Sparkles, Download, Info, Bell, Copy
} from 'lucide-react';
import html2canvas from 'html2canvas';
import CaptionRemixer from './CaptionRemixer';
import { supabase, supabaseUrl, supabaseAnonKey } from './supabaseClient';            // ← only once
import { messagingPromise, getToken, deleteToken, onMessage } from './firebase';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import SummonsLanding from './components/SummonsLanding';
import CreatorAnalytics from './components/CreatorAnalytics';
import GrandTrialSeeder from './components/GrandTrialSeeder';
import QuickTrialSeeder from './components/QuickTrialSeeder';
import VerdictVault from './pages/VerdictVault';
import TrialDetail from './pages/TrialDetail';
import LeaderboardRedirect from "./pages/LeaderboardRedirect";
import CreateTrial from './pages/CreateTrial';
import CoinShop from './pages/CoinShop';
import Diagnostics from './pages/Diagnostics';
import AdminReports from './pages/AdminReports';
import AboutPage from './pages/AboutPage';
import CrewPage from './pages/CrewPage';
import SettingsPanel from './components/SettingsPanel';
import HomeDiscoverySection from './components/HomeDiscoverySection';
import HomeCrewBanner from './components/HomeCrewBanner';
import HomeWhatChangedSection from './components/HomeWhatChangedSection';
import createTrialCard from './components/TrialCard';
import CreateCrewModal from './components/CreateCrewModal';
import { FollowsProvider, useFollows } from './contexts/FollowsContext';
import CrewsList from './components/CrewsList';
import CrewJoinPage from './components/CrewJoinPage';
import DailyDecisionVote from './components/DailyDecisionVote';
import DailyDecisionVerdict from './pages/DailyDecisionVerdict';
import PublicUserProfile from './pages/PublicUserProfile';
import InlineErrorBoundary from './components/InlineErrorBoundary';
import FirstVoteModal from './components/FirstVoteModal';
import VerdictShareCard from './components/VerdictShareCard';
import JurySeatProgress from './components/JurySeatProgress';
import TemplateSelectorModal from './components/TemplateSelectorModal';
import AdvancedShareModal from './components/AdvancedShareModal';
import { CANONICAL_HOST, getShareUrl } from './utils/deepLinkConfig';
import { trackReferralAttribution, recordReferralMilestone } from './utils/referrals';
import { generatePunchline } from './utils/VerdictPunchlines';
import { safeStorage } from './utils/safeStorage';
import {
  getPendingAuthChallenge,
  setPendingAuthChallenge,
  clearPendingAuthChallenge,
} from './utils/pendingAuthChallenge';
import { updateStreakForActivity } from './utils/streaks';
import { beginOtpSendAttempt, finishOtpSendAttempt, getOtpSendGuardState } from './utils/authOtpGuards';
import { recordAuthOtpAttribution } from './utils/authOtpAttribution';
import { singleFlight, singleFlightCached } from './utils/singleFlight';
import { registerOnce } from './utils/registerOnce';
import { peekCrewDailyChallengePending, consumeCrewDailyChallengePending } from './utils/crewDailyChallenge';
import { crewChallengeVoteNow } from './utils/crewChallengeHandlers';
import useHomeTrialsFeed from './hooks/useHomeTrialsFeed';
import useAppBootstrap from './hooks/useAppBootstrap';
import { getRuntimeConfig } from './config/runtimeConfig';
import { FEATURED_TRIAL_TEMPLATES } from './config/featuredTrials';
import NotificationPermissionModal, {
  shouldShowNotificationPrompt,
  markSignificantAction
} from './components/NotificationPermissionModal';

import {
  initializeFirebaseMessaging,
  setupPushNotifications,
  setupForegroundMessageHandler,
  saveFCMTokenToDatabase
} from './utils/fcmUtils';
import { isNativeApp } from './utils/isNativeApp';
import { getCanonicalTrialDetailVerdictUrl, getCanonicalTrialVerdictText } from './utils/verdictAssets';
// import { useAutoRefreshFCMToken } from './hooks/useAutoRefreshFCMToken';
// ===== ADD THIS RIGHT AFTER IMPORTS (around line 51) =====
// ===== GLOBAL ERROR RECOVERY (with loop prevention) =====
// ===== AI image persistence helper =====
// Prefer newest AI verdict image_url; otherwise keep whatever is already stored on the trial row.
const LOCAL_HIDDEN_TRIALS_KEY = 'gweffit_hidden_trial_ids';
const STARTER_TRIAL_RETURN_SCOPE_KEY = 'gweffit_starter_trial_return_scope';
function readLocallyHiddenTrialIds() {
  try {
    const raw = localStorage.getItem(LOCAL_HIDDEN_TRIALS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map((v) => String(v)) : []);
  } catch {
    return new Set();
  }
}

// ✅ Don't run auto-reload/localStorage recovery inside native iOS WebView (can cause boot loops)
const IS_NATIVE = isNativeApp();
if (!IS_NATIVE) {
// Prevent infinite reload loops
const RELOAD_KEY = 'gweffit_reload_count';
let reloadCount = 0;
try {
  reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
} catch {
  reloadCount = 0;
}

if (reloadCount > 2) {
  // Stop trying to auto-recover, let the error show
  console.error('🛑 Too many reload attempts, stopping auto-recovery');
  try { sessionStorage.removeItem(RELOAD_KEY); } catch {}
} else {
  window.addEventListener('error', (event) => {
    console.error('💥 Global error:', event.error);
    if (event.error?.message?.includes('JSON') || 
        event.error?.message?.includes('localStorage') ||
        event.error?.message?.includes('Unexpected token')) {
      console.log('🧹 Clearing corrupted localStorage...');
      try { sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1)); } catch {}
      try { safeStorage.clear(); } catch {}
      window.location.reload();
    }
  });
}

// Validate localStorage on app start
try {
  const testCache = safeStorage.get('gweffit_user_cache');
  if (testCache) JSON.parse(testCache);
  
  const testLocal = safeStorage.get('gweffit_user_local');
  if (testLocal) JSON.parse(testLocal);
} catch (e) {
  if (reloadCount <= 2) {
    console.log('🧹 Corrupted cache detected on startup, clearing...');
    sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));
    safeStorage.clear();
    window.location.reload();
  }
}

// Clear reload count on successful load (after React mounts)
setTimeout(() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch {} }, 5000);
}
// ===== BETA EVENT TRACKING =====
// Lightweight analytics for funnel conversion tracking
const trackEvent = async (eventName, data = {}) => {
  const timestamp = new Date().toISOString();

  // Build structured event payload for table
  let user_id = null;
  let anon_id = null;
  let device_id = null;

  // Try to get user ID from cache
  try {
    const cached = safeStorage.get('gweffit_user_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      user_id = parsed.id || null;
    }
  } catch {}

  // Try to get anon_id (for guest users)
  try {
    anon_id = safeStorage.get('gweffit_anon_id') || null;
  } catch {}

  // Try to get device_id (if stored)
  try {
    device_id = safeStorage.get('gweffit_device_id') || null;
  } catch {}

  // Detect platform
  const isCapacitor = window.Capacitor?.isNativePlatform?.();
  const platform = isCapacitor
    ? (window.Capacitor?.getPlatform?.() || 'ios')
    : 'web';

  // Extract trial_id from data if present
  const trial_id = data.trial_id || null;

  // Build props (everything except trial_id)
  const { trial_id: _, ...props } = data;

  console.log(`📊 [TRACK] ${eventName}`, { user_id, anon_id, trial_id, platform, ...props });
    // ---- TRACKING: prevent log spam if telemetry breaks ----
    const _trackWarnedRef = window.__gweffitTrackWarnedRef || (window.__gweffitTrackWarnedRef = new Set());
    const warnOnce = (key, ...args) => {
      if (_trackWarnedRef.has(key)) return;
      _trackWarnedRef.add(key);
      console.warn(...args);
    };
    // Store to Supabase app_events table (fire-and-forget, no UI impact)
    try {
      // primary: app_events
      supabase
        .from('app_events')
        .insert([{
          event_name: eventName,
          user_id,
          anon_id,
          device_id,
          trial_id,
          platform,
          props: Object.keys(props || {}).length > 0 ? props : null,
        }])
        .then(({ error }) => {
          if (!error) return;

          // If app_events is missing / blocked / misconfigured, try fallback once-per-error-type
          warnOnce(`app_events:${error.code || error.message}`, '[TRACK] app_events failed (non-fatal):', error);

          return supabase
            .from('beta_events')
            .insert([{
              event: eventName,
              timestamp,
              user_id,
              ...data
            }])
            .then(({ error: betaErr }) => {
              if (betaErr) {
                warnOnce(`beta_events:${betaErr.code || betaErr.message}`, '[TRACK] beta_events failed (non-fatal):', betaErr);
              }
            });
        })
        .catch((e) => {
          // Network / unexpected client errors
          warnOnce(`track:catch:${e?.message || 'unknown'}`, '[TRACK] unexpected tracking error (non-fatal):', e);
        });
    } catch (e) {
      // Synchronous safety net (rare)
      warnOnce(`track:sync:${e?.message || 'unknown'}`, '[TRACK] sync tracking error (non-fatal):', e);
    }
};

// ===== BETA MODE CONFIG =====
const BETA_MODE = true; // flip to false when you're out of beta
const SHOW_JURY_PROGRESS_UI = true; // A/B flag: set false to use old progress bar UI
const BETA_JURY_SIZE = 6; // Default jury size for beta (change to 12 when ready)
const LIVE_TRIAL_STATUSES = new Set(['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending']);

// Make available globally for other components
window.trackEvent = trackEvent;
window.SHOW_JURY_PROGRESS_UI = SHOW_JURY_PROGRESS_UI;
window.BETA_JURY_SIZE = BETA_JURY_SIZE;

// Update last_login_date once per app load
function useUpdateLastLogin() {
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      await supabase.rpc('update_last_login'); // fire-and-forget
    })();
  }, []);
}

// ===== COURT-THEMED VERDICT LOADING LINES =====
const VERDICT_LOADING_LINES = [
  '🧑‍⚖️ The jury has reached a verdict...',
  '📜 Drafting the official ruling...',
  '⚖️ Foreperson is reviewing the decision...',
  '🎨 Court artist capturing the moment...',
  '📋 Filing the verdict with the clerk...',
  '🔔 Preparing to announce the decision...',
  '✍️ The court scribe is at work...',
  '🏛️ Order in the court...',
  '👨‍⚖️ All rise for the verdict...',
  '🗳️ Counting the final ballots...',
];

// Add after imports
function PushPermissionPrompt({ onClose, onEnable }) {
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999] max-w-[500px] w-[calc(100%-40px)]">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-5 shadow-2xl border-2 border-white/20">
        <div className="flex items-start gap-3">
          <div className="text-4xl">🔥</div>
          <div className="flex-1">
            <div className="text-white font-black text-base mb-1">
              Protect Your Streak!
            </div>
            <div className="text-white/90 text-sm mb-3">
              Get reminded before your streak expires. We'll only notify you if you're at risk of losing it.
            </div>
            <div className="flex gap-2">
              <button
                onClick={onEnable}
                className="flex-1 bg-white text-purple-600 border-none rounded-lg px-4 py-2.5 font-black text-sm cursor-pointer hover:bg-gray-100 transition-colors"
              >
                Enable Reminders
              </button>
              <button
                onClick={onClose}
                className="bg-white/20 text-white border-none rounded-lg px-4 py-2.5 font-bold text-sm cursor-pointer hover:bg-white/30 transition-colors"
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// Bonus coins indicator when user clicks summons notification
function BonusCoinsIndicator({ bonusCoins, onClose }) {
  if (!bonusCoins) return null;

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[9999] max-w-md w-[calc(100%-40px)] animate-bounce">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 shadow-2xl border-2 border-white/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-4xl">💰</div>
            <div>
              <div className="text-white font-black text-lg">
                Vote now → +{bonusCoins} coins!
              </div>
              <div className="text-white/90 text-sm">
                Your friend summoned you to this trial
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
const pick = (o, keys) => Object.fromEntries(keys.map(k => [k, o[k]]));

const generateRandomUsername = () => {
  const adjectives = [
    'Fair', 'Bold', 'Wise', 'Swift', 'Brave', 'Sharp', 'Quick', 'Keen',
    'Just', 'Noble', 'Clear', 'Firm', 'Calm', 'Strong', 'Bright', 'Cool',
    'Clever', 'Honest', 'Loyal', 'Skilled', 'Epic', 'Elite', 'Prime', 'Supreme',
    'Random', 'Curious', 'Casual', 'Neutral', 'Silent', 'Quiet', 'Gentle', 'Peaceful',
    'Legendary', 'Certified', 'Official', 'Based', 'Chaotic', 'Skeptical'
  ];

  const nouns = [
    'Judge', 'Juror', 'Bailiff', 'Verdict', 'Gavel', 'Justice', 'Witness',
    'Lawyer', 'Advocate', 'Counsel', 'Attorney', 'Prosecutor', 'Defender',
    'Arbiter', 'Mediator', 'Moderator', 'Referee', 'Umpire', 'Decider',
    'Voter', 'Analyst', 'Critic', 'Expert', 'Sage', 'Oracle', 'Thinker',
    'Owl', 'Eagle', 'Fox', 'Wolf', 'Bear', 'Lion', 'Tiger', 'Hawk',
    'Philosopher', 'Scholar', 'Professor', 'Maverick', 'Vigilante'
  ];

  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 9000) + 1000; // 1000-9999

  return `${adjective}${noun}${number}`;
};

// ========= VERDICT TEMPLATES & TONE PACK =========

// Pick deterministic things per trial (so re-exports match)
const seeded = (seed) => {
  let s = 0;
  for (let i = 0; i < String(seed).length; i++) s = (s * 31 + String(seed).charCodeAt(i)) >>> 0;
  return (min, max) => min + (s = (1103515245 * s + 12345) % 2147483648) % (max - min + 1);
};

// Map category → default tone
const defaultToneByCategory = (category) => ({
  sports: 'announcer',
  campus: 'aunt',
  office: 'corp',
  food: 'prosecutor',
  pop: 'roast',
}[category] || 'judge');

// Minimal template registry (2 fully wired now; 10 more later)
const TEMPLATE_REGISTRY = {
  sports_desk: {
    name: 'Sports Desk',
    // Tailwind layout instructions for the VerdictModal to render
    render: (ctx) => (
      <div className="relative w-full overflow-hidden rounded-3xl bg-slate-950 border-4 border-yellow-400">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black" />
        <div className="p-6 relative">
          {/* Score Bug */}
          <div className="absolute top-4 right-4 bg-white text-slate-900 px-3 py-1 rounded font-black text-xs">FINAL</div>
          {/* Title */}
          <div className="text-center">
            <div className="text-yellow-300 text-lg font-black tracking-wide mb-1">GWEFFIT SPORTS DESK</div>
            <h2 className="text-2xl md:text-3xl font-black text-white">{ctx.title}</h2>
          </div>
          {/* Winner */}
          <div className="mt-5 text-center">
            <div className="text-6xl md:text-7xl font-black text-yellow-300 leading-none">{ctx.winner}</div>
            <div className="text-white/90 font-bold mt-1">{ctx.pct}% of the jury</div>
          </div>
          {/* Split Bar */}
          <div className="mt-6">
            <div className="h-3 rounded-full overflow-hidden bg-white/10">
              <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all" style={{ width: `${ctx.winnerPct}%` }}/>
            </div>
            <div className="flex justify-between text-xs text-white/70 mt-1">
              <div>{ctx.sideA} — {(ctx.sideAPct).toFixed(1)}%</div>
              <div>{ctx.sideB} — {(ctx.sideBPct).toFixed(1)}%</div>
            </div>
          </div>
          {/* Punchline Ticker */}
          <div className="mt-6 bg-white/10 border border-white/20 rounded-xl px-4 py-3">
            <div className="text-sm font-bold text-white/90">{ctx.punchline}</div>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-white/60 mt-6">
            <div>Jury of {ctx.totalVotes} • {ctx.timestamp}</div>
            <div className="bg-white text-purple-900 px-2 py-1 rounded font-black">gweffit.com</div>
          </div>
        </div>
      </div>
    )
  },

  roastmaster: {
    name: 'Roastmaster',
    render: (ctx) => (
      <div className="relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-pink-600 via-rose-700 to-purple-900 border-4 border-yellow-400">
        <div className="p-6 relative">
          {/* Stage light */}
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-80 h-40 bg-white/10 blur-2xl rounded-full" />
          {/* Title */}
          <div className="text-center">
            <div className="text-yellow-200 text-sm font-black tracking-widest">ROAST SESSION</div>
            <h2 className="text-white text-2xl md:text-3xl font-black mt-1">{ctx.title}</h2>
          </div>
          {/* Winner */}
          <div className="mt-6 text-center">
            <div className="inline-block bg-black/40 px-4 py-2 rounded-full">
              <span className="text-white/70 text-xs font-bold mr-2">WINNER</span>
              <span className="text-yellow-300 text-2xl font-black">{ctx.winner}</span>
            </div>
            <div className="text-white font-bold mt-2">{ctx.pct}% of the jury</div>
          </div>
          {/* Punchline Bubble */}
          <div className="mt-6 bg-black/40 border border-white/10 rounded-2xl px-5 py-4">
            <div className="text-lg text-yellow-200 font-black">“{ctx.punchline}”</div>
          </div>
          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-white/60 mt-6">
            <div>Jury of {ctx.totalVotes} • {ctx.timestamp}</div>
            <div className="bg-white text-purple-900 px-2 py-1 rounded font-black">gweffit.com</div>
          </div>
        </div>
      </div>
    )
  }
};

// choose a template deterministically per trial
const pickTemplateId = (trial) => {
  const chooser = seeded(trial.id || trial.title);
  const list = ['sports_desk', 'roastmaster']; // add more as you implement
  return list[chooser(0, list.length - 1)];
};

// ===== Stable helpers for VerdictModal (module scope) =====
const getStableVerdictNumber = (trialId) => {
  const s = String(trialId ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) >>> 0;
  }
  // 10000–99999
  return 10000 + (h % 90000);
};

// ========= VERDICT MODAL COMPONENT (OUTSIDE MAIN COMPONENT) =========
const VerdictModal = ({ 
  verdictData, 
  setVerdictData, 
  setShowVerdict, 
  user, 
  shownVerdicts, 
  setShownVerdicts, 
  downloadMeme, 
  shareMeme, 
  isGeneratingMeme, 
  verdictRef,
  setShowPremiumModal,
  openMemeCreator
}) => {
  // ✅ 1. ALL HOOKS FIRST
  const [selectedCaptionIndex, setSelectedCaptionIndex] = useState(0);
  const [showDecisionUpdateModal, setShowDecisionUpdateModal] = useState(false);
  const [decisionFollowed, setDecisionFollowed] = useState(null); // true | false | null
  const [decisionNote, setDecisionNote] = useState('');
  const [savingDecisionUpdate, setSavingDecisionUpdate] = useState(false);
  const [verdictImgLoaded, setVerdictImgLoaded] = useState(false);
  // ✅ Always reset caption selection when opening a different verdict
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedCaptionIndex(0);
  }, [verdictData?.trial?.id]); // keep stable to avoid extra re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setVerdictImgLoaded(false);
  }, [verdictData?.trial?.id, verdictData?.aiImage, verdictData?.ai_image, verdictData?.image_url]); // sync on image changes only

// 📊 Track verdict modal opened (shows loading or final verdict)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (verdictData?.trial?.id && window.trackEvent) {
      const aiImageUrl = verdictData?.aiImage || verdictData?.ai_image || verdictData?.image_url;
      window.trackEvent(aiImageUrl ? 'verdict_viewed' : 'verdict_loading_shown', {
        trial_id: verdictData.trial.id,
        has_ai_image: !!aiImageUrl,
      });
    }
  }, [verdictData?.trial?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- single-shot tracking

// 📊 Track when verdict image finishes loading
// eslint-disable-next-line react-hooks/exhaustive-deps
// eslint-disable-next-line react-hooks/exhaustive-deps
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (verdictImgLoaded && verdictData?.trial?.id && window.trackEvent) {
    window.trackEvent('verdict_viewed', {
      trial_id: verdictData.trial.id,
      has_ai_image: true,
    });
  }
}, [verdictImgLoaded, verdictData?.trial?.id]);

const stableVerdictData = useRef(verdictData);
const endedAtRef = useRef({ trialId: null, ts: null });  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (verdictData?.trial?.id !== stableVerdictData.current?.trial?.id) {
      stableVerdictData.current = verdictData;
    }
  }, [verdictData?.trial?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- stable ref sync
  
  // ✅ 2. USEMEMO (must come before early returns!)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allCaptions = useMemo(() => {
  try {
    const canonicalText = getCanonicalTrialVerdictText({
      ...(verdictData || {}),
      trial: verdictData?.trial || null,
      verdict_data:
        verdictData?.verdict_data
        || verdictData?.trial?.verdict_data
        || null,
    });
    const canonicalBase = canonicalText.captionFull || canonicalText.captionShort || stableVerdictData.current?.punchline || verdictData?.punchline;

    if (verdictData?.trial?.verdict_data || verdictData?.verdict_data) {
      return canonicalBase ? [canonicalBase] : ['No verdict available'];
    }

    const normalizeTemplate = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map(p => (typeof p === 'string' ? p : p?.text))
        .filter(Boolean);

    const normalizeAi = (ai) => {
      if (!ai) return [];

      // If ai is already an array, normalize like templates
      if (Array.isArray(ai)) return normalizeTemplate(ai);

      // Expected shape: { roast, sports, internet } where each can be string or array
      const first = (v) => (Array.isArray(v) ? v[0] : v);
      return [first(ai.roast), first(ai.sports), first(ai.internet)].filter(Boolean);
    };

    // IMPORTANT: base punchline stays stable for this trial (even if user remixes)
    const base = canonicalBase;

    const template = normalizeTemplate(verdictData?.templatePunchlines);
    const ai = normalizeAi(verdictData?.aiPunchlines);

    // Merge + de-dupe (prevents “3 options but only 2 unique”)
    const merged = [...ai, base, ...template].filter(Boolean);
    return [...new Set(merged)];
  } catch (err) {
    console.warn('Caption fallback error:', err);
    return [stableVerdictData.current?.punchline || verdictData?.punchline || 'No verdict available'];
  }
}, [
  verdictData,
  verdictData?.templatePunchlines,
  verdictData?.aiPunchlines,
  verdictData?.punchline,
  verdictData?.trial?.verdict_data,
  verdictData?.verdict_data,
]);

// ✅ Clamp index when caption count changes (prevents “1/3 but only 2”)
useEffect(() => {
  if (!allCaptions?.length) return;
  if (selectedCaptionIndex > allCaptions.length - 1) setSelectedCaptionIndex(0);
}, [allCaptions, selectedCaptionIndex]);

const safeCaptionIndex = Math.min(
  selectedCaptionIndex,
  Math.max(allCaptions.length - 1, 0)
);

const effectivePunchline =
  allCaptions[safeCaptionIndex] || allCaptions[0] || 'No verdict';

  
  // ✅ 3. NOW SAFE TO DO EARLY RETURNS
  if (!verdictData) return null;
  
  const trial = verdictData.trial;
  // ✅ Freeze an "endedAt" timestamp per trial so grace window can actually expire

if (endedAtRef.current.trialId !== trial?.id) {
  const closed =
    trial?.closedAt ??
    (trial?.closed_at ? new Date(trial.closed_at).getTime() : null) ??
    null;

  const created =
    trial?.createdAt ??
    (trial?.created_at ? new Date(trial.created_at).getTime() : null) ??
    null;

  endedAtRef.current = {
    trialId: trial?.id,
    ts: closed || created || Date.now(),
  };
}

  if (!trial) return null;
      
    // ✅ 4. Extract other values
  const winner = verdictData.winner;            // 'A' | 'B' | 'Tie'
  const winnerPct = verdictData.winnerPct;
  const verdictType = verdictData.verdictType;
  const verdictNumber = verdictData.verdictNumber;
  const snap = verdictData.snap;
    // ✅ Resolve image URL consistently
  const aiImageUrl =
    verdictData.aiImage ??
    verdictData.ai_image ??
    verdictData.image_url ??
    null;

  // Keep your "verdict text exists" signal
  const hasVerdictContent = !!(verdictData?.aiPunchlines || verdictData?.punchline);

  // ✅ Grace window to prevent purple flash right after close
  const GRACE_MS = 15000;
  const endedAt = endedAtRef.current.ts || Date.now();
  const withinGrace = Date.now() - endedAt < GRACE_MS;

  // ✅ Only show the big “Generating…” card while we *don’t yet have a URL* (within grace).
// Once we have an image URL, we must render <img> so it can actually load.
const isGeneratingVerdict = !aiImageUrl && withinGrace;

// Separate flag: show a loader overlay while the image bytes are loading
const isImageLoading = !!aiImageUrl && !verdictImgLoaded;

  // Keep downstream logic working
  const hasAIVerdict = hasVerdictContent || !!aiImageUrl;
  const aiImage = aiImageUrl;
    
  // Template fallback support
  const templateId = trial ? pickTemplateId(trial) : 'roastmaster';
  const Template = TEMPLATE_REGISTRY[templateId];
  
  // ---- derived values & ctx ----
  const isQuickTrial = trial.type === 'quick';
  const isTie = winner === 'Tie';
  
  // Check if current user is the creator of a decide-for-me trial
  const trialCreatorId = trial.creator_id || trial.created_by;
  const isTrialCreator = !!(user?.id && trialCreatorId && user.id === trialCreatorId);
  const isDecideForMeTrial = trial.trialMode === 'decide_for_me' || trial.trial_mode === 'decide_for_me';
  const existingDecisionUpdate = trial?.verdict_data?.decision_update || null;
  const isClosed = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(trial.status);

  // Label for the “big” winner text
  const winnerSide = isTie
    ? "Hung Jury – It's a tie"
    : (winner === 'A' ? trial.sideA : trial.sideB);

  const total = Math.max(trial.totalVotes, 1);

  const ctx = {
    title: trial.title,
    sideA: trial.sideA,
    sideB: trial.sideB,
    winner: winnerSide,
    winnerPct: +(
      winnerPct ??
      ((winner === 'A' ? trial.votesA : trial.votesB) / total * 100).toFixed(1)
    ),
    sideAPct: (trial.votesA / total) * 100,
    sideBPct: (trial.votesB / total) * 100,
    pct: +(
      winnerPct ??
      ((winner === 'A' ? trial.votesA : trial.votesB) / total * 100).toFixed(1)
    ),

    totalVotes: total,
    punchline: effectivePunchline,
        timestamp: new Date().toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }),
  };
    const saveDecisionUpdate = async () => {
  if (!trial?.id) return;
  if (!isDecideForMeTrial || !isTrialCreator) return;

  // (Optional) only allow once ended
  if (trial.status && !['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(String(trial.status || '').toLowerCase())) {
    alert('This trial is not closed yet.');
    return;
  }

  if (decisionFollowed !== true && decisionFollowed !== false) {
    alert('Pick Yes or No first.');
    return;
  }

  setSavingDecisionUpdate(true);
  try {
    const nextUpdate = {
      followed: decisionFollowed,
      note: (decisionNote || '').trim().slice(0, 280),
      updated_at: new Date().toISOString(),
    };

    const nextVerdictData = {
      ...(trial.verdict_data || {}),
      decision_update: nextUpdate,
    };

    const { data: currentTrialRow } = await supabase
      .from('trials')
      .select('verdict_data')
      .eq('id', trial.id)
      .maybeSingle();

    const serverVerdictData =
      currentTrialRow?.verdict_data && typeof currentTrialRow.verdict_data === 'object'
        ? currentTrialRow.verdict_data
        : {};
    const mergedVerdictData = {
      ...serverVerdictData,
      ...nextVerdictData,
    };
    if (serverVerdictData?.verdict_source) {
      mergedVerdictData.verdict_source = serverVerdictData.verdict_source;
    }
    if (serverVerdictData?.reason_code) {
      mergedVerdictData.reason_code = serverVerdictData.reason_code;
    }

    const { error } = await supabase
      .from('trials')
      .update({ verdict_data: mergedVerdictData })
      .eq('id', trial.id);

    if (error) throw error;

    // Update the currently open modal immediately
    setVerdictData(prev => {
      if (!prev?.trial) return prev;
      return {
        ...prev,
        trial: { ...prev.trial, verdict_data: mergedVerdictData },
      };
    });

    setShowDecisionUpdateModal(false);
  } catch (e) {
    console.error('Failed to save decision update:', e);
    alert('Could not save update. Try again.');
  } finally {
    setSavingDecisionUpdate(false);
  }
};

  const closeVerdict = () => {
    if (trial?.id) setShownVerdicts(prev => new Set([...prev, trial.id]));
    setShowVerdict(false);
    setVerdictData(null);
  };

    // Push helpers...


  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-6xl">
          
          {/* Close button */}
          <button
            onClick={closeVerdict}
            className="ml-auto mb-3 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full flex items-center justify-center"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Responsive flex container */}
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            
            {/* LEFT COLUMN: Verdict Card */}
            <div className="w-full lg:flex-1">
              
              {/* Badge row */}
              <div className="flex items-center justify-between text-xs text-white/80 mb-2">
                <div className="inline-flex items-center gap-2">
                  {isQuickTrial && (
                    <span className="bg-yellow-400 text-purple-900 font-black px-2 py-1 rounded">QUICK</span>
                  )}
                  {snap && <span className="bg-white/20 px-2 py-1 rounded font-bold">SNAP VERDICT</span>}
                </div>
                <div className="bg-yellow-400 text-purple-900 px-2 py-1 rounded font-black">
                  #{verdictNumber || (trial?.id ? parseInt(trial.id.slice(-5), 16) % 90000 + 10000 : 10000)}
                </div>
              </div>
              
              {/* Caption Remixer */}
              {allCaptions.length > 1 && (
  <CaptionRemixer
    key={trial?.id} // ✅ remount per verdict so internal state resets cleanly
    captions={allCaptions}
    onSelect={(caption, index) => {
      setSelectedCaptionIndex(index);

      // ✅ keep share-card punchline synced to the currently selected caption
      setVerdictData(prev => (prev ? { ...prev, punchline: caption } : prev));
    }}
  />
)}

              {/* Verdict Card */}
              <div ref={verdictRef}>
                {isGeneratingVerdict ? (
  // 🎨 LOADING STATE - AI is generating verdict meme
  <div className="relative rounded-3xl overflow-hidden border-4 border-yellow-400 bg-gradient-to-br from-purple-700 via-purple-900 to-black">
    {/* Animated pulse overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-purple-400/20 to-purple-600/0 animate-pulse" />

    <div className="relative z-10 p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
      {/* Jury seats visual - shows all seats filled */}
      <div className="mb-4">
        <JurySeatProgress
          filled={trial.totalVotes || trial.jurySize || BETA_JURY_SIZE}
          total={trial.jurySize || BETA_JURY_SIZE}
          showLabel={false}
          className="justify-center"
        />
        <div className="text-sm text-yellow-300/80 font-semibold mt-2 animate-pulse">
          ✅ Jury seated. Deliberating…
        </div>
      </div>

      <h3 className="text-2xl font-black text-yellow-300 mb-2">
        {VERDICT_LOADING_LINES[Math.floor(Date.now() / 2000) % VERDICT_LOADING_LINES.length]}
      </h3>
      <p className="text-white/70 mb-6">The court artist is at work...</p>

      {/* Preview winner info */}
      <div className="bg-black/40 rounded-xl px-6 py-4 mb-4 backdrop-blur-sm">
        <div className="text-xs text-yellow-300/70 uppercase tracking-wider mb-1">{verdictType || 'VERDICT'}</div>
        <div className="text-3xl font-black text-yellow-300">
          {winner === 'Tie' ? "It's a Tie!" : (winner === 'A' ? trial.sideA : trial.sideB)}
        </div>
        <div className="text-white/80 font-medium mt-1">
          {winner === 'Tie' ? '50/50 Split' : `${ctx.pct}% of the jury`}
        </div>
      </div>

      <div className="flex items-center gap-2 text-white/50 text-sm">
        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
        <span>Usually takes 5-15 seconds</span>
      </div>

      {/* Future sponsor slot */}
      {/* <div className="mt-6 text-xs text-white/30">This verdict brought to you by [Sponsor]</div> */}
    </div>
  </div>
) : aiImage ? (
  // AI-Generated Verdict Card (WITH image)
  <div className="relative rounded-3xl overflow-hidden border-4 border-yellow-400">
    <img
  src={aiImage}
  alt="Verdict background"
  className={`w-full h-auto transition-opacity duration-300 ${verdictImgLoaded ? 'opacity-100' : 'opacity-0'}`}
  onLoad={() => {
    setVerdictImgLoaded(true);
    console.log("✅ Verdict image loaded:", aiImage?.substring(0, 80));
  }}
  onError={(e) => {
    setVerdictImgLoaded(true); // stop loading overlay on error
    console.error("❌ AI image failed to load:", aiImage);
    console.error("❌ Error event:", e?.nativeEvent?.error || e?.type || "unknown");
    console.error("❌ Domain:", (() => { try { return new URL(aiImage).host } catch { return "bad-url" } })());
    setVerdictData(prev => (prev ? { ...prev, aiImage: null } : prev));
  }}
/>
{isImageLoading && (
  <div className="absolute inset-0 bg-gradient-to-br from-purple-700 via-purple-900 to-black flex flex-col items-center justify-center">
    <div className="text-5xl mb-3 animate-bounce">🎨</div>
    <div className="text-yellow-300 font-black text-lg mb-1">Rendering your meme…</div>
    <div className="text-white/60 text-sm">Almost there</div>
  </div>
)}

    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
      {/* ⚖️ WATERMARK - Bottom right corner */}
      <div className="absolute bottom-3 right-3 text-white/50 text-xs font-bold flex items-center gap-1">
        <span>⚖️</span>
        <span>gweffit.com</span>
      </div>

      <div className="inline-flex items-center gap-2 bg-yellow-400 text-purple-900 px-3 py-1 rounded-full font-black text-xs mb-3 self-start">
        {verdictType}
      </div>

      {/* 📋 QUESTION CONTEXT */}
      <div className="text-white/60 text-xs font-bold uppercase tracking-wide mb-1">
        Trial:
      </div>

      <h2 className="text-white text-2xl md:text-3xl font-black mb-3 drop-shadow-lg">
        {trial.title}
      </h2>

      <div className="mb-3">
        <div className="text-yellow-300 text-4xl md:text-5xl font-black leading-none drop-shadow-lg">
          {isTie ? "Hung Jury – It's a tie" : winnerSide}
        </div>
        <div className="text-white/90 font-bold mt-1 text-lg">
          {isTie ? "The jury is split 50 / 50." : `${ctx.pct}% of the jury`}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 mb-3">
        <div className="text-white font-bold text-base italic">
          "{effectivePunchline}"
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-white/70">
        <div>Jury of {ctx.totalVotes} • {ctx.timestamp}</div>
        <div className="bg-yellow-400 text-purple-900 px-2 py-1 rounded font-black">
          #{verdictNumber || getStableVerdictNumber(trial?.id)}
        </div>
      </div>
    </div>
  </div>
) : hasAIVerdict ? (
  // AI-Generated Verdict Card (NO image) - Purple fallback
  <div className="relative rounded-3xl overflow-hidden border-4 border-yellow-400 bg-gradient-to-br from-purple-700 via-purple-900 to-black">
    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.35),_transparent_60%)]" />
    
    {/* ⚖️ WATERMARK - Bottom right corner */}
    <div className="absolute bottom-3 right-3 text-white/30 text-xs font-bold flex items-center gap-1 z-10">
      <span>⚖️</span>
      <span>gweffit.com</span>
    </div>

    <div className="relative p-6">
      <div className="inline-flex items-center gap-2 bg-yellow-400 text-purple-900 px-3 py-1 rounded-full font-black text-xs mb-3 self-start">
        {verdictType}
      </div>

      {/* 📋 QUESTION CONTEXT */}
      <div className="text-white/60 text-xs font-bold uppercase tracking-wide mb-1">
        Trial:
      </div>

      <h2 className="text-white text-2xl md:text-3xl font-black mb-3 drop-shadow-lg">
        {trial.title}
      </h2>

      <div className="mb-3">
        <div className="text-yellow-300 text-4xl md:text-5xl font-black leading-none drop-shadow-lg">
          {isTie ? "Hung Jury – It's a tie" : winnerSide}
        </div>
        <div className="text-white/90 font-bold mt-1 text-lg">
          {isTie ? "The jury is split 50 / 50." : `${ctx.pct}% of the jury`}
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 mb-3">
        <div className="text-white font-bold text-base italic">
          "{effectivePunchline}"
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-white/70">
        <div>Jury of {ctx.totalVotes} • {ctx.timestamp}</div>
        <div className="bg-yellow-400 text-purple-900 px-2 py-1 rounded font-black">
          #{verdictNumber || getStableVerdictNumber(trial?.id)}
        </div>
      </div>
    </div>
  </div>
) : Template?.render ? (
  Template.render(ctx)
) : (
  <div className="rounded-3xl p-8 text-white border-4 border-yellow-400 bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 relative">
    {/* ⚖️ WATERMARK - Bottom right corner */}
    <div className="absolute bottom-3 right-3 text-white/30 text-xs font-bold flex items-center gap-1">
      <span>⚖️</span>
      <span>gweffit.com</span>
    </div>

    <div className="text-center">
      <div className="text-sm font-black text-yellow-300 mb-2">VERDICT</div>
      
      {/* 📋 QUESTION CONTEXT */}
      <div className="text-white/60 text-xs font-bold uppercase tracking-wide mb-1">
        Trial:
      </div>
      
      <h2 className="text-2xl font-black text-yellow-300 mb-3">{trial.title}</h2>
      <div className="text-5xl font-black mb-2">{winnerSide}</div>
      <div className="text-white/90 font-bold mb-4">{ctx.pct}% of the jury</div>
      <div className="bg-white/10 px-4 py-3 rounded-xl italic">"{effectivePunchline}"</div>
    </div>
  </div>
)}
              </div>

            </div>
            
            {/* RIGHT COLUMN: Actions */}
            <div className="w-full lg:w-80 space-y-3">
              
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-white/50">Send the verdict</div>
                <div className="mt-1 text-sm text-white/75">
                  Save it, send it, or copy the receipt text.
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  <button
                    onClick={isGeneratingMeme ? null : shareMeme}
                    disabled={isGeneratingMeme}
                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
                  >
                    <Share2 size={18} />
                    Send Verdict
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={isGeneratingMeme ? null : downloadMeme}
                      disabled={isGeneratingMeme}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
                    >
                      {isGeneratingMeme ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Download size={18} />
                          Save Card
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `${trial.title} — Jury says ${winnerSide} (${ctx.pct}%). "${effectivePunchline}" #GweffIT`
                        )
                      }
                      className="w-full bg-white/15 hover:bg-white/20 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
                    >
                      <Copy size={18} />
                      Copy Caption
                    </button>
                  </div>

                  <button
                    onClick={closeVerdict}
                    className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold"
                  >
                    Close
                  </button>
                </div>
              </div>

{isDecideForMeTrial && isTrialCreator ? (
  <div className="text-center py-3">
    <div className="text-purple-300 font-bold">📣 Your crew has spoken!</div>
    <div className="text-sm text-white/60 mt-1">Now go make your decision 💪</div>

    {isClosed ? (
      existingDecisionUpdate ? (
        <div className="mt-3 bg-white/10 border border-white/20 rounded-xl p-3 text-left">
          <div className="text-xs text-white/60 mb-1">Outcome update</div>
          <div className="font-bold text-white">
            {existingDecisionUpdate.followed
              ? '✅ I took the jury’s advice'
              : '❌ I did NOT take the jury’s advice'}
          </div>
          {existingDecisionUpdate.note ? (
            <div className="text-sm text-white/80 mt-1">“{existingDecisionUpdate.note}”</div>
          ) : null}
        </div>
      ) : (
        <button
          onClick={() => {
            setDecisionFollowed(null);
            setDecisionNote('');
            setShowDecisionUpdateModal(true);
          }}
          className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-xl font-bold"
        >
          Post outcome update
        </button>
      )
    ) : (
      <div className="mt-3 text-xs text-white/50">
        Outcome updates unlock when the trial closes.
      </div>
    )}
  </div>
) : trial.hasVoted ? (

  <div className="flex items-center justify-center gap-2 text-yellow-300 text-sm font-bold py-2">
    <Trophy size={16} />
    +10 XP • +50 Coins 💰 • Streak: {user?.streak || 0} 🔥
  </div>
) : (
  <div className="flex items-center justify-center gap-2 text-white/60 text-sm font-bold italic py-2">
    👀 You watched as a spectator
  </div>
)}

{/* Upsell - hidden in beta */}
{!BETA_MODE && (
  <div className="bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-xl p-3 border border-yellow-400/30">
    <div className="flex flex-col gap-2 text-yellow-300 text-sm text-center">
      <div className="flex items-center justify-center gap-2">
        <Crown size={16} />
        <span>Want AI-generated verdicts?</span>
      </div>
      <button
        onClick={() => {
          setShowVerdict(false);
          setShowPremiumModal(true);
        }}
        className="text-xs bg-yellow-400 text-purple-900 px-4 py-2 rounded-full font-bold"
      >
        Upgrade to Premium!
      </button>
    </div>
  </div>
)}

{/* ✅ Decide-for-me outcome update modal */}
{showDecisionUpdateModal && (
  <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-5">
      <div className="text-white font-black text-lg mb-2">Did you follow the jury?</div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <button
          onClick={() => setDecisionFollowed(true)}
          className={`py-3 rounded-xl font-bold ${
            decisionFollowed === true ? "bg-green-500 text-white" : "bg-white/10 text-white"
          }`}
        >
          ✅ Yes
        </button>
        <button
          onClick={() => setDecisionFollowed(false)}
          className={`py-3 rounded-xl font-bold ${
            decisionFollowed === false ? "bg-red-500 text-white" : "bg-white/10 text-white"
          }`}
        >
          ❌ No
        </button>
      </div>

      <textarea
        value={decisionNote}
        onChange={(e) => setDecisionNote(e.target.value)}
        placeholder="Optional: quick context (max 280 chars)"
        maxLength={280}
        className="w-full min-h-[90px] bg-black/30 border border-white/10 rounded-xl p-3 text-white placeholder:text-white/40 outline-none"
      />

      <div className="flex gap-3 mt-4">
        <button
          onClick={() => setShowDecisionUpdateModal(false)}
          className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold"
        >
          Cancel
        </button>
        <button
          disabled={savingDecisionUpdate}
          onClick={saveDecisionUpdate}
          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900/50 text-white py-3 rounded-xl font-bold"
        >
          {savingDecisionUpdate ? "Saving…" : "Save update"}
        </button>
      </div>
    </div>
  </div>
)}

            </div>
          </div>
        </div>
      </div>
      {showDecisionUpdateModal && (
  <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-5">
      <div className="text-white font-black text-lg mb-2">Did you follow the jury?</div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <button
          onClick={() => setDecisionFollowed(true)}
          className={`py-3 rounded-xl font-bold ${
            decisionFollowed === true ? "bg-green-500 text-white" : "bg-white/10 text-white"
          }`}
        >
          ✅ Yes
        </button>
        <button
          onClick={() => setDecisionFollowed(false)}
          className={`py-3 rounded-xl font-bold ${
            decisionFollowed === false ? "bg-red-500 text-white" : "bg-white/10 text-white"
          }`}
        >
          ❌ No
        </button>
      </div>

      <textarea
        value={decisionNote}
        onChange={(e) => setDecisionNote(e.target.value)}
        placeholder="Optional: quick context (max 280 chars)"
        maxLength={280}
        className="w-full min-h-[90px] bg-black/30 border border-white/10 rounded-xl p-3 text-white placeholder:text-white/40 outline-none"
      />

      <div className="flex gap-3 mt-4">
        <button
          onClick={() => setShowDecisionUpdateModal(false)}
          className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold"
        >
          Cancel
        </button>
        <button
          disabled={savingDecisionUpdate}
          onClick={saveDecisionUpdate}
          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900/50 text-white py-3 rounded-xl font-bold"
        >
          {savingDecisionUpdate ? "Saving…" : "Save update"}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};
// ========= NOTIFICATION HELPERS =========
  
// UPDATED AuthModal with OTP Code Support for PWA
// Replace lines 710-906 in App.js with this code

function AuthModal({
  authMode,
  setAuthMode,
  authEmail,
  setAuthEmail,
  authMessage,
  setAuthMessage,
  authError,
  setAuthError,
  setShowAuthModal,
  authContext,
  currentAuthUserId,
  currentAppUserId,
}) {
  const renderCount = useRef(0);
  renderCount.current++;
  console.log('🔐 AuthModal render #', renderCount.current);

  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);  // NEW: Track if OTP was sent
  const [otpCode, setOtpCode] = useState('');     // NEW: Store the 6-digit code
  const emailInputRef = useRef(null);
  const authRequestInFlightRef = useRef(false);
  
  // Detect native Capacitor app
  const isNative = isNativeApp();

  console.log('🔐 AuthModal state - loading:', loading, 'authEmail:', authEmail, 'isNative:', isNative);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowAuthModal(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [setShowAuthModal]);

  useEffect(() => {
    const pendingChallenge = getPendingAuthChallenge();
    if (!pendingChallenge?.email) return;
    setAuthEmail(pendingChallenge.email);
    setOtpSent(true);
    setOtpCode('');
    setAuthError('');
    setAuthMessage(`Enter the 6-digit code we sent to ${pendingChallenge.email}.`);
  }, [setAuthEmail, setAuthError, setAuthMessage]);

  useEffect(() => {
    const route = `${window.location.pathname}${window.location.search || ''}`;
    const pendingChallenge = getPendingAuthChallenge();
    trackEvent('auth_modal_opened', {
      mode: authMode,
      source_surface: authContext || 'unknown',
      route,
      pending_challenge_present: Boolean(pendingChallenge?.email),
    });
  }, [authContext, authMode]);

  // Email auth is OTP-only across the app.
  const handleSendEmail = async (e, { forceResend = false } = {}) => {
    e.preventDefault();
    if (!authEmail || loading || authRequestInFlightRef.current) return;

    const route = `${window.location.pathname}${window.location.search || ''}`;
    const pendingChallenge = getPendingAuthChallenge();
    const normalizedEmail = String(authEmail || '').trim().toLowerCase();
    const normalizedPendingEmail = String(pendingChallenge?.email || '').trim().toLowerCase();

    if (!forceResend && normalizedEmail && normalizedPendingEmail && normalizedEmail === normalizedPendingEmail) {
      recordAuthOtpAttribution({
        event: 'otp_send_reused_pending_challenge',
        source_surface: authContext || 'unknown',
        route,
        auth_mode: authMode,
        email: normalizedEmail,
        app_thinks_authenticated: Boolean(currentAuthUserId || currentAppUserId),
        current_auth_user_id: currentAuthUserId || null,
        current_app_user_id: currentAppUserId || null,
        pending_challenge_present: true,
        force_resend: Boolean(forceResend),
        allowed: false,
        blocked_reason: 'reused_pending_challenge',
      });
      setOtpSent(true);
      setAuthError('');
      setAuthMessage(`Enter the 6-digit code we sent to ${pendingChallenge.email}.`);
      console.log('[auth_otp_request_reused_pending_challenge]', {
        context: 'app_auth_modal',
        source_surface: authContext || 'unknown',
        route,
        email: normalizedEmail,
      });
      return;
    }

    if (currentAuthUserId || currentAppUserId) {
      recordAuthOtpAttribution({
        event: 'otp_send_blocked_visible_authenticated_user',
        source_surface: authContext || 'unknown',
        route,
        auth_mode: authMode,
        email: normalizedEmail,
        app_thinks_authenticated: true,
        current_auth_user_id: currentAuthUserId || null,
        current_app_user_id: currentAppUserId || null,
        pending_challenge_present: Boolean(pendingChallenge?.email),
        force_resend: Boolean(forceResend),
        allowed: false,
        blocked_reason: 'visible_authenticated_user',
      });
      setAuthError('');
      setAuthMessage('You are already signed in on this device.');
      setShowAuthModal(false);
      return;
    }

    // 📊 Track signup/login started
    trackEvent('signup_started', {
      mode: authMode,
      method: 'email_otp',
      source_surface: authContext || 'unknown',
      route,
      pending_challenge_present: Boolean(pendingChallenge?.email),
    });
    const guardStart = beginOtpSendAttempt(authContext || 'app_auth_modal');
    if (!guardStart.ok) {
      recordAuthOtpAttribution({
        event: 'otp_send_blocked_guard',
        source_surface: authContext || 'unknown',
        route,
        auth_mode: authMode,
        email: normalizedEmail,
        app_thinks_authenticated: Boolean(currentAuthUserId || currentAppUserId),
        current_auth_user_id: currentAuthUserId || null,
        current_app_user_id: currentAppUserId || null,
        pending_challenge_present: Boolean(pendingChallenge?.email),
        force_resend: Boolean(forceResend),
        allowed: false,
        blocked_reason: guardStart.reason,
        guard_state: getOtpSendGuardState(),
      });
      console.log('[auth_otp_request_blocked_guard]', {
        context: 'app_auth_modal',
        source_surface: authContext || 'unknown',
        route,
        reason: guardStart.reason,
        last_context: guardStart.lastContext || null,
        age_ms: guardStart.ageMs || 0,
      });
      setAuthError('Please wait a moment before requesting another code.');
      return;
    }
    console.log('[auth_request_started]', {
      context: 'app_auth_modal',
      source_surface: authContext || 'unknown',
      mode: authMode,
      email_present: Boolean(authEmail),
      route,
      guard_state: getOtpSendGuardState(),
    });

    setLoading(true);
    authRequestInFlightRef.current = true;
    setAuthError('');
    setAuthMessage('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        clearPendingAuthChallenge();
        recordAuthOtpAttribution({
          event: 'otp_send_blocked_existing_session',
          source_surface: authContext || 'unknown',
          route,
          auth_mode: authMode,
          email: normalizedEmail,
          app_thinks_authenticated: Boolean(currentAuthUserId || currentAppUserId),
          current_auth_user_id: currentAuthUserId || null,
          current_app_user_id: currentAppUserId || null,
          session_present: true,
          session_auth_user_id: session.user.id,
          pending_challenge_present: Boolean(pendingChallenge?.email),
          force_resend: Boolean(forceResend),
          allowed: false,
          blocked_reason: 'existing_supabase_session',
        });
        console.log('[auth_otp_request_blocked_existing_session]', {
          context: 'app_auth_modal',
          source_surface: authContext || 'unknown',
          route,
          auth_user_id: session.user.id,
        });
        setAuthMessage('You are already signed in.');
        setShowAuthModal(false);
        return;
      }
      recordAuthOtpAttribution({
        event: 'otp_send_allowed',
        source_surface: authContext || 'unknown',
        route,
        auth_mode: authMode,
        email: normalizedEmail,
        app_thinks_authenticated: Boolean(currentAuthUserId || currentAppUserId),
        current_auth_user_id: currentAuthUserId || null,
        current_app_user_id: currentAppUserId || null,
        session_present: Boolean(session?.user),
        session_auth_user_id: session?.user?.id || null,
        pending_challenge_present: Boolean(pendingChallenge?.email),
        force_resend: Boolean(forceResend),
        allowed: true,
      });
      console.log('[auth_method_selected]', {
        context: 'app_auth_modal',
        source_surface: authContext || 'unknown',
        method: 'email_otp',
        link_send_disabled: true,
        route,
      });
      const { error } = await supabase.auth.signInWithOtp({
        email: authEmail,
        options: {
          shouldCreateUser: authMode !== 'login',
        },
      });

      if (error) throw error;

      setPendingAuthChallenge({
        email: authEmail,
        context: authContext || 'general',
        started_at: new Date().toISOString(),
      });
      setOtpSent(true);
      setAuthMessage('We emailed you a 6-digit code. Enter it here to continue.');
      console.log('[auth_otp_requested_success]', {
        context: 'app_auth_modal',
        source_surface: authContext || 'unknown',
        method: 'email_otp',
        link_send_used: false,
        route,
      });
    } catch (error) {
      recordAuthOtpAttribution({
        event: 'otp_send_error',
        source_surface: authContext || 'unknown',
        route,
        auth_mode: authMode,
        email: normalizedEmail,
        app_thinks_authenticated: Boolean(currentAuthUserId || currentAppUserId),
        current_auth_user_id: currentAuthUserId || null,
        current_app_user_id: currentAppUserId || null,
        pending_challenge_present: Boolean(pendingChallenge?.email),
        force_resend: Boolean(forceResend),
        allowed: false,
        blocked_reason: 'send_error',
        error_message: error?.message || String(error),
      });
      console.error('Auth error', error);
      setAuthError(error.message || 'Something went wrong. Try again.');
    } finally {
      finishOtpSendAttempt();
      authRequestInFlightRef.current = false;
      setLoading(false);
    }
  };

  // NEW: Handle OTP verification
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setAuthError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    setAuthError('');

    try {
      console.log('🔑 Verifying OTP code...');
      const { data, error } = await supabase.auth.verifyOtp({
        email: authEmail,
        token: otpCode,
        type: 'email',
      });

      if (error) throw error;

      clearPendingAuthChallenge();
      console.log('✅ OTP verified successfully!', data);
      setAuthMessage('Success! Logging you in...');
      
      // Modal will close automatically via auth state change listener
    } catch (error) {
      console.error('OTP verification error', error);
      setAuthError(error.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Go back to email input
  const handleBackToEmail = () => {
    clearPendingAuthChallenge();
    setOtpSent(false);
    setOtpCode('');
    setAuthError('');
    setAuthMessage('');
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (!BETA_MODE && e.target === e.currentTarget) {
          setShowAuthModal(false);
        }
      }}
    >
      <div
        className="bg-slate-800 rounded-3xl p-8 max-w-md w-full relative border-2 border-purple-500 max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        {!BETA_MODE && (
          <button
            onClick={BETA_MODE ? undefined : () => setShowAuthModal(false)}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-300 z-10"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">⚖️</div>
          <h2 className="text-3xl font-black text-slate-50 mb-2">
            {otpSent ? 'Enter Your Code' : 'Welcome'}
          </h2>
          <p className="text-slate-300">
            {otpSent 
              ? `We sent a 6-digit code to ${authEmail}`
              : (authMode === 'login'
                ? 'Enter your email, then enter the 6-digit code we send you'
                : 'Create your account by email, then enter the 6-digit code we send you')}
          </p>
          {/* OTP indicator */}
          {!otpSent && (
            <p className="text-xs text-purple-400 mt-2">
              ✨ We'll send a 6-digit code to your email
            </p>
          )}
        </div>

        {/* Benefits - only show before OTP sent */}
        {!otpSent && (
          <div className="bg-purple-900/20 rounded-xl p-4 mb-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-purple-200">
              <span className="text-green-400">✓</span>
              <span>Keep your coins & avatars forever</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-200">
              <span className="text-green-400">✓</span>
              <span>Access from any device</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-200">
              <span className="text-green-400">✓</span>
              <span>Compete on global leaderboard</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-purple-200">
              <span className="text-green-400">✓</span>
              <span>Get notified when trials end</span>
            </div>
          </div>
        )}

        {/* OTP CODE INPUT FORM - shown after email sent in PWA mode */}
        {otpSent ? (
          <form onSubmit={handleVerifyOtp} className="space-y-2">
            <label
              htmlFor="otp-code"
              className="block text-sm font-medium text-slate-200 mb-1"
            >
              6-Digit Code
            </label>
            <input
              id="otp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              value={otpCode}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, '');
                setOtpCode(value);
              }}
              placeholder="123456"
              disabled={loading}
              className="w-full bg-slate-900/70 border border-slate-600 rounded-xl px-4 py-3 text-slate-50 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3 text-center text-2xl tracking-widest font-mono"
              autoFocus
              required
            />

            {/* Message / errors */}
            {authMessage && (
              <p className="text-sm text-green-400 mb-2">{authMessage}</p>
            )}
            {authError && (
              <p className="text-sm text-red-400 mb-2">{authError}</p>
            )}

            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              className={`w-full rounded-xl py-3 font-black text-sm mt-2 flex items-center justify-center gap-2
                ${
                  loading || otpCode.length !== 6
                    ? 'bg-slate-600 text-slate-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                }`}
            >
              {loading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Code
                  <span className="text-lg">🔓</span>
                </>
              )}
            </button>

            {/* Back button */}
            <button
              type="button"
              onClick={handleBackToEmail}
              className="w-full text-purple-400 text-sm hover:text-purple-300 mt-3"
            >
              ← Use a different email
            </button>

            {/* Resend code */}
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtpCode('');
                setTimeout(() => handleSendEmail({ preventDefault: () => {} }, { forceResend: true }), 100);
              }}
              className="w-full text-slate-400 text-xs hover:text-slate-300 mt-2"
            >
              Didn't get the code? Send again
            </button>
          </form>
        ) : (
          /* EMAIL INPUT FORM - original form */
          <form onSubmit={handleSendEmail} className="space-y-2">
            <label
              htmlFor="auth-email"
              className="block text-sm font-medium text-slate-200 mb-1"
            >
              Email Address
            </label>
            <input
              ref={emailInputRef}
              id="auth-email"
              type="email"
              autoComplete="email"
              value={authEmail}
              onChange={(e) => {
                console.log('📧 Input onChange fired, value:', e.target.value);
                setAuthEmail(e.target.value);
              }}
              onFocus={() => console.log('📧 Input focused')}
              onBlur={() => console.log('📧 Input blurred')}
              onInput={(e) => console.log('📧 onInput fired:', e.target.value)}
              placeholder="your.email@example.com"
              disabled={loading}
              className="w-full bg-slate-900/70 border border-slate-600 rounded-xl px-4 py-3 text-slate-50 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
              style={{ pointerEvents: loading ? 'none' : 'auto' }}
              required
            />

            {/* Message / errors */}
            {authMessage && (
              <p className="text-sm text-green-400 mb-2">{authMessage}</p>
            )}
            {authError && (
              <p className="text-sm text-red-400 mb-2">{authError}</p>
            )}

            <button
              type="submit"
              disabled={loading || !authEmail}
              className={`w-full rounded-xl py-3 font-black text-sm mt-2 flex items-center justify-center gap-2
                ${
                  loading || !authEmail
                    ? 'bg-slate-600 text-slate-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                }`}
            >
              {loading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  Send Code
                  <span className="text-lg">🔢</span>
                </>
              )}
            </button>
          </form>
        )}

        {/* Toggle - only show before OTP sent */}
        {!otpSent && (
          <div className="mt-6 text-center">
            <button
              onClick={() =>
                setAuthMode(authMode === 'login' ? 'signup' : 'login')
              }
              className="text-purple-400 text-sm hover:text-purple-300"
            >
              {authMode === 'login'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function HowToPlayModal({ onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[10000] bg-slate-950/92 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4">
      <div
        className="bg-slate-800 w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-3xl p-6 sm:p-8 relative max-h-screen overflow-y-auto border-t-4 sm:border-2 border-purple-500 shadow-2xl shadow-black/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-to-play-title"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onTouchCancel={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-slate-300"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6 pt-6 sm:pt-0">
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-500/20 border border-purple-400/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-200 mb-4">
            How To Play
          </div>
          <div className="text-6xl mb-4">⚖️</div>
          <h2 id="how-to-play-title" className="text-3xl font-black text-slate-50 mb-2">How to Play GweffIT</h2>
          <p className="text-slate-300">Settle arguments. Earn coins. Collect avatars.</p>
        </div>

        <div className="space-y-6">
          <div className="flex items-start gap-4 p-4 bg-blue-900/20 rounded-xl">
            <div className="text-4xl flex-shrink-0">1️⃣</div>
            <div>
              <h3 className="font-black text-lg text-slate-50 mb-2">Vote as a Juror (FREE)</h3>
              <p className="text-slate-200 text-sm mb-2">Browse trials and cast your vote. Your vote is private - nobody can see how you voted.</p>
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
                Earn +50 Coins per vote
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-purple-900/20 rounded-xl">
            <div className="text-4xl flex-shrink-0">2️⃣</div>
            <div>
              <h3 className="font-black text-lg text-slate-50 mb-2">Post Your Own Trial</h3>
              <p className="text-slate-200 text-sm mb-2">Start a call, pull in the jury, and come back when the verdict lands.</p>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-bold">
                Build your own receipt
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-pink-900/20 rounded-xl border border-pink-500/30">
            <div className="text-4xl flex-shrink-0">3️⃣</div>
            <div>
              <h3 className="font-black text-lg text-slate-50 mb-2">Spend Coins on Avatars</h3>
              <p className="text-slate-200 text-sm mb-2">Unlock unique avatars (500-10,000 coins) to customize your profile. Collect rare ones to show off!</p>
              <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs font-bold">
                25 unique avatars to collect
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-orange-900/20 rounded-xl border border-orange-500/30">
            <div className="text-4xl flex-shrink-0">4️⃣</div>
            <div>
              <h3 className="font-black text-lg text-slate-50 mb-2">Share Verdict Memes</h3>
              <p className="text-slate-200 text-sm mb-2">Every verdict is a shareable meme with SNARKY commentary. Download or share to social media!</p>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold">
                Viral growth = free marketing
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3"></div>
        <div className="mt-6 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-4 border-2 border-purple-500/30">
          <div className="flex items-start gap-3">
            <Sparkles className="text-purple-600 flex-shrink-0 mt-1" size={24} />
            <div className="text-sm text-purple-800">
              <div className="font-bold mb-1">Pro Tip:</div>
              <div>Vote consistently to build your streak (🔥), earn XP, and unlock achievements. The more you participate, the more coins you earn!</div>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition-all"
        >
          Got it! Let's Play
        </button>
      </div>
    </div>
  );
}

// ========= MAIN COMPONENT =========
function GweffIT({ initialView = 'home' }) {
  // STATE DECLARATIONS START HERE
  const { follows } = useFollows();
  const [view, setView] = useState(initialView);
  const [isTabActive, setIsTabActive] = useState(true);
  const [user, setUser] = useState({
  id: null,
  username: 'Loading...',
  points: 0,
  streak: 0,
  coins: 1000,
  trialsJoined: 0,
  isPremium: false,
  ownedAvatars: ['default'],
  selectedAvatar: 'default',
});

// Notify FollowsContext whenever the authenticated user ID changes
useEffect(() => {
  window.dispatchEvent(
    new CustomEvent('gweffit:user-changed', { detail: { userId: user?.id || null } })
  );
}, [user?.id]);

// === Push notifications state & effects (Fix #6) ===
const [userLoading, setUserLoading] = useState(true);
const [pushStatus, setPushStatus] = useState('idle'); // 'idle' | 'enabled' | 'blocked' | 'unsupported'

// 🔥 Auto-enable push on native after login (critical for test button to work)
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!isNativeApp()) return;
  if (!user?.id) return;

  const now = Date.now();
  try {
    const raw = safeStorage.get('gweffit_push_auto_register_lock');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        parsed.userId === user.id &&
        typeof parsed.ts === 'number' &&
        now - parsed.ts < 90_000
      ) {
        console.log('[push] auto_register_skipped', {
          reason: 'session_cooldown',
          user_id: user.id,
        });
        return;
      }
    }
  } catch {}
  const recentlyAttemptedSameUser =
    pushAutoRegisterRef.current.lastUserId === user.id &&
    now - pushAutoRegisterRef.current.lastAttemptAt < 90_000;
  if (pushAutoRegisterRef.current.inFlight || recentlyAttemptedSameUser) {
    console.log('[push] auto_register_skipped', {
      reason: pushAutoRegisterRef.current.inFlight ? 'in_flight' : 'cooldown',
      user_id: user.id,
    });
    return;
  }

  pushAutoRegisterRef.current.inFlight = true;
  pushAutoRegisterRef.current.lastUserId = user.id;
  pushAutoRegisterRef.current.lastAttemptAt = now;
  try {
    safeStorage.set(
      'gweffit_push_auto_register_lock',
      JSON.stringify({ userId: user.id, ts: now })
    );
  } catch {}

  // Register on native to keep APNs/FCM fresh and mapped to current user.
  console.log('[push] 🚀 Auto-registering push for native app');
  enablePush().catch((e) => {
    console.warn('[push] ⚠️ Auto enable failed:', e);
  }).finally(() => {
    pushAutoRegisterRef.current.inFlight = false;
  });
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- enablePush intentionally stable

// If we already received a token before user.id was stable, persist it now
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!isNativeApp()) return;
  if (!user?.id) return;
  try {
    const pending = safeStorage.get('gweffit_last_native_token');
    if (!pending) return;
    persistPushEnabled({
      userId: user.id,
      token: pending,
      platform: Capacitor.getPlatform(),
    }).catch(() => {});
  } catch {}
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- persistPushEnabled intentionally stable

const [showPushPrompt, setShowPushPrompt] = useState(false);
// Only show the big nudge when we still need permission

const [showCreateCrewModal, setShowCreateCrewModal] = useState(false);
// ADD THIS - Crew join token detection
const [crewJoinToken, setCrewJoinToken] = useState(null);
const [suppressCrewJoin, setSuppressCrewJoin] = useState(false);
const [crewRefreshKey, setCrewRefreshKey] = useState(0);
const [showTemplateSelector, setShowTemplateSelector] = useState(false);
const [showShareModal, setShowShareModal] = useState(false);
const [verdictForMeme, setVerdictForMeme] = useState(null);
const [sharedImageBlob, setSharedImageBlob] = useState(null);
// Notification permission modal state
const [showNotificationModal, setShowNotificationModal] = useState(false);
const [notificationTriggerContext, setNotificationTriggerContext] = useState('default');
const nativePushWiredRef = useRef(false);
const userIdRef = useRef(null);
const authUserIdRef = useRef(null);
const reliabilityCountersRef = useRef({
  identity_mismatch: 0,
  stale_response_dropped: 0,
  token_conflict_resolved: 0,
  authoritative_state_missing: 0,
});
const lastHealthSnapshotAtRef = useRef(0);
const finalizeInFlightRef = useRef(new Set());
const finalizeCompletedRef = useRef(new Set());
const finalizeLastAttemptRef = useRef(new Map());
const finalizeSuppressedUntilRef = useRef(new Map());
const trialTimerStageRef = useRef(new Map());
const appResumeInFlightRef = useRef(false);
const appResumeWatchdogRef = useRef(null);
const appResumeLastRunRef = useRef(0);
const appResumeRecoveryCountRef = useRef(0);
const appResumeDebounceTimerRef = useRef(null);
const lastBootstrappedAuthUserRef = useRef(null);
const pushTokenRegisterInFlightRef = useRef(new Set());
const pushTokenRegisterCooldownRef = useRef(new Map());
const pushAutoRegisterRef = useRef({
  inFlight: false,
  lastUserId: null,
  lastAttemptAt: 0,
});
const pendingWhatChangedScrollRef = useRef(false);
const nativeRegisterAttemptRef = useRef({
  inFlight: false,
  lastAttemptAt: 0,
});
const verdictMemeRecoveryTriggeredRef = useRef(new Set());
const verdictMemeRecoveryInFlightRef = useRef(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      userIdRef.current = user?.id || null;
    }, [user?.id]);

const isLoadingUserRef = useRef(false);
// 🔒 CRITICAL: Use useRef instead of useState for synchronous deduplication
const generatingVerdictsRef = useRef(new Set());
// 🆕 Safety timeout to prevent infinite loading
  // ⬅️ run once per app load
  useUpdateLastLogin();
  const navigate = useNavigate();
  const verdictUpdateDebounce = useRef(null);

  // If a crew join just completed, force profile and suppress join page
  useEffect(() => {
    const justJoined = safeStorage.get('crewJoinJustJoined');
    if (justJoined && crewJoinToken && justJoined.toLowerCase() === crewJoinToken.toLowerCase()) {
      safeStorage.remove('crewJoinJustJoined');
      setCrewJoinToken(null);
      setSuppressCrewJoin(true);
      navigate('/profile', { replace: true });
    }
  }, [crewJoinToken, navigate]);

  // 🆕 ADD THIS - Clear corrupted data from old version
  // ⚠️ IMPORTANT: Don't clear Supabase auth session (stored in sb-*-auth-token)
  useEffect(() => {
  try {
    const APP_VERSION = '2.0'; // Bump this when you need to force clear
    const storedVersion = safeStorage.get('gweffit_app_version');

    if (storedVersion !== APP_VERSION) {
      console.log('🔄 Migrating to app version', APP_VERSION);
      safeStorage.remove('gweffit_user_id');
      safeStorage.remove('gweffit_user_local');
      safeStorage.set('gweffit_app_version', APP_VERSION);

      // Only reload once per real version change
      setTimeout(() => {
        window.location.reload();
      }, 0);
    }
  } catch (e) {
    console.warn('Version migration skipped due to storage error:', e);
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- WEB_PUSH_ENABLED handled inside

// Initialize Firebase Messaging on app start (web only)
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  // Web-only and only when web push is enabled
  if (!WEB_PUSH_ENABLED || isNativeApp()) return;
  initializeFirebaseMessaging();
  setupForegroundMessageHandler((payload) => {
    console.log('📬 In-app notification:', payload);
  });
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- constants/refs intentionally stable

// ========= PRODUCTION-READY VERSION (SMART FALLBACK) =========
// This tries the real Edge Function first
// Falls back to mock if CORS fails (localhost development)
// Will automatically work in production without code changes!

// ========================================
// DIRECT FETCH - HARDCODED FOR iOS DEBUG
// ========================================

const EDGE_FUNCTION_URL = 'https://nemmitznpthnrshbjipz.supabase.co/functions/v1/generate-verdict';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5lbW1pdHpucHRobnJzaGJqaXB6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjEwNzg0MjIsImV4cCI6MjA3NjY1NDQyMn0.NCtVTP4zZJttp4a9BuxAoD1yo4Ik8r8gSxiiAnWrhwk';

const fetchWithTimeout = async (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const mergeServerVerdictAuthority = (serverVerdictData, candidateVerdictData) => {
  const server = (serverVerdictData && typeof serverVerdictData === 'object') ? serverVerdictData : {};
  const candidate = (candidateVerdictData && typeof candidateVerdictData === 'object') ? candidateVerdictData : {};
  const merged = { ...server, ...candidate };

  if (server.verdict_source) merged.verdict_source = server.verdict_source;
  else if (!merged.verdict_source) delete merged.verdict_source;

  if (server.reason_code) merged.reason_code = server.reason_code;
  else if (!merged.reason_code) delete merged.reason_code;

  return merged;
};

const withTimeout = useCallback(async (promise, timeoutMs, label = 'operation_timeout') => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(label)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- constants/refs intentionally stable

const nextRequestId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const logHealthSnapshot = useCallback((reason = 'unknown') => {
  const now = Date.now();
  if (now - lastHealthSnapshotAtRef.current < 4000) return;
  lastHealthSnapshotAtRef.current = now;
  console.log('[health_snapshot]', {
    reason,
    auth_user_id: authUserIdRef.current || null,
    app_user_id: userIdRef.current || null,
    visibility_state: typeof document !== 'undefined' ? document.visibilityState : null,
    channel_count: supabase.getChannels().length,
    counters: { ...reliabilityCountersRef.current },
  });
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- constants/refs intentionally stable

// [TRIAL_FINALIZE_SERVER_OWNER]
const FINALIZE_RETRY_MIN_INTERVAL_MS = 90 * 1000;
const FINALIZE_ERROR_BACKOFF_MS = 120 * 1000;
const FINALIZE_EXPIRES_GRACE_MS = 10 * 1000;
const FINALIZE_TERMINAL_STATUSES = new Set(['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed']);
const FINALIZE_OPEN_STATUSES = new Set(['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending']);
const FINALIZE_BULK_DISABLED_REASONS = new Set(['timer_expired', 'stale_scan', 'fallback_scan', 'mount']);
const FINALIZE_DETAIL_REASON_PREFIX = 'trial_detail';
const FINALIZE_NON_OWNER_REASONS = new Set(['jury_full_vote', 'vote_blowout']);

// eslint-disable-next-line react-hooks/exhaustive-deps
const finalizeTrialServer = useCallback(async (trialId, context = {}) => {
  if (!trialId) return { ok: false, skipped: true, reason: 'missing_trial_id' };
  const priorStatus = String(context?.priorStatus || '').toLowerCase();
  const expiresAt = context?.expiresAt || null;
  const reason = context?.reason || 'client_gate';
  const now = Date.now();
  if (finalizeCompletedRef.current.has(trialId)) {
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'already_finalized_local', prior_status: priorStatus });
    return { ok: true, skipped: true, reason: 'already_finalized_local' };
  }
  if (finalizeInFlightRef.current.has(trialId)) {
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'in_flight', prior_status: priorStatus });
    return { ok: false, skipped: true, reason: 'in_flight' };
  }
  const storageBackoffKey = `trial_finalize_backoff_until:${trialId}`;
  let persistedSuppressedUntil = 0;
  try {
    persistedSuppressedUntil = Number(safeStorage.get(storageBackoffKey) || 0);
  } catch {}
  const suppressedUntil = Math.max(
    finalizeSuppressedUntilRef.current.get(trialId) || 0,
    persistedSuppressedUntil
  );
  if (suppressedUntil > now) {
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'suppressed_backoff', prior_status: priorStatus, retry_after_ms: suppressedUntil - now });
    return { ok: false, skipped: true, reason: 'suppressed_backoff' };
  }
  const lastAttemptAt = finalizeLastAttemptRef.current.get(trialId) || 0;
  if (now - lastAttemptAt < FINALIZE_RETRY_MIN_INTERVAL_MS) {
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'throttled', prior_status: priorStatus, retry_after_ms: FINALIZE_RETRY_MIN_INTERVAL_MS - (now - lastAttemptAt) });
    return { ok: false, skipped: true, reason: 'throttled' };
  }
  finalizeInFlightRef.current.add(trialId);
  setFinalizeInProgressTrials(prev => {
    if (prev.has(trialId)) return prev;
    const next = new Set(prev);
    next.add(trialId);
    return next;
  });
  finalizeLastAttemptRef.current.set(trialId, now);
  const startedAt = Date.now();
  const invokeFinalize = async () => withTimeout(
    supabase.functions.invoke('finalize-trial-status', {
      body: { trial_id: trialId, reason },
    }),
    10_000,
    'trial_finalize_invoke_timeout'
  );
  const getStatus = (err, payload) => {
    const statusFromErr = Number(err?.context?.status || err?.status || 0);
    if (statusFromErr) return statusFromErr;
    const statusFromPayload = Number(payload?.status || payload?.status_code || 0);
    return statusFromPayload || 0;
  };
  const isRetryable = (status, err) => {
    if (status === 401 || status === 403 || status === 404) return false;
    if (status >= 500) return true;
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('network') || msg.includes('timeout') || msg.includes('failed to fetch');
  };
  const applyCooldown = (ms = FINALIZE_ERROR_BACKOFF_MS) => {
    const nextAllowedAt = Date.now() + ms;
    finalizeSuppressedUntilRef.current.set(trialId, nextAllowedAt);
    try {
      safeStorage.set(storageBackoffKey, String(nextAllowedAt));
    } catch {}
  };
  try {
    console.log('[trial_finalize_attempt]', {
      trial_id: trialId,
      prior_status: priorStatus || null,
      expires_at: expiresAt,
      now: new Date().toISOString(),
      reason,
    });
    let data = null;
    let error = null;
    ({ data, error } = await invokeFinalize());
    if (error) {
      const status = getStatus(error, null);
      if (isRetryable(status, error)) {
        const retryDelay = 400 + Math.floor(Math.random() * 500);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        ({ data, error } = await invokeFinalize());
      }
    }
    if (error) {
      const errorPayload = error?.context || {};
      const status = getStatus(error, errorPayload);
      applyCooldown(FINALIZE_ERROR_BACKOFF_MS);
      console.warn('[trial_finalize_error]', {
        trial_id: trialId,
        prior_status: priorStatus || null,
        expires_at: expiresAt,
        http_status: status || null,
        buildId: errorPayload?.buildId || null,
        request_id: errorPayload?.request_id || null,
        error: error.message || String(error),
        elapsed_ms: Date.now() - startedAt,
      });
      setFinalizeErrorTrials(prev => {
        const next = new Set(prev);
        next.add(trialId);
        return next;
      });
      return { ok: false, error: error.message || String(error), buildId: errorPayload?.buildId || null, status };
    }
    if (!data?.ok) {
      const status = getStatus(null, data);
      if (status === 401 || status === 403 || status === 404) {
        applyCooldown(FINALIZE_ERROR_BACKOFF_MS);
      } else {
        applyCooldown(FINALIZE_ERROR_BACKOFF_MS);
      }
      console.warn('[trial_finalize_error]', {
        trial_id: trialId,
        prior_status: priorStatus || null,
        expires_at: expiresAt,
        http_status: status || null,
        buildId: data?.buildId || null,
        request_id: data?.request_id || null,
        error: data?.error || 'finalize_response_not_ok',
        elapsed_ms: Date.now() - startedAt,
      });
      return { ok: false, error: data?.error || 'finalize_response_not_ok', buildId: data?.buildId || null, status };
    }
    finalizeSuppressedUntilRef.current.delete(trialId);
    try {
      safeStorage.remove(storageBackoffKey);
    } catch {}
    const alreadyFinalized = !!data?.already_finalized;
    const changed = !!data?.changed;
    const nextStatus = String(data?.terminal_status || '').toLowerCase();
    if (alreadyFinalized || changed || FINALIZE_TERMINAL_STATUSES.has(nextStatus)) {
      finalizeCompletedRef.current.add(trialId);
    }
    setFinalizeErrorTrials(prev => {
      if (!prev.has(trialId)) return prev;
      const next = new Set(prev);
      next.delete(trialId);
      return next;
    });
    console.log('[trial_finalize_result]', {
      trial_id: trialId,
      result: alreadyFinalized ? 'already_finalized' : (changed ? 'changed' : 'noop'),
      prior_status: priorStatus || null,
      next_status: nextStatus || null,
      expires_at: expiresAt,
      buildId: data?.buildId || null,
      request_id: data?.request_id || null,
      elapsed_ms: Date.now() - startedAt,
    });
    return { ok: true, changed, already_finalized: alreadyFinalized, data };
  } catch (e) {
    const nextAllowedAt = Date.now() + FINALIZE_ERROR_BACKOFF_MS;
    finalizeSuppressedUntilRef.current.set(trialId, nextAllowedAt);
    try {
      safeStorage.set(storageBackoffKey, String(nextAllowedAt));
    } catch {}
    console.warn('[trial_finalize_error]', {
      trial_id: trialId,
      prior_status: priorStatus || null,
      expires_at: expiresAt,
      error: e?.message || String(e),
      elapsed_ms: Date.now() - startedAt,
    });
    setFinalizeErrorTrials(prev => {
      const next = new Set(prev);
      next.add(trialId);
      return next;
    });
    return { ok: false, error: e?.message || String(e) };
  } finally {
    finalizeInFlightRef.current.delete(trialId);
    setFinalizeInProgressTrials(prev => {
      if (!prev.has(trialId)) return prev;
      const next = new Set(prev);
      next.delete(trialId);
      return next;
    });
  }
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- constants/refs intentionally stable

// eslint-disable-next-line react-hooks/exhaustive-deps
const maybeFinalizeTrial = useCallback(async (trial, reason = 'unknown') => {
  const trialId = String(trial?.id || '').trim();
  if (!trialId) {
    console.log('[trial_finalize_skip_reason]', { trial_id: null, reason: 'missing_trial_id' });
    return { ok: false, skipped: true, reason: 'missing_trial_id' };
  }

  const statusNorm = String(trial?.status || '').toLowerCase();
  const expiresAtRaw = trial?.expiresAt || trial?.expires_at || null;
  const expiresAtMs = expiresAtRaw ? new Date(expiresAtRaw).getTime() : NaN;
  const actorUserId = user?.id || null;
  const ownerId = trial?.created_by || null;
  const normalizedReason = String(reason || '');
  const allowNonOwner = FINALIZE_NON_OWNER_REASONS.has(normalizedReason);
  const skipReason =
    (!actorUserId) ? 'missing_user' :
    (!allowNonOwner && (!ownerId || ownerId !== actorUserId)) ? 'not_owner' :
    (!allowNonOwner && !normalizedReason.startsWith(FINALIZE_DETAIL_REASON_PREFIX)) ? 'non_detail_trigger' :
    FINALIZE_BULK_DISABLED_REASONS.has(normalizedReason) ? 'bulk_finalize_disabled' :
    !expiresAtRaw ? 'missing_expires_at' :
    (!Number.isFinite(expiresAtMs) ? 'invalid_expires_at' : null) ||
    (Date.now() < (expiresAtMs + FINALIZE_EXPIRES_GRACE_MS) ? 'not_expired' : null);
  if (skipReason) {
    console.log('[trial_finalize_skip_reason]', {
      trial_id: trialId,
      reason: skipReason,
      trigger_reason: reason,
      prior_status: statusNorm || null,
      expires_at: expiresAtRaw,
      actor_user_id: actorUserId,
      created_by: ownerId,
    });
    return { ok: true, skipped: true, reason: skipReason };
  }
  if (FINALIZE_TERMINAL_STATUSES.has(statusNorm)) {
    finalizeCompletedRef.current.add(trialId);
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'already_terminal', prior_status: statusNorm });
    return { ok: true, skipped: true, reason: 'already_terminal' };
  }
  if (!FINALIZE_OPEN_STATUSES.has(statusNorm)) {
    console.log('[trial_finalize_skip_reason]', { trial_id: trialId, reason: 'non_finalizable_status', prior_status: statusNorm });
    return { ok: false, skipped: true, reason: 'non_finalizable_status' };
  }
  const result = await finalizeTrialServer(trialId, {
    reason,
    priorStatus: statusNorm,
    expiresAt: expiresAtRaw,
  });
  if (result?.ok && (result?.changed || result?.already_finalized)) {
    const { data: refreshedTrial, error: refreshedErr } = await supabase
      .from('trials')
      .select('*')
      .eq('id', trialId)
      .maybeSingle();
    if (refreshedErr) {
      console.warn('[trial_finalize_error]', {
        trial_id: trialId,
        prior_status: statusNorm,
        error: refreshedErr.message || String(refreshedErr),
        reason: 'post_finalize_refetch_failed',
      });
    } else if (refreshedTrial?.id) {
      console.log('[trial_realtime_update]', {
        trial_id: refreshedTrial.id,
        prior_status: statusNorm || null,
        next_status: String(refreshedTrial.status || '').toLowerCase() || null,
        expires_at: refreshedTrial.expires_at || null,
        source: 'post_finalize_refetch',
      });
      setTrials(prev => prev.map(existing => {
        if (existing.id !== trialId) return existing;
        const totalVotes = refreshedTrial.total_votes ?? ((refreshedTrial.votes_a || 0) + (refreshedTrial.votes_b || 0));
        const expiresAt = refreshedTrial.expires_at ?? existing.expiresAt;
        const nextTimeLeft = expiresAt
          ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000))
          : existing.timeLeft;
        return {
          ...existing,
          status: refreshedTrial.status ?? existing.status,
          votesA: refreshedTrial.votes_a ?? existing.votesA,
          votesB: refreshedTrial.votes_b ?? existing.votesB,
          totalVotes,
          jurorsFilled: refreshedTrial.jurors_filled ?? existing.jurorsFilled,
          expiresAt,
          timeLeft: nextTimeLeft,
          closedAt: refreshedTrial.closed_at ? new Date(refreshedTrial.closed_at).getTime() : existing.closedAt,
          verdict_data: refreshedTrial.verdict_data ?? existing.verdict_data,
        };
      }));
    }
  }
  return result;
}, [finalizeTrialServer, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- constants intentionally stable

const openNotificationPermissionModal = useCallback((triggerContext = 'default') => {
  if (isNativeApp()) {
    console.log('[pwa] pwa_prompt_suppressed_native', {
      platform: Capacitor?.getPlatform?.() || 'native',
      trigger_context: triggerContext,
    });
    return;
  }
  setNotificationTriggerContext(triggerContext);
  setShowNotificationModal(true);
}, []);

const fetchAIVerdict = useCallback(async (trialId) => {
  const isCapacitor = typeof window !== 'undefined' && !!window.Capacitor;
  const platform = isCapacitor ? 'iOS/Capacitor' : 'Web';
  
  console.log(`🎯 [${platform}] fetchAIVerdict called for trial: ${trialId}`);
  
  // 🔒 SYNCHRONOUS CHECK: Use ref instead of state to prevent race condition
  if (generatingVerdictsRef.current.has(trialId)) {
    console.log(`⏸️ [${platform}] Already generating verdict for trial ${trialId}, skipping duplicate`);
    return null;
  }
  
  // 🔒 SYNCHRONOUS ADD: Add to ref immediately (not async like setState)
  generatingVerdictsRef.current.add(trialId);
  
  try {
    console.log(`📞 [${platform}] Calling Edge Function via direct fetch...`);
    console.log(`📞 [${platform}] URL: ${EDGE_FUNCTION_URL}`);
    
    const response = await fetchWithTimeout(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ trialId }),
    }, 15000);
    
    console.log(`📞 [${platform}] Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error(`❌ [${platform}] HTTP error: ${response.status} - ${errorText}`);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log(`📞 [${platform}] Response received, keys: ${Object.keys(data || {}).join(', ')}`);
    
    if (!data) {
      console.error(`❌ [${platform}] No data returned from Edge Function`);
      return null;
    }
    
    // ✅ Handle server-side "pending" status (another request is generating)
    if (data.status === "pending") {
      console.log(`⏳ [${platform}] ${data.message || 'Verdict being generated'}, will retry...`);
      
      // Wait 2 seconds and retry once
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const retryResponse = await fetchWithTimeout(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ trialId }),
      }, 15000);
      
      if (!retryResponse.ok) {
        console.log(`⏸️ [${platform}] Retry failed with status ${retryResponse.status}`);
        return null;
      }
      
      const retryData = await retryResponse.json();
      
      if (!retryData || retryData.status === "pending") {
        console.log(`⏸️ [${platform}] Still generating after retry, giving up`);
        return null;
      }
      
      console.log(`✅ [${platform}] AI verdict received (after retry)`);
      return {
        ...retryData,
        aiImage: retryData.image_url || null,
        aiPunchlines: {
          roast: retryData.roast_punchlines || [retryData.roast_punchline],
          sports: retryData.sports_punchlines || [retryData.sports_punchline],
          internet: retryData.internet_punchlines || [retryData.internet_punchline],
        },
      };
    }
    
    console.log(`✅ [${platform}] AI verdict received! image_url: ${data.image_url ? 'YES' : 'NO'}`);
    
    return {
      ...data,
      aiImage: data.image_url || null,
      aiPunchlines: {
        roast: data.roast_punchlines || [data.roast_punchline],
        sports: data.sports_punchlines || [data.sports_punchline],
        internet: data.internet_punchlines || [data.internet_punchline],
      },
    };
    
  } catch (error) {
    console.error(`❌ [${platform}] fetchAIVerdict error:`, error?.message || error);
    return null;
  } finally {
    generatingVerdictsRef.current.delete(trialId);
  }
}, []);

const persistTrialVerdictData = useCallback(async (trialId, candidateVerdictData) => {
  if (!trialId) return candidateVerdictData;

  let serverVerdictData = {};
  const { data: currentTrial, error: currentErr } = await supabase
    .from('trials')
    .select('verdict_data')
    .eq('id', trialId)
    .maybeSingle();

  if (currentErr) {
    console.warn('[trial_verdict_persist_warn]', {
      trial_id: trialId,
      stage: 'read_current',
      error: currentErr.message || String(currentErr),
    });
  } else {
    serverVerdictData = currentTrial?.verdict_data || {};
  }

  const mergedVerdictData = mergeServerVerdictAuthority(serverVerdictData, candidateVerdictData);
  const { error: updateErr } = await supabase
    .from('trials')
    .update({ verdict_data: mergedVerdictData })
    .eq('id', trialId);

  if (updateErr) {
    throw updateErr;
  }
  return mergedVerdictData;
}, []);

  const resolveCanonicalUserIds = useCallback(async ({ authUserId, hintAppUserId, reason = 'unknown', requestId = null }) => {
    const reqId = requestId || nextRequestId('identity');
    let mappedAppUserId = null;

    if (authUserId) {
      const { data: byAuth, error: byAuthErr } = await supabase
        .from('users')
        .select('id, auth_user_id')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      if (byAuthErr) {
        console.warn('[identity] lookup_by_auth_failed', {
          request_id: reqId,
          reason,
          auth_user_id: authUserId,
          error: byAuthErr.message || String(byAuthErr),
        });
      }
      mappedAppUserId = byAuth?.id || null;
    }

    let resolvedAppUserId = mappedAppUserId || hintAppUserId || null;
    if (mappedAppUserId && hintAppUserId && mappedAppUserId !== hintAppUserId) {
      reliabilityCountersRef.current.identity_mismatch += 1;
      console.warn('[identity] mismatch_detected', {
        request_id: reqId,
        reason,
        auth_user_id: authUserId || null,
        mapped_app_user_id: mappedAppUserId,
        hinted_app_user_id: hintAppUserId,
        mismatch_count: reliabilityCountersRef.current.identity_mismatch,
      });
      resolvedAppUserId = mappedAppUserId;
    }

    if (!resolvedAppUserId && hintAppUserId) {
      const { data: byId } = await supabase
        .from('users')
        .select('id, auth_user_id')
        .eq('id', hintAppUserId)
        .maybeSingle();
      if (byId?.id) {
        resolvedAppUserId = byId.id;
      }
    }

    return {
      requestId: reqId,
      authUserId: authUserId || null,
      appUserId: resolvedAppUserId || null,
      mappedAppUserId: mappedAppUserId || null,
    };
  }, []);

  // Update user row fields only (canonical identity resolution; no anon recreation side-effects)
  const updateUserInSupabase = useCallback(async (hintUserId, updates) => {
    const identity = await resolveCanonicalUserIds({
      authUserId: authUserIdRef.current || null,
      hintAppUserId: hintUserId || null,
      reason: 'update_user_profile',
    });
    if (!identity.appUserId) {
      console.warn('[identity] update_skipped_no_app_user', {
        request_id: identity.requestId,
        auth_user_id: identity.authUserId,
      });
      return null;
    }

    const { data, error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', identity.appUserId)
      .select('id')
      .maybeSingle();

    if (error) {
      console.error('[identity] update_failed', {
        request_id: identity.requestId,
        app_user_id: identity.appUserId,
        error: error.message || String(error),
      });
      return null;
    }
    return data || { id: identity.appUserId };
  }, [resolveCanonicalUserIds]);

  // === Push Pref Helpers (Step 5A) — place right here ===
  const PUSH_KEY = 'gweffit_push_opt_in';

  const readPushPref = () => {
    try { return safeStorage.get(PUSH_KEY) === 'true'; } catch { return false; }
  };

  const writePushPref = (val) => {
    try { safeStorage.set(PUSH_KEY, val ? 'true' : 'false'); } catch {}
  };
 
useEffect(() => {
  // Skip browser notification checks on native
  if (isNativeApp()) {
    setPushStatus(readPushPref() ? 'enabled' : 'idle');
    return;
  }
  
  // Web-only checks
  if (!('Notification' in window)) { setPushStatus('unsupported'); return; }
  if (Notification.permission === 'denied') { setPushStatus('blocked'); return; }
  if (Notification.permission === 'default') { setPushStatus('idle'); return; }
  setPushStatus(readPushPref() ? 'enabled' : 'idle');
}, []);

// Clear badge count when app opens with authenticated user
useEffect(() => {
  if (!isNativeApp() || !user?.id) return;
  
  const clearBadge = async () => {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('[push] 🔔 Badge cleared on app open');
    } catch (e) {
      console.warn('[push] Could not clear badge:', e);
    }
  };
  
  clearBadge();
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- WEB_PUSH_ENABLED handled inside

// useAutoRefreshFCMToken(user?.id);

const getVerdictCopy = useCallback((trial, winner, forcedTone) => {
  console.log('🎯 getVerdictCopy CALLED for:', trial.title, 'winner:', winner);
  
  // Handle tie case
  const isTie = winner === 'Tie' || trial.votesA === trial.votesB;
  
  if (isTie) {
    const verdictType = "HUNG JURY";
    const punchline = "The jury is deadlocked. Neither side could secure the win.";
    const punchlines = [
      { text: "The jury is deadlocked. Neither side could secure the win.", style: 'auto', label: '🎯 Classic' },
      { text: "Split decision. The internet remains undefeated in its inability to agree.", style: 'roast', label: '🔥 Roast' },
      { text: "It's giving ✨indecisive energy✨. Jury said \"both of you sit down.\"", style: 'internet', label: '💀 Gen Z' }
    ];
    const verdictNumber = Math.floor(Math.random() * 90000) + 10000;
    return { verdictType, punchline, punchlines, verdictNumber, tone: 'judge', winnerPct: 50 };
  }
  
  const winnerVotes = winner === 'A' ? trial.votesA : trial.votesB;
  const total = Math.max(trial.totalVotes, 1);
  const winnerPct = +( (winnerVotes / total) * 100 ).toFixed(1);
  const marginPct = Math.abs(trial.votesA - trial.votesB) / total * 100;

  // classify the verdict type
  let verdictType;
  if (marginPct > 30) verdictType = "CRUSHING VERDICT";
  else if (marginPct > 15) verdictType = "CLEAR VERDICT";
  else verdictType = "CONTROVERSIAL VERDICT";

  // decide tone
  const tone = forcedTone || defaultToneByCategory(trial.category);
  // ✅ OPTION B: Generate 3 diverse verdict options
  // 1. Matched/Recommended (smart category detection)
  // 2. Classic Judge (safe fallback)
  // 3. Wild Card (random surprise)
  
  // Import verdict data for the enhanced punchline generator
  const verdictForPunchline = { 
    winner, 
    winnerPct 
  };
  
  // 🆕 Use the SPICY punchline generator instead of boring TONE_PACK
  // Option 1: Auto-detected best style (margin & keyword aware)
  const option1 = generatePunchline(trial, verdictForPunchline, 'auto');
  console.log('🎯 Generated punchlines:', option1);
  
  // Option 2: Roast style (always fun)
  const option2 = generatePunchline(trial, verdictForPunchline, 'roast');
  
  // Option 3: Internet/Gen Z style (viral-ready)
  const option3 = generatePunchline(trial, verdictForPunchline, 'internet');
  
  // Primary punchline (used for single display)
  const punchline = option1;
  
  // Three diverse options for user selection
  const punchlines = [
    { text: option1, style: 'auto', label: '🎯 Recommended' },
    { text: option2, style: 'roast', label: '🔥 Roast' },
    { text: option3, style: 'internet', label: '💀 Gen Z' }
  ];

const verdictNumber = Math.floor(Math.random() * 90000) + 10000;
  return { verdictType, punchline, punchlines, verdictNumber, tone, winnerPct };
}, []); // ← Added closing bracket and empty dependency array

// ========================================
// HOW THIS WORKS (NO CORS!)
// ========================================
// 1. Insert row into verdict_requests table (just a database write - no CORS)
// 2. Supabase webhook triggers Edge Function
// 3. Edge Function generates AI verdict, writes to verdict_cache
// 4. This function polls verdict_cache every 1 second
// 5. When verdict appears, return it!
// 
// ZERO CORS issues because we never call the Edge Function from the browser!
// ========================================

// This bypasses the Supabase client entirely and uses raw fetch()
// This will tell us if the issue is with:
// 1. The Edge Function (unlikely - it works in Supabase test)
// 2. The Supabase client library (likely)
// 3. CORS at infrastructure level

    useEffect(() => {
      const extractCrewToken = (locationLike) => {
        try {
          const url = new URL(locationLike || window.location.href, window.location.origin);
          const path = url.pathname || '';
          const search = url.searchParams;
          const pathMatch = path.match(/^\/(?:c|crew\/join|invite)\/([^/?#]+)/i);
          if (pathMatch?.[1]) {
            return {
              token: decodeURIComponent(pathMatch[1]).trim(),
              source: 'path',
              path,
              search: url.search || '',
            };
          }
          const queryToken = (
            search.get('token')
            || search.get('crew_token')
            || search.get('invite_token')
            || search.get('invite')
            || ''
          ).trim();
          if (queryToken) {
            return {
              token: decodeURIComponent(queryToken),
              source: 'query',
              path,
              search: url.search || '',
            };
          }
          return {
            token: null,
            source: 'none',
            path,
            search: url.search || '',
          };
        } catch (err) {
          return {
            token: null,
            source: 'parse_error',
            path: window.location.pathname || '',
            search: window.location.search || '',
            error: err?.message || String(err),
          };
        }
      };

      const readCrewTokenFromPath = () => {
        const parsed = extractCrewToken(window.location.href);
        console.log('[crew-token] parse', {
          source: parsed.source,
          parsed_token: parsed.token || null,
          path: parsed.path,
          search: parsed.search,
          error: parsed.error || null,
        });

        const token = parsed.token;
        if (token) {
          const suppressUntilRaw = safeStorage.get('crewJoinSuppressUntil');
          const suppressUntil = suppressUntilRaw ? Number(suppressUntilRaw) : 0;
          if (suppressUntil && Date.now() < suppressUntil) {
            console.log('[crew-token] suppressed', { token, source: parsed.source });
            setCrewJoinToken(null);
            return;
          }
          const justJoined = safeStorage.get('crewJoinJustJoined');
          if (justJoined && token.toLowerCase() === justJoined.toLowerCase()) {
            console.log('[crew-token] already_joined_suppress', { token, source: parsed.source });
            safeStorage.remove('crewJoinJustJoined');
            setCrewJoinToken(null);
            setSuppressCrewJoin(true);
            return;
          }
          setCrewJoinToken(token);
          return;
        }

        setCrewJoinToken(null);
      };

      const readCrewTokenFromNativeUrl = (event) => {
        const nativeUrl = event?.detail?.url;
        if (!nativeUrl) return;
        const parsed = extractCrewToken(nativeUrl);
        console.log('[crew-token] native_parse', {
          source: parsed.source,
          parsed_token: parsed.token || null,
          native_source: event?.detail?.source || null,
          path: parsed.path,
          search: parsed.search,
          error: parsed.error || null,
        });
        if (parsed.token) setCrewJoinToken(parsed.token);
      };

      // Run once on initial load
      readCrewTokenFromPath();

      // Re-run when browser navigation happens (back/forward)
      window.addEventListener('popstate', readCrewTokenFromPath);

      // Re-run when SPA navigation happens (pushState/replaceState)
      // Guard so we don't wrap multiple times on re-renders / hot reloads
      if (!window.__gweffitHistoryWrapped) {
        window.__gweffitHistoryWrapped = true;

        const wrapHistory = (type) => {
          const original = window.history[type];
          return function () {
            const ret = original.apply(this, arguments);
            window.dispatchEvent(new Event('locationchange'));
            return ret;
          };
        };

        window.history.pushState = wrapHistory('pushState');
        window.history.replaceState = wrapHistory('replaceState');
      }

      window.addEventListener('locationchange', readCrewTokenFromPath);
      window.addEventListener('gweffit:urlopen', readCrewTokenFromNativeUrl);

      return () => {
        window.removeEventListener('popstate', readCrewTokenFromPath);
        window.removeEventListener('locationchange', readCrewTokenFromPath);
        window.removeEventListener('gweffit:urlopen', readCrewTokenFromNativeUrl);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

// === Top-bar UI state used by header buttons ===
const [, setShowUpgrade] = useState(false);
const [, setShowBuyCoins] = useState(false);
const [showMore, setShowMore] = useState(false); // the "⋯ More" dropdown
const [showActions, setShowActions] = useState(false);

  // === Beta Settings panel state ===
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    jury_duty: true,
    verdicts: true,
    daily: true,
    streak: true,
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [streakFreezeCount, setStreakFreezeCount] = useState(0);
  const [homeSearchTerm, setHomeSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [homeFeedScope, setHomeFeedScope] = useState('all'); // 'all' | 'crews'
  const homeFeedScopeRef = useRef('all');
  const [homeCrewCount, setHomeCrewCount] = useState(0);
  const hasAutoPromotedCrewFeedRef = useRef(false);
  const [locallyHiddenTrialIds, setLocallyHiddenTrialIds] = useState(() => readLocallyHiddenTrialIds());
  const [featuredBankCards, setFeaturedBankCards] = useState([]);
  const [featuredBankLoading, setFeaturedBankLoading] = useState(false);
  const [canSeeQuarantinedTrials, setCanSeeQuarantinedTrials] = useState(false);
  const [atRiskCrews, setAtRiskCrews] = useState([]);

  const handleOpenSettingsPanel = () => {
    if (!BETA_MODE) return;
    setShowSettingsPanel(true);
  };

  const handleEnableNotificationsFromSettings = async () => {
    setShowSettingsPanel(false);
    await togglePushEnabled();
  };

  useEffect(() => {
    homeFeedScopeRef.current = homeFeedScope;
  }, [homeFeedScope]);

  const loadNotificationPrefs = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('user_notification_preferences')
      .select('jury_duty, verdicts, daily, notif_trial_summons, notif_trial_verdicts, notif_daily_decision, notif_streak')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) {
      console.warn('[prefs] load_failed', { user_id: user.id, error: error.message || String(error) });
      return;
    }
    if (data) {
      setNotificationPrefs({
        jury_duty: (data.notif_trial_summons ?? data.jury_duty) !== false,
        verdicts: (data.notif_trial_verdicts ?? data.verdicts) !== false,
        daily: (data.notif_daily_decision ?? data.daily) !== false,
        streak: data.notif_streak !== false,
      });
    }
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- WEB_PUSH_ENABLED handled inside

  const loadStreakFreezeCount = useCallback(async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('user_streak_freezes')
      .select('freeze_count')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') {
      console.warn('[streak-freeze] load_failed', { user_id: user.id, error: error.message || String(error) });
      return;
    }
    setStreakFreezeCount(Number(data?.freeze_count || 0));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadNotificationPrefs();
  }, [user?.id, loadNotificationPrefs]);

  useEffect(() => {
    if (!user?.id) return;
    loadStreakFreezeCount();
  }, [user?.id, loadStreakFreezeCount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getRuntimeConfig();
        if (!cancelled) setRuntimeDebugEnabled(Boolean(cfg?.debug));
      } catch {
        if (!cancelled) setRuntimeDebugEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } = {} } = await supabase.auth.getSession();
        const authUserId = session?.user?.id || null;
        if (!authUserId) {
          if (!cancelled) setCanSeeQuarantinedTrials(false);
          return;
        }
        const cfg = await getRuntimeConfig();
        const runtimeAdmins = new Set((cfg?.adminAuthUserIds || []).map((v) => String(v)));
        if (runtimeAdmins.has(String(authUserId))) {
          if (!cancelled) setCanSeeQuarantinedTrials(true);
          return;
        }
        const { data: adminRow } = await supabase
          .from('moderation_admins')
          .select('auth_user_id')
          .eq('auth_user_id', authUserId)
          .eq('is_active', true)
          .maybeSingle();
        if (!cancelled) setCanSeeQuarantinedTrials(Boolean(adminRow?.auth_user_id));
      } catch {
        if (!cancelled) setCanSeeQuarantinedTrials(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const refreshHiddenTrials = () => setLocallyHiddenTrialIds(readLocallyHiddenTrialIds());
    window.addEventListener('storage', refreshHiddenTrials);
    window.addEventListener('focus', refreshHiddenTrials);
    return () => {
      window.removeEventListener('storage', refreshHiddenTrials);
      window.removeEventListener('focus', refreshHiddenTrials);
    };
  }, []);

  const loadFeaturedBankCards = useCallback(async (reason = 'manual') => {
    if (!user?.id) {
      setFeaturedBankCards([]);
      return;
    }
    setFeaturedBankLoading(true);
    try {
      const picks = [];
      const seen = new Set();
      for (let i = 0; i < 3; i += 1) {
        const picker = () => supabase.rpc('pick_featured_trial');
        const { data, error } = (reason === 'mount')
          ? await singleFlightCached(`pick_featured_trial_boot:${i}`, 10_000, picker)
          : await singleFlight(`pick_featured_trial:${i}`, picker);
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        if (!row?.id || seen.has(row.id)) continue;
        seen.add(row.id);
        picks.push(row);
      }
      setFeaturedBankCards(picks);
    } catch (err) {
      console.warn('[featured] pick_featured_trial_failed', { error: err?.message || String(err) });
      setFeaturedBankCards([]);
    } finally {
      setFeaturedBankLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadFeaturedBankCards('mount');
  }, [loadFeaturedBankCards]);

  const refreshAtRiskCrews = useCallback(async () => {
    if (!user?.id) {
      setAtRiskCrews([]);
      return;
    }
    try {
      const { data } = await supabase.rpc('get_my_at_risk_crews');
      setAtRiskCrews(Array.isArray(data) ? data : []);
    } catch {
      setAtRiskCrews([]);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshAtRiskCrews();
  }, [refreshAtRiskCrews, showSettingsPanel]);

  const triggerWhatChangedScroll = useCallback((source = 'unknown') => {
    const node = document.getElementById('home-what-changed-anchor');
    if (!node) {
      console.log('[what_changed_menu_scroll_target_missing]', {
        source,
        view,
        target_id: 'home-what-changed-anchor',
      });
      return false;
    }
    console.log('[what_changed_menu_scroll_target_found]', {
      source,
      view,
      target_id: 'home-what-changed-anchor',
    });
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    console.log('[what_changed_menu_scroll_triggered]', {
      source,
      view,
      target_id: 'home-what-changed-anchor',
    });
    return true;
  }, [view]);

  useEffect(() => {
    if (!pendingWhatChangedScrollRef.current) return;
    if (view !== 'home') return;
    if (showActions || showMore) return;

    console.log('[what_changed_menu_effect_ready]', { view, showActions, showMore });

    if (triggerWhatChangedScroll('effect_ready')) {
      pendingWhatChangedScrollRef.current = false;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (triggerWhatChangedScroll('effect_retry')) {
        pendingWhatChangedScrollRef.current = false;
      }
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [showActions, showMore, triggerWhatChangedScroll, view]);

  useEffect(() => {
    const onCrewChallengeVoteSuccess = () => {
      refreshAtRiskCrews();
    };
    window.addEventListener('crew-challenge-vote-success', onCrewChallengeVoteSuccess);
    return () => window.removeEventListener('crew-challenge-vote-success', onCrewChallengeVoteSuccess);
  }, [refreshAtRiskCrews]);

  useEffect(() => {
    let alive = true;
    const term = String(homeSearchTerm || '').trim();
    if (!term) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const liveStatuses = ['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending'];
      const { data, error } = await supabase
        .from('trials')
        .select('*')
        .eq('is_blocked', false)
        .or('is_hidden.is.null,is_hidden.eq.false')
        .in('status', liveStatuses)
        .ilike('title', `%${term}%`)
        .order('created_at', { ascending: false })
        .limit(20);
      if (!alive) return;
      if (error) {
        console.warn('[search] trials_search_failed', { term, error: error.message || String(error) });
        setSearchResults([]);
      } else {
        const hiddenSet = readLocallyHiddenTrialIds();
        setSearchResults(
          (Array.isArray(data) ? data : [])
            .filter((row) => canSeeQuarantinedTrials || !row?.is_quarantined)
            .filter((row) => !hiddenSet.has(String(row?.id || '')))
        );
      }
      setSearchLoading(false);
    }, 300);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [homeSearchTerm, canSeeQuarantinedTrials]);

  const handleToggleNotificationPref = async (key, enabled) => {
    if (!user?.id) return;
    const next = { ...notificationPrefs, [key]: !!enabled };
    setNotificationPrefs(next);
    setPrefsSaving(true);
    const { error } = await supabase
      .from('user_notification_preferences')
      .upsert({
        user_id: user.id,
        jury_duty: next.jury_duty,
        verdicts: next.verdicts,
        daily: next.daily,
        notif_trial_summons: next.jury_duty,
        notif_trial_verdicts: next.verdicts,
        notif_daily_decision: next.daily,
        notif_streak: next.streak,
      }, { onConflict: 'user_id' });
    setPrefsSaving(false);
    if (error) {
      console.warn('[prefs] save_failed', { user_id: user.id, error: error.message || String(error) });
    }
  };

// === Dev seeding entry (wired from ⋯ More menu) ===
const openSeeder = () => {
  setShowMore(false);
  // If you have a seeder view, navigate or switch view here.
  // For now, take the user to "Create Trial" so it's useful.
  try { setView?.('create'); } catch {}
};

const openUnreadComebacks = useCallback(() => {
  console.log('[what_changed_menu_tap]', {
    current_view: view,
    show_actions: showActions,
    show_more: showMore,
  });
  setShowMore(false);
  setShowActions(false);
  pendingWhatChangedScrollRef.current = true;
  console.log('[what_changed_menu_navigation]', {
    target_view: 'home',
    from_view: view,
  });
  setView('home');
  requestAnimationFrame(() => {
    if (triggerWhatChangedScroll('menu_tap_immediate')) {
      pendingWhatChangedScrollRef.current = false;
      return;
    }
    window.setTimeout(() => {
      triggerWhatChangedScroll('menu_tap_retry');
      pendingWhatChangedScrollRef.current = false;
    }, 180);
  });
}, [showActions, showMore, triggerWhatChangedScroll, view]);

// === Toggle handler used by notification settings surfaces ===
const togglePushEnabled = async () => {
  // If enabled -> disable
  if (pushStatus === 'enabled') {
    await disablePush();
    return;
  }

  // If not enabled -> run the unified enable flow (Native vs Web handled inside)
  await enablePush();
};

// Detect push support/permission and keep status fresh on window focus
// === Push opt-in helpers and header toggle ===

// === Fix #6 (final): robust support/permission detection ===
useEffect(() => {
  if (typeof window === 'undefined') return;

  // ✅ Native app (TestFlight / Capacitor): use Capacitor permission status, not browser checks
  if (isNativeApp()) {
    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.checkPermissions();
        setPushStatus(perm.receive === 'granted' ? 'enabled' : 'idle');
      } catch (e) {
        console.warn('[push] Native permission check failed:', e);
        setPushStatus('idle');
      }
    })();
    return;
  }
  const isIOS =
    /iPhone|iPod|iPad.*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);

  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  const hasBasics =
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  if (!hasBasics || (isIOS && !isStandalone)) {
    setPushStatus('unsupported');
    return;
  }

  const refresh = () => {
    // If browser permission is denied → blocked
    if (Notification.permission === 'denied') {
      setPushStatus('blocked');
      return;
    }
    // If permission default → idle
    if (Notification.permission === 'default') {
      setPushStatus('idle');
      return;
    }
    // Permission granted → show enabled iff user opted-in previously
    const opted = readPushPref(); // 'true' → enabled, anything else → idle
    setPushStatus(opted ? 'enabled' : 'idle');
  };

  refresh();
  window.addEventListener('focus', refresh);
  return () => window.removeEventListener('focus', refresh);
}, []);


const [newTrialIds] = useState(new Set()); // placeholder so the prop still compiles

// Logout function
const handleLogout = async () => {
  if (!window.confirm('Are you sure you want to logout? You can always sign back in with the same email.')) {
    return;
  }

  try {
    // Sign out from Supabase
    await supabase.auth.signOut();

    // Clear local storage
    safeStorage.remove('gweffit_user_id');
    safeStorage.remove('gweffit_user_local');
    safeStorage.remove('gweffit_user_cache');
    safeStorage.remove('pushPromptShown');

    // Return to guest browsing instead of forcing auth immediately
    setUser(null);
    setAuthUser(null);
    setShowAuthModal(false);

  } catch (error) {
    console.error('Error logging out:', error);
    alert('Failed to logout. Please try again.');
  }
};
const openMemeCreator = (trial, verdictData) => {
  setVerdictForMeme({
    trial: {
      id: trial.id,
      title: trial.title,
      sideA: trial.sideA,
      sideB: trial.sideB,
      totalVotes: trial.totalVotes || trial.votes_count || 0,
      image: trial.image || trial.emoji
    },
    winner: verdictData.winner, // 'A' or 'B'
    winnerPct: verdictData.percentage || verdictData.winnerPct,
    punchline: verdictData.punchline || generatePunchline(
      trial,
      { 
        winner: verdictData.winner, 
        winnerPct: verdictData.percentage || verdictData.winnerPct 
      }
    )
  });
  setShowTemplateSelector(true);
};
// Handle push prompt enable button
const handleEnablePushPrompt = async () => {
  setShowPushPrompt(false);
  safeStorage.set('pushPromptShown', 'true');
  await enablePush();
};

// Handle push prompt dismiss
const handleDismissPushPrompt = () => {
  setShowPushPrompt(false);
  safeStorage.set('pushPromptShown', 'true');
};

  // Avatar catalog
  const AVATARS = {
    // FREE
    default: { id: 'default', name: 'Classic Juror', emoji: '👨‍⚖️', price: 0, tier: 'free' },

    // COMMON (500-1,000 coins)
    business: { id: 'business', name: 'Business Juror', emoji: '👔', price: 500, tier: 'common' },
    scholar: { id: 'scholar', name: 'Scholar Juror', emoji: '🎓', price: 500, tier: 'common' },
    cool: { id: 'cool', name: 'Cool Juror', emoji: '😎', price: 750, tier: 'common' },
    cowboy: { id: 'cowboy', name: 'Cowboy Juror', emoji: '🤠', price: 750, tier: 'common' },
    pirate: { id: 'pirate', name: 'Pirate Juror', emoji: '🏴‍☠️', price: 1000, tier: 'common' },

    // RARE (1,500-2,500 coins)
    royal: { id: 'royal', name: 'Royal Judge', emoji: '👑', price: 1500, tier: 'rare' },
    superhero: { id: 'superhero', name: 'Superhero Juror', emoji: '🦸', price: 1500, tier: 'rare' },
    wizard: { id: 'wizard', name: 'Wizard Juror', emoji: '🧙', price: 2000, tier: 'rare' },
    robot: { id: 'robot', name: 'Robot Juror', emoji: '🤖', price: 2000, tier: 'rare' },
    alien: { id: 'alien', name: 'Alien Juror', emoji: '👽', price: 2500, tier: 'rare' },

    // EPIC (3,000-5,000 coins)
    diamond: { id: 'diamond', name: 'Diamond Judge', emoji: '💎', price: 3000, tier: 'epic' },
    legendary: { id: 'legendary', name: 'Legendary Juror', emoji: '🌟', price: 3500, tier: 'epic' },
    champion: { id: 'champion', name: 'Champion Juror', emoji: '🏆', price: 4000, tier: 'epic' },
    lightning: { id: 'lightning', name: 'Lightning Juror', emoji: '⚡', price: 4500, tier: 'epic' },
    fire: { id: 'fire', name: 'Fire Juror', emoji: '🔥', price: 5000, tier: 'epic' },

    // LEGENDARY (7,500-10,000 coins)
    supreme: { id: 'supreme', name: 'Supreme Justice', emoji: '🦅', price: 7500, tier: 'legendary' },
    demon: { id: 'demon', name: 'Demon Judge', emoji: '👹', price: 8000, tier: 'legendary' },
    angel: { id: 'angel', name: 'Angel Juror', emoji: '😇', price: 8500, tier: 'legendary' },
    dragon: { id: 'dragon', name: 'Dragon Arbiter', emoji: '🐉', price: 9000, tier: 'legendary' },
    grim: { id: 'grim', name: 'Grim Verdict', emoji: '💀', price: 10000, tier: 'legendary' }
  };

const [finalizeErrorTrials, setFinalizeErrorTrials] = useState(() => new Set());
const [finalizeInProgressTrials, setFinalizeInProgressTrials] = useState(() => new Set());
const [realtimeResetNonce, setRealtimeResetNonce] = useState(0);
const [homePullDistance, setHomePullDistance] = useState(0);
const [homeRefreshing, setHomeRefreshing] = useState(false);
const [inboxSummons, setInboxSummons] = useState([]);
const [inboxLoading, setInboxLoading] = useState(false);
const [inboxError, setInboxError] = useState(null);
const inboxFetchTokenRef = useRef(0);
const INBOX_RETENTION_DAYS = 7;
const pruneInboxItems = useCallback((items) => {
  const cutoff = Date.now() - INBOX_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  return (items || []).filter((item) => {
    if (item.is_unread) return true;
    const ts = item.read_at || item.created_at;
    if (!ts) return true;
    const parsed = new Date(ts).getTime();
    if (!Number.isFinite(parsed)) return true;
    return parsed >= cutoff;
  });
}, []);
const homeWhatChangedFeedItems = useMemo(() => {
  if (!Array.isArray(inboxSummons) || inboxSummons.length === 0) return [];
  const freshnessCutoff = Date.now() - (72 * 60 * 60 * 1000);
  const rolePriority = { creator: 0, voter: 1 };
  const eventPriority = { sudden_death_resolved: 0, trial_ended: 1, trial_ended_voter: 2 };

  return inboxSummons
    .filter((item) => item.category === 'verdicts' && item.trial_id && item.is_unread)
    .map((item) => {
      const role = String(
        item.data?.role
        || (item.kind === 'inbox_item' && item.data?.type === 'trial_ended' ? 'creator' : '')
        || (item.kind === 'inbox_item' && item.data?.type === 'trial_ended_voter' ? 'voter' : '')
      ).toLowerCase();
      const eventType = String(item.data?.event_type || item.data?.type || '').toLowerCase();
      const resolvedAt = item.created_at ? new Date(item.created_at).getTime() : Number.NaN;
      const isCreator = role === 'creator';
      const isSuddenDeath = eventType === 'sudden_death_resolved';
      const specificTitle = String(
        item.trial_title
        || item.data?.trial_title
        || item.data?.title
        || ''
      ).trim();
      const winnerLabel = String(
        item.data?.winner_label
        || item.data?.winnerLabel
        || item.data?.winning_side_label
        || ''
      ).trim();
      const displayEyebrow = isSuddenDeath
        ? 'Overtime Verdict'
        : (isCreator ? 'Your Verdict Is In' : 'Verdict Landed');
      const displayBody = isSuddenDeath
        ? (winnerLabel
          ? `${winnerLabel} won in overtime.`
          : 'This one went to overtime. Open the result now.')
        : (isCreator
          ? (winnerLabel
            ? `${winnerLabel} won. Open your receipt.`
            : 'Your trial resolved. Open your receipt.')
          : (winnerLabel
            ? `${winnerLabel} won. See how your vote landed.`
            : 'You voted on this. See how it landed.'));
      const displayTitle = specificTitle || item.title || 'Verdict landed';
      return {
        ...item,
        title: displayTitle,
        verdictRole: isCreator ? 'creator' : 'voter',
        verdictEventType: eventType || 'trial_ended_voter',
        resolvedAt,
        displayEyebrow,
        displayTitle,
        displayBody,
      };
    })
    .filter((item) => Number.isFinite(item.resolvedAt) && item.resolvedAt >= freshnessCutoff)
    .sort((a, b) => {
      const eventDelta = (eventPriority[a.verdictEventType] ?? 9) - (eventPriority[b.verdictEventType] ?? 9);
      if (eventDelta !== 0) return eventDelta;
      const roleDelta = (rolePriority[a.verdictRole] ?? 9) - (rolePriority[b.verdictRole] ?? 9);
      if (roleDelta !== 0) return roleDelta;
      if (Boolean(a.is_unread) !== Boolean(b.is_unread)) return a.is_unread ? -1 : 1;
      return (b.resolvedAt || 0) - (a.resolvedAt || 0);
    });
}, [inboxSummons]);
const homeWhatChangedPreviewItems = useMemo(
  () => homeWhatChangedFeedItems.slice(0, 3),
  [homeWhatChangedFeedItems]
);
const unreadComebackCount = homeWhatChangedPreviewItems.length;
const unreadComebackBadge = unreadComebackCount > 9 ? '9+' : String(unreadComebackCount);
useEffect(() => {
  console.log('[what_changed_counts]', {
    menu_count: unreadComebackCount,
    total_matching_items: homeWhatChangedFeedItems.length,
    rendered_preview_count: homeWhatChangedPreviewItems.length,
  });
}, [homeWhatChangedFeedItems.length, homeWhatChangedPreviewItems.length, unreadComebackCount]);
const {
  trials,
  setTrials,
  trialsLoading,
  refetchTrials,
  invalidateTrialsFetch,
  stopTrialsLoading,
} = useHomeTrialsFeed({
  supabase,
  withTimeout,
  homeFeedScope,
  homeSearchTerm,
  user,
  canSeeQuarantinedTrials,
  locallyHiddenTrialIds,
  readLocallyHiddenTrialIds,
  setLocallyHiddenTrialIds,
  finalizeCompletedRef,
  setFinalizeErrorTrials,
});
const bootTimingRef = useRef({
  startedAt: Date.now(),
  firstHomePaintLogged: false,
  bootCompleteLogged: false,
});
const homePaintedRef = useRef(false);
const bootWatchdogStateRef = useRef({
  view: null,
  userLoading: true,
  trialsLoading: true,
  authUserId: null,
  appUserId: null,
});
const homeRefreshInFlightRef = useRef(false);
const homePullStartYRef = useRef(null);
const homePullActiveRef = useRef(false);
const HOME_PULL_TRIGGER_PX = 72;

useEffect(() => {
  if (process.env.NODE_ENV === 'production') return;
  if (bootTimingRef.current.firstHomePaintLogged) return;
  if (userLoading) return;
  if (view !== 'home') return;
  bootTimingRef.current.firstHomePaintLogged = true;
  console.log('[boot] first_home_paint', {
    elapsed_ms: Date.now() - bootTimingRef.current.startedAt,
  });
}, [userLoading, view]);

useEffect(() => {
  if (process.env.NODE_ENV === 'production') return;
  if (bootTimingRef.current.bootCompleteLogged) return;
  if (userLoading || trialsLoading) return;
  if (view !== 'home') return;
  bootTimingRef.current.bootCompleteLogged = true;
  console.log('[boot] complete', {
    elapsed_ms: Date.now() - bootTimingRef.current.startedAt,
  });
}, [userLoading, trialsLoading, view]);

useEffect(() => {
  if (!userLoading && view === 'home') {
    homePaintedRef.current = true;
  }
}, [userLoading, view]);

useEffect(() => {
  bootWatchdogStateRef.current = {
    view,
    userLoading,
    trialsLoading,
    authUserId: null,
    appUserId: user?.id || null,
  };
}, [view, userLoading, trialsLoading, user?.id]);
// 🆕 ADD THIS - Safety timeout to prevent infinite loading
useEffect(() => {
  const timeout = setTimeout(() => {
    const state = bootWatchdogStateRef.current;
    const loaderIsVisible = !!state?.userLoading;
    const homeHasPainted = !!homePaintedRef.current;

    if (!loaderIsVisible && homeHasPainted) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[boot] watchdog_skipped_home_visible', {
          ...state,
          homeHasPainted,
        });
      }
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('[boot] watchdog_fire_state', {
        ...state,
        homeHasPainted,
        loaderIsVisible,
      });
    }

    console.warn('⏰ Loading timeout - forcing load completion');
    setUserLoading(false);
    stopTrialsLoading();
  }, 5000); // Force load after 5 seconds max

  return () => clearTimeout(timeout);
}, [stopTrialsLoading]);
const [votingInProgress, setVotingInProgress] = useState(false);
const [showSuddenDeath, setShowSuddenDeath] = useState(false);
  const [suddenDeathTrial, setSuddenDeathTrial] = useState(null);
  const [showVerdict, setShowVerdict] = useState(false);
  const [verdictData, setVerdictData] = useState(null);
  const [shownVerdicts, setShownVerdicts] = useState(new Set());
  const shownVerdictsRef = useRef(shownVerdicts);
  const [verdictQueue, setVerdictQueue] = useState([]);
  const verdictQueueRef = useRef(new Set());
  const verdictProcessingRef = useRef(false);

  useEffect(() => {
    shownVerdictsRef.current = shownVerdicts;
  }, [shownVerdicts]);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const enqueueVerdict = useCallback((trialRow) => {
    if (!trialRow?.id) return;
    if (shownVerdictsRef.current.has(trialRow.id)) return;
    if (verdictQueueRef.current.has(trialRow.id)) return;
    verdictQueueRef.current.add(trialRow.id);
    setVerdictQueue((prev) => [...prev, trialRow]);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shouldAutoOpenVerdictModal = useCallback((trialRow) => {
    if (!trialRow?.id) return false;
    const pathname = window.location.pathname || '';
    const trialCreatorId = trialRow.created_by || trialRow.creator_id || null;
    const currentUserId = user?.id || null;
    const isCreatorVerdict = Boolean(
      currentUserId &&
      trialCreatorId &&
      String(currentUserId) === String(trialCreatorId)
    );
    const isAlreadyOnTrialDetail = pathname.includes(`/t/${trialRow.id}`);

    if (isCreatorVerdict) return false;
    if (isAlreadyOnTrialDetail) return false;
    return true;
  }, [user?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processVerdict = useCallback(async (t) => {
    const a = t.votes_a || 0;
    const b = t.votes_b || 0;
    const winner = a === b ? 'Tie' : a > b ? 'A' : 'B';

    let verdictType = 'VERDICT';
    const tv = Math.max(t.total_votes || a + b || 1, 1);
    const marginPct = Math.abs(a - b) / tv * 100;
    if (marginPct > 30) verdictType = 'CRUSHING VICTORY';
    else if (marginPct > 15) verdictType = 'CLEAR WIN';
    else verdictType = 'CONTROVERSIAL SPLIT';

    const aiVerdict = await fetchAIVerdict(t.id);

    let punchline, winnerPct, aiImage;
    if (aiVerdict) {
      punchline =
        aiVerdict.roast_punchline ??
        aiVerdict.roast_punchlines?.[0] ??
        aiVerdict.internet_punchline ??
        aiVerdict.internet_punchlines?.[0] ??
        aiVerdict.sports_punchline ??
        aiVerdict.sports_punchlines?.[0] ??
        'The jury has spoken.';
      winnerPct = Number(aiVerdict.winner_pct) || undefined;
      aiImage = getPersistedAiImage(t, aiVerdict);
    } else {
      const fb = getVerdictCopy(
        {
          id: t.id,
          title: t.title,
          sideA: t.side_a,
          sideB: t.side_b,
          votesA: a,
          votesB: b,
          totalVotes: tv,
          category: t.category,
          trialMode: t.trial_mode ?? 'debate',
          decisionCategory: t.decision_category ?? null,
        },
        winner
      );
      punchline = fb.punchline;
      winnerPct = fb.winnerPct;
      aiImage = getPersistedAiImage(t, null);
    }

    setVerdictData({
      trial: {
        id: t.id,
        creator_id: t.creator_id,
        created_by: t.created_by,
        type: t.trial_type,
        title: t.title,
        sideA: t.side_a,
        sideB: t.side_b,
        trialMode: t.trial_mode || 'debate',
        decisionCategory: t.decision_category || null,
        votesA: a,
        votesB: b,
        totalVotes: tv,
        status: t.status,
        createdAt: t.created_at ? new Date(t.created_at).getTime() : Date.now(),
        closedAt: t.closed_at ? new Date(t.closed_at).getTime() : Date.now(),
        image: t.image_emoji,
        hasVoted: true,
      },
      winner,
      verdictType,
      punchline,
      verdictNumber: Math.floor(Math.random() * 90000) + 10000,
      winnerPct,
      aiImage,
      aiPunchlines: aiVerdict
        ? {
            roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
            sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
            internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline],
          }
        : null,
    });

    // ✅ FIX: Save verdict to database AND local state
    const verdictNumber = Math.floor(Math.random() * 90000) + 10000;
    const verdictDataToStore = {
      ...(t.verdict_data || {}),   // ✅ preserve any previously stored fields
      winner,
      verdictType,
      punchline,
      verdictNumber,
      winnerPct,
      ...(aiImage ? { aiImage } : {}),                     // ✅ make sure aiImage is already “persisted” (see below)
      aiPunchlines: aiVerdict ? {
        roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
        sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
        internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline]
      } : (t.verdict_data?.aiPunchlines ?? null) // ✅ keep old punchlines if no AI verdict this time
    };

    const persistedVerdictData = await persistTrialVerdictData(t.id, verdictDataToStore);

    // Update local trials state with verdict_data
    setTrials(prev => prev.map(x =>
      x.id === t.id ? { ...x, verdict_data: persistedVerdictData } : x
    ));

    setShowSuddenDeath(false);
    setShowVerdict(true);
    setShownVerdicts((prev) => {
      const next = new Set(prev);
      next.add(t.id);
      return next;
    });
  }, [fetchAIVerdict, getVerdictCopy, persistTrialVerdictData]); // eslint-disable-line react-hooks/exhaustive-deps -- setTrials stable

  useEffect(() => {
    if (verdictProcessingRef.current) return;
    if (verdictQueue.length === 0) return;

    verdictProcessingRef.current = true;
    const nextTrial = verdictQueue[0];

    (async () => {
      try {
        if (!shownVerdictsRef.current.has(nextTrial.id)) {
          await processVerdict(nextTrial);
        }
      } catch (err) {
        console.error('❌ Verdict processing failed:', err);
      } finally {
        setVerdictQueue((prev) => prev.slice(1));
        verdictQueueRef.current.delete(nextTrial.id);
        verdictProcessingRef.current = false;
      }
    })();
  }, [verdictQueue, processVerdict]);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showBuyCoinsModal, setShowBuyCoinsModal] = useState(false);
  const [showAvatarShop, setShowAvatarShop] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  // Auto-open leaderboard if a redirect set the flag
useEffect(() => {
  if (safeStorage.get("openLeaderboardOnce") === "1") {
    safeStorage.remove("openLeaderboardOnce");
    setShowLeaderboard(true);
  }
}, []);
  const [showCreatorAnalytics, setShowCreatorAnalytics] = useState(false);
  const [referralStats, setReferralStats] = useState({
    totalInvites: 0,
    joinedCount: 0,
    firstVoteConversions: 0,
    totalReferralRewardsEarned: 0,
  });
  const [referralStatsLoading, setReferralStatsLoading] = useState(false);
  const [runtimeDebugEnabled, setRuntimeDebugEnabled] = useState(false);
  const [leaderboardTab, setLeaderboardTab] = useState('xp'); // 'xp', 'streak', 'coins'
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [userRank, setUserRank] = useState(null);
  const getLeaderboardMetricValue = useCallback((entry, category = leaderboardTab) => {
    if (!entry) return 0;
    if (category === 'streak') return Number(entry.streak || 0);
    if (category === 'coins') return Number(entry.coins || 0);
    return Number(entry.points || 0);
  }, [leaderboardTab]);
  const getLeaderboardMetricLabel = useCallback((entry, category = leaderboardTab) => {
    const value = getLeaderboardMetricValue(entry, category);
    if (category === 'streak') return `${value} day streak`;
    if (category === 'coins') return `${value} coins`;
    return `${value} XP`;
  }, [getLeaderboardMetricValue, leaderboardTab]);
  const dedupedLeaderboardData = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const entry of Array.isArray(leaderboardData) ? leaderboardData : []) {
      const stableId = entry?.id ? String(entry.id) : null;
      if (!stableId || seen.has(stableId)) continue;
      seen.add(stableId);
      rows.push(entry);
    }
    return rows;
  }, [leaderboardData]);
  const leaderboardRows = useMemo(() => {
    const currentUserId = userRank && user?.id ? String(user.id) : null;
    if (!currentUserId) return dedupedLeaderboardData;
    return dedupedLeaderboardData.filter((entry) => String(entry?.id || '') !== currentUserId);
  }, [dedupedLeaderboardData, user?.id, userRank]);
  useEffect(() => {
    if (!showLeaderboard) return;
    console.log('[leaderboard_modal_opened]', {
      source: 'in_app',
      tab: leaderboardTab,
      user_id: user?.id || null,
      fetched_rows: Array.isArray(leaderboardData) ? leaderboardData.length : 0,
      deduped_rows: dedupedLeaderboardData.length,
    });
    try {
      window.trackEvent?.('leaderboard_modal_opened', {
        source: 'in_app',
        tab: leaderboardTab,
      });
    } catch {}
  }, [showLeaderboard, leaderboardTab, user?.id, leaderboardData, dedupedLeaderboardData.length]);
  const [recentVotes, setRecentVotes] = useState({}); // Track votes per trial in last minute
  const recentVotesRef = useRef(recentVotes);
  const recentSelfVoteRef = useRef(new Map());
  const homeFeedResyncTimerRef = useRef(null);
  const [animatingVotes, setAnimatingVotes] = useState(new Set()); // Trials with active animations

  useEffect(() => {
    recentVotesRef.current = recentVotes;
  }, [recentVotes]);

  useEffect(() => () => {
    clearTimeout(homeFeedResyncTimerRef.current);
  }, []);

  const scheduleHomeFeedResync = useCallback((reason = 'unknown') => {
    clearTimeout(homeFeedResyncTimerRef.current);
    homeFeedResyncTimerRef.current = setTimeout(() => {
      refetchTrials().catch((err) => {
        console.warn('[home_feed_resync_failed]', {
          reason,
          error: err?.message || String(err),
        });
      });
    }, 350);
  }, [refetchTrials]);
  // Auth state
  const [authUser, setAuthUser] = useState(null); // Supabase auth user
  const [showAuthModal, setShowAuthModal] = useState(false); // Login/signup modal
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [authContext, setAuthContext] = useState('general');
  useEffect(() => {
    let cancelled = false;

    async function loadHomeCrewCount() {
      if (!user?.id && !authUser?.id) {
        if (!cancelled) setHomeCrewCount(0);
        return;
      }

      try {
        const [createdResult, memberResult] = await Promise.all([
          user?.id
            ? supabase
                .from('crews')
                .select('id')
                .eq('created_by', user.id)
            : Promise.resolve({ data: [], error: null }),
          authUser?.id || user?.id
            ? supabase
                .from('crew_members')
                .select('crew_id')
                .in('user_id', [...new Set([authUser?.id, user?.id].filter(Boolean))])
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (createdResult?.error) throw createdResult.error;
        if (memberResult?.error) throw memberResult.error;

        const uniqueCrewIds = new Set([
          ...(createdResult?.data || []).map((row) => row?.id).filter(Boolean),
          ...(memberResult?.data || []).map((row) => row?.crew_id).filter(Boolean),
        ]);

        if (!cancelled) setHomeCrewCount(uniqueCrewIds.size);
      } catch (err) {
        console.warn('⚠️ Failed to load home crew count:', err?.message || err);
        if (!cancelled) setHomeCrewCount(0);
      }
    }

    loadHomeCrewCount();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authUser?.id]);

  useEffect(() => {
    if (view !== 'home') return;
    try {
      const raw = localStorage.getItem(STARTER_TRIAL_RETURN_SCOPE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.scope === 'all') {
        hasAutoPromotedCrewFeedRef.current = true;
        setHomeFeedScope('all');
        localStorage.removeItem(STARTER_TRIAL_RETURN_SCOPE_KEY);
        setTimeout(() => {
          refetchTrials().catch((err) => {
            console.warn('[starter_trial_return_scope_refetch_failed]', { error: err?.message || String(err) });
          });
        }, 0);
      }
    } catch {}
  }, [view, refetchTrials]);

  useEffect(() => {
    if (hasAutoPromotedCrewFeedRef.current) return;
    if (view !== 'home') return;
    if (homeSearchTerm.trim()) return;
    if (!authUser?.id) return;
    if (Math.max(Number(homeCrewCount || 0), Array.isArray(atRiskCrews) ? atRiskCrews.length : 0) <= 0) return;
    if (homeFeedScope !== 'all') return;

    hasAutoPromotedCrewFeedRef.current = true;
    setHomeFeedScope('crews');
  }, [view, homeSearchTerm, authUser?.id, homeCrewCount, atRiskCrews, homeFeedScope]);

  // Trial seeder state
  const [showGrandSeeder, setShowGrandSeeder] = useState(false);
  const [showQuickSeeder, setShowQuickSeeder] = useState(false);
 // First vote onboarding state
const [showFirstVoteModal, setShowFirstVoteModal] = useState(false);
const [showFirstVoteNextStepModal, setShowFirstVoteNextStepModal] = useState(false);
const processedReferralSignatureRef = useRef(null);
const [hasCompletedFirstVote, setHasCompletedFirstVote] = useState(() => {
  // Read any existing completion flag from localStorage
  if (typeof window === 'undefined') return null;
  const localStatus = safeStorage.get('gweffit_first_vote_completed');
  // true  => completed
  // null/anything else => haven’t completed (or we’ll confirm via DB)
  return localStatus === 'true';
});
  const [firstVoteLoading, setFirstVoteLoading] = useState(true);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [appNotice, setAppNotice] = useState(null);
  const [isGeneratingMeme, setIsGeneratingMeme] = useState(false);
const [flippedVerdicts, setFlippedVerdicts] = useState(new Set());
const verdictRef = useRef(null);

const [showBonusCoinsIndicator, setShowBonusCoinsIndicator] = useState(0);
const profileVersionLongPressRef = useRef(null);

useEffect(() => {
  if (!appNotice?.message) return undefined;
  const timeout = window.setTimeout(() => setAppNotice(null), 2600);
  return () => window.clearTimeout(timeout);
}, [appNotice]);

const showAppNotice = useCallback((message, tone = 'success') => {
  setAppNotice({ message, tone });
}, []);

// Load user from auth
const maybeConsumeStreakFreezeForBrokenStreak = async (authUserId, currentStreak) => {
  if (!authUserId || !currentStreak || currentStreak <= 0) {
    return { consumed: false, freeze_count: null };
  }
  try {
    const { data, error } = await supabase.rpc('consume_streak_freeze');
    if (error) {
      console.warn('[streak-freeze] consume_failed', { auth_user_id: authUserId, error: error.message || String(error) });
      return { consumed: false, freeze_count: null };
    }
    if (data?.ok && data?.consumed) {
      console.log('🧊 Streak freeze consumed - streak preserved');
      if (Number.isFinite(Number(data?.freeze_count))) {
        setStreakFreezeCount(Number(data.freeze_count));
      }
      return { consumed: true, freeze_count: Number(data?.freeze_count ?? 0) };
    }
    if (Number.isFinite(Number(data?.freeze_count))) {
      setStreakFreezeCount(Number(data.freeze_count));
    }
    return { consumed: false, freeze_count: Number(data?.freeze_count ?? 0) };
  } catch (e) {
    console.warn('[streak-freeze] consume_failed', { auth_user_id: authUserId, error: e?.message || String(e) });
    return { consumed: false, freeze_count: null };
  }
};

const loadUserFromAuth = async (authUserData) => {
  try {
    console.log('🔍 [loadUserFromAuth] START for:', authUserData.id);
    
    // ========== STEP 1: Try Cache First ==========
    const cachedUser = safeStorage.get('gweffit_user_cache');
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser);
        
        // Verify it's the right user
        if (parsed.auth_user_id === authUserData.id) {
          console.log('✅ [loadUserFromAuth] Using cached user data');

          // 🔥 Check if streak was broken (based on last_streak_date, not last_login_date)
          // Streak only increments on actual activity (vote or create trial), not on app open
          const lastStreakDate = parsed.last_streak_date;
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          let currentStreak = parsed.streak || 0;

          if (lastStreakDate) {
            const lastDate = new Date(lastStreakDate);
            lastDate.setHours(0, 0, 0, 0);
            const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));

            // If more than 1 day since last activity, streak is broken - reset to 0
            if (daysSince > 1) {
              const freeze = await maybeConsumeStreakFreezeForBrokenStreak(authUserData.id, currentStreak);
              if (freeze?.consumed) {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = yesterday.toISOString().split('T')[0];
                parsed.last_streak_date = yesterdayStr;
                safeStorage.set('gweffit_user_cache', JSON.stringify(parsed));
                console.log('🧊 Streak freeze saved your streak');
              } else {
                currentStreak = 0;
                console.log('🔥 Streak broken - resetting to 0');
              }
            }
            // Otherwise keep current streak (activity will increment it)
          }
          // If no last_streak_date, keep streak as 0 until they do activity

          // Get localStorage data for local-only user customizations
          const localData = safeStorage.get('gweffit_user_local');
          const local = localData ? JSON.parse(localData) : {};
          
          // Set user state immediately from cache
          setUser({
            id: parsed.id,
            username: parsed.username || authUserData.email?.split('@')[0] || 'User',
            email: authUserData.email,
            points: parsed.points || 0,
            coins: parsed.coins || 1000,
            streak: currentStreak,
            selectedAvatar: local.selectedAvatar || parsed.selected_avatar || 'default',
            fcmToken: parsed.fcm_token,
            trialsJoined: local.trialsJoined || 0,
            ownedAvatars: local.ownedAvatars || ['default'],
          });
          
          console.log('✅ [loadUserFromAuth] User state set from cache');
          
          // ========== Background Refresh (don't wait) ==========
          console.log('🔄 [loadUserFromAuth] Starting background database refresh...');
          supabase
            .from('users')
            .select('*')
            .eq('auth_user_id', authUserData.id)
            .then(({ data, error }) => {
              if (error) {
                console.warn('⚠️ Background refresh failed:', error);
                return;
              }
              
              const freshUser = data?.[0];
              if (freshUser) {
                console.log('✅ Background refresh complete, updating cache');
                
                // Update cache with fresh data
                safeStorage.set('gweffit_user_cache', JSON.stringify(freshUser));
                
                // Update user state if coins/points changed significantly
                const coinsDiff = Math.abs(freshUser.coins - parsed.coins);
                const pointsDiff = Math.abs(freshUser.points - parsed.points);
                
                if (coinsDiff > 0 || pointsDiff > 0) {
                  console.log('🔄 Updating user state with fresh data from background sync');
                  setUser(prev => ({
                    ...prev,
                    coins: freshUser.coins || prev.coins,
                    points: freshUser.points || prev.points,
                    streak: freshUser.streak || prev.streak
                  }));
                }
                
                // Update last_login_date in background (but NOT streak - that only updates on activity)
                supabase
                  .from('users')
                  .update({
                    last_login_date: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', freshUser.id)
                  .then(() => console.log('✅ last_login_date updated'))
                  .catch(err => console.warn('⚠️ Failed to update last_login_date:', err));
              }
            })
            .catch(err => {
              console.warn('⚠️ Background refresh error:', err);
            });
          
          // Return early - user is loaded from cache!
          return;
} else {
  reliabilityCountersRef.current.identity_mismatch += 1;
  console.warn('[identity] cache_auth_mismatch', {
    auth_user_id: authUserData.id,
    cached_auth_user_id: parsed.auth_user_id || null,
    mismatch_count: reliabilityCountersRef.current.identity_mismatch,
  });
  safeStorage.remove('gweffit_user_cache');
  safeStorage.remove('gweffit_user_local');
}
} catch (e) {
  console.warn('⚠️ Failed to parse cached user, clearing cache:', e);
  safeStorage.remove('gweffit_user_cache');
  safeStorage.remove('gweffit_user_local');
}
    }
    
    // ========== STEP 2: No Cache - Full Database Query ==========
    console.log('🔍 [loadUserFromAuth] No cache available, querying database...');
    
    const { data: users, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('auth_user_id', authUserData.id);

    console.log('🔍 [loadUserFromAuth] Query complete:', { users, fetchError });

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching user:', fetchError);
      throw fetchError;
    }

    const existingUser = users?.[0];

    if (existingUser) {
      console.log('🔍 [loadUserFromAuth] User found, processing...');
      
      // Cache the user data for next time
      safeStorage.set('gweffit_user_cache', JSON.stringify(existingUser));
      console.log('✅ User data cached');
      
      const localData = safeStorage.get('gweffit_user_local');
      const local = localData ? JSON.parse(localData) : {};

      // 🔥 Check if streak was broken (based on last_streak_date, not last_login_date)
      // Streak only increments on actual activity (vote or create trial), not on app open
      const lastStreakDate = existingUser.last_streak_date;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let currentStreak = existingUser.streak || 0;

      if (lastStreakDate) {
        const lastDate = new Date(lastStreakDate);
        lastDate.setHours(0, 0, 0, 0);
        const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));

        // If more than 1 day since last activity, streak is broken - reset to 0
        if (daysSince > 1) {
          const freeze = await maybeConsumeStreakFreezeForBrokenStreak(authUserData.id, currentStreak);
          if (freeze?.consumed) {
            console.log('🧊 Streak freeze saved your streak');
          } else {
            currentStreak = 0;
            console.log('🔥 Streak broken - resetting to 0');
          }
        }
        // Otherwise keep current streak (activity will increment it)
      }
      // If no last_streak_date, keep streak as 0 until they do activity

      setUser({
        id: existingUser.id,
        username: existingUser.username || authUserData.email?.split('@')[0] || 'User',
        email: authUserData.email,
        points: existingUser.points || 0,
        coins: existingUser.coins || 1000,
        streak: currentStreak,
        selectedAvatar: local.selectedAvatar || existingUser.selected_avatar || 'default',
        fcmToken: existingUser.fcm_token,
        trialsJoined: local.trialsJoined || 0,
        ownedAvatars: local.ownedAvatars || ['default'],
      });

      console.log('✅ [loadUserFromAuth] User state set successfully');

      // Update last_login_date only (NOT streak - that only updates on activity)
      await supabase
        .from('users')
        .update({
          last_login_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', existingUser.id);

      console.log('✅ [loadUserFromAuth] Database updated');

    } else {
      // ========== STEP 3: Create New User ==========
      console.log('🔍 [loadUserFromAuth] No user found, creating new user...');
      
      const newUsername = generateRandomUsername();

      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert([{
          auth_user_id: authUserData.id,
          username: newUsername,
          points: 0,
          coins: 1000,
          streak: 0, // Start at 0 - first activity (vote or create) will set to 1
          is_anonymous: false,
          last_login_date: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) {
        console.error('Error creating user:', insertError);
        throw insertError;
      }

      console.log('✅ [loadUserFromAuth] New user created');
      
      // Cache the new user
      safeStorage.set('gweffit_user_cache', JSON.stringify(newUser));
// 📊 Track signup
      try {
        trackEvent('signup_completed', { user_id: newUser.id });
      } catch {}
      setUser({
        id: newUser.id,
        username: newUser.username,
        email: authUserData.email,
        points: 0,
        coins: 1000,
        streak: 0, // Start at 0 - first activity (vote or create) will set to 1
        selectedAvatar: 'default',
        fcmToken: null,
        trialsJoined: 0,
        ownedAvatars: ['default'],
      });

      console.log('✅ [loadUserFromAuth] New user state set');
    }

  } catch (error) {
    console.error('❌ Error in loadUserFromAuth:', error);
    throw error; // Re-throw so auth listener's try-catch can handle it
  }
};

const { guestClaimPending, attemptGuestActivityClaim } = useAppBootstrap({
  user,
  authUser,
  navigate,
  withTimeout,
  loadUserFromAuth,
  logHealthSnapshot,
  trackEvent,
  setUser,
  setUserLoading,
  setAuthUser,
  setShowAuthModal,
  userIdRef,
  authUserIdRef,
  isLoadingUserRef,
  lastBootstrappedAuthUserRef,
});

const getLocalTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// ✅ Stable device id for multi-device push
const getNativeDeviceId = async () => {
  try {
    const { Device } = await import('@capacitor/device');
    const id = await Device.getId();
    return id?.identifier || null;
  } catch (e) {
    console.warn('[push] ⚠️ getNativeDeviceId failed:', e);
    return null;
  }
};

// === Native Push (iOS/Android) helper ===
const ensureNativePushWired = async () => {
  if (nativePushWiredRef.current) return;
  nativePushWiredRef.current = true;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  await registerOnce('cap_push_notifications_listeners', async () => {
    // ✅ Listen for APNs registration (we'll use this to trigger FCM token fetch)
    const h1 = await PushNotifications.addListener('registration', async (apnsToken) => {
      const apnsTokenStr = apnsToken?.value || apnsToken;
      console.log('[push] 📱 APNs token received:', typeof apnsTokenStr === 'string' ? apnsTokenStr.slice(0, 20) + '...' : apnsTokenStr);

      // Now fetch the FCM token using @capacitor-firebase/messaging
      try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const fcmResult = await FirebaseMessaging.getToken();
        const fcmToken = fcmResult?.token;

        if (fcmToken) {
          console.log('[push] 🔥 FCM token obtained:', fcmToken.slice(0, 30) + '...');
          console.log('[push] 📊 FCM token length:', fcmToken.length);

          // Store locally
          try {
            safeStorage.set('gweffit_last_fcm_token', fcmToken);
          } catch {}

          const uid = userIdRef.current;
          if (!uid) {
            console.warn('[push] ⚠️ No user id yet; saved FCM token locally.');
            return;
          }

          // Save the FCM token (not the APNs token!)
          await persistPushEnabled({
            userId: uid,
            token: fcmToken,
            platform: Capacitor.getPlatform(),
          });
          console.log('[push] ✅ FCM token saved to database successfully');
        } else {
          console.error('[push] ❌ FirebaseMessaging.getToken() returned empty');
        }
      } catch (e) {
        console.error('[push] ❌ Failed to get FCM token:', e);
        // Fallback: try to save APNs token (won't work with FCM backend but better than nothing)
        console.log('[push] ⚠️ Falling back to APNs token');
      }
    });

    const h2 = await PushNotifications.addListener('registrationError', (err) => {
      console.error('[push] ❌ Registration error:', err);
    });

    const h3 = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[push] 📩 Received (foreground):', notification);
    });

    const h4 = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[push] 👆 Action performed:', action);
      try {
        const data = action?.notification?.data || {};
        const targetUrl =
          data?.click_action ||
          data?.url ||
          (data?.trial_id ? `gweffit://t/${data.trial_id}` : null);

        if (targetUrl) {
          window.dispatchEvent(new CustomEvent('gweffit:urlopen', {
            detail: { url: targetUrl, source: 'pushNotificationActionPerformed' },
          }));
        }
      } catch (err) {
        console.warn('[push] Failed to route notification tap:', err);
      }
    });

    return () => {
      try { h1?.remove?.(); } catch {}
      try { h2?.remove?.(); } catch {}
      try { h3?.remove?.(); } catch {}
      try { h4?.remove?.(); } catch {}
    };
  });
};

const persistPushEnabled = async ({ userId, token, platform }) => {
  if (!token) return;
  const requestId = nextRequestId('pushreg');
  const coarseKey = `${userId || 'unknown'}:${platform || 'unknown'}:${String(token).slice(0, 24)}`;
  if (pushTokenRegisterInFlightRef.current.has(coarseKey)) {
    console.log('[push] token_register_skipped_duplicate', {
      request_id: requestId,
      reason: 'in_flight',
      key: coarseKey,
    });
    return;
  }
  pushTokenRegisterInFlightRef.current.add(coarseKey);
  console.log('[push] token_register_start', {
    request_id: requestId,
    hinted_user_id: userId || null,
    platform: platform || null,
  });
  try {
    const identity = await resolveCanonicalUserIds({
      authUserId: authUserIdRef.current || null,
      hintAppUserId: userId || null,
      reason: 'persist_push_enabled',
      requestId,
    });
    if (!identity.appUserId) {
      console.warn('[identity] token_register_no_app_user', {
        request_id: requestId,
        auth_user_id: identity.authUserId,
        hinted_user_id: userId || null,
      });
      return;
    }

    const deviceId = await getNativeDeviceId();
    const tupleKey = `${identity.appUserId}:${deviceId || 'nodevice'}:${token}`;
    const lastRegisteredAt = pushTokenRegisterCooldownRef.current.get(tupleKey) || 0;
    if (Date.now() - lastRegisteredAt < 60_000) {
      console.log('[push] token_register_skipped_duplicate', {
        request_id: requestId,
        reason: 'cooldown',
        app_user_id: identity.appUserId,
        device_id: deviceId || null,
      });
      return;
    }

    const payload = {
      p_user_id: identity.appUserId,
      p_device_id: deviceId || null,
      p_fcm_token: token,
      p_platform: platform || Capacitor.getPlatform(),
      p_timezone: getLocalTimezone(),
      p_now: new Date().toISOString(),
    };

    const isTransient = (err) => {
      const status = Number(err?.code || err?.status || err?.context?.status || 0);
      if (status >= 500) return true;
      const msg = String(err?.message || '').toLowerCase();
      return msg.includes('network') || msg.includes('timeout') || msg.includes('failed to fetch');
    };

    let rpcData = null;
    let rpcErr = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { data, error } = await supabase.rpc('register_user_fcm_token', payload);
      rpcData = data || null;
      rpcErr = error || null;
      if (!rpcErr) break;
      if (!isTransient(rpcErr) || attempt === 1) break;
      const backoffMs = 300 + Math.floor(Math.random() * 450);
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
    }

    if (rpcErr) {
      console.error('[push] token_register_error', {
        request_id: requestId,
        app_user_id: identity.appUserId,
        device_id: deviceId || null,
        code: rpcErr.code || null,
        message: rpcErr.message || String(rpcErr),
      });
      throw rpcErr;
    }

    pushTokenRegisterCooldownRef.current.set(tupleKey, Date.now());
    const conflictResolved = Boolean(rpcData?.conflict_resolved);
    if (conflictResolved) {
      reliabilityCountersRef.current.token_conflict_resolved += 1;
    }

    console.log('[push] token_register_success', {
      request_id: requestId,
      app_user_id: identity.appUserId,
      device_id: deviceId || null,
      conflict_resolved: conflictResolved,
      conflict_resolved_count: reliabilityCountersRef.current.token_conflict_resolved,
    });
  } finally {
    pushTokenRegisterInFlightRef.current.delete(coarseKey);
  }
};
// ✅ Launch decision: NO web push at launch (native apps only)
const WEB_PUSH_ENABLED = false;
// === One-time enable flow ===
const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_FIREBASE_VAPID_KEY ||
  'BFL5HxqRitHO7a0A4qMJoqscWf-VS7_QYp606z_lc6RDuGMWzVcRI2wbXBoNnaypdNqfYZMVB1OvksK8N_nrQu8';

const enablePush = async () => {
  try {
    if (!user?.id) {
      alert('You need to be signed in to enable notifications.');
      return false;
    }

    // ✅ Native app path (TestFlight / Capacitor)
    // iOS WKWebView does NOT support the browser Notification API.
    if (isNativeApp()) {
      console.log('[push] 📱 Native platform detected');
      
      // Wire up the listeners first (does nothing if already wired)
      await ensureNativePushWired();

      const { PushNotifications } = await import('@capacitor/push-notifications');
      
      // Check current permission status
      let perm = await PushNotifications.checkPermissions();
      console.log('[push] Current permissions:', perm);
      
      // Request if not granted
      if (perm.receive !== 'granted') {
        console.log('[push] Requesting permissions...');
        perm = await PushNotifications.requestPermissions();
      }
      
      if (perm.receive !== 'granted') {
        console.log('[push] ❌ Permission denied:', perm.receive);
        setPushStatus('blocked');
        alert('Notifications were not enabled. Go to Settings → Notifications → GweffIT to enable them.');
        return false;
      }

      const now = Date.now();
      try {
        const last = Number(safeStorage.get('gweffit_native_register_last_at') || 0);
        if (last && now - last < 15_000) {
          console.log('[push] register_skipped', { reason: 'session_cooldown' });
          return true;
        }
      } catch {}
      if (nativeRegisterAttemptRef.current.inFlight) {
        console.log('[push] register_skipped', { reason: 'in_flight' });
        return true;
      }
      if (now - nativeRegisterAttemptRef.current.lastAttemptAt < 15_000) {
        console.log('[push] register_skipped', { reason: 'cooldown' });
        return true;
      }

      nativeRegisterAttemptRef.current.inFlight = true;
      nativeRegisterAttemptRef.current.lastAttemptAt = now;
      try {
        safeStorage.set('gweffit_native_register_last_at', String(now));
      } catch {}
      try {
        await PushNotifications.register();
      } finally {
        nativeRegisterAttemptRef.current.inFlight = false;
      }

      writePushPref(true);
      setPushStatus('enabled');
      console.log('[push] ✅ Native push enabled');
      return true;
    }

    // 🌐 Web browser path (disabled for launch)
    alert('Push notifications are only available in the iOS/Android app right now.');
    return false;

  } catch (e) {
    console.error('❌ enablePush failed:', e);
    alert('Could not enable notifications.');
    return false;
  }
};

const disablePush = async () => {
  try {
    if (!user?.id) return;

    // Best-effort: deactivate this device only (multi-device safe)
try {
  const deviceId = await getNativeDeviceId();
  if (deviceId) {
    await supabase
      .from('user_fcm_tokens')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('device_id', deviceId);
  }
} catch {}
// ✅ Optional: recompute push_enabled based on whether any devices remain active
try {
  const { data: rows, error } = await supabase
    .from('user_fcm_tokens')
    .select('device_id')
    .eq('user_id', user.id)
    .eq('is_active', true);

  if (!error) {
    await supabase
      .from('users')
      .update({ push_enabled: (rows?.length || 0) > 0 })
      .eq('id', user.id);
  }
} catch {}
    writePushPref(false);
    setPushStatus('idle');

    // Web: delete browser token
    if (!isNativeApp()) {
      const messaging = await messagingPromise;
      if (messaging) {
        try {
          await deleteToken(messaging);
        } catch {}
      }
    }

    // Native: For Capacitor apps, the system handles token cleanup
    // No additional action needed
  } catch (e) {
    console.error('disablePush failed:', e?.message || e);
  }
};

// 🧪 Test FCM directly - bypasses all queue logic
// 🧪 Test push to THIS DEVICE token (multi-device safe)
const testFCM = async () => {
  try {
    if (!user?.id) {
      alert('❌ No user logged in');
      return;
    }

    const deviceId = await getNativeDeviceId();
    if (!deviceId) {
      alert('❌ Could not determine device id.');
      return;
    }

    // 1) Try DB token for this device
    const { data: row, error: tokenErr } = await supabase
      .from('user_fcm_tokens')
      .select('fcm_token')
      .eq('user_id', user.id)
      .eq('device_id', deviceId)
      .eq('is_active', true)
      .single();

    let token = row?.fcm_token;

    // 2) If no token, need to enable push first
    if (!token || tokenErr) {
      alert('❌ No device token found. Please enable notifications first by tapping "Enable Reminders".');
      console.error('Token fetch error:', tokenErr);
      return;
    }

    console.log('🧪 Testing push with device token:', token.substring(0, 20) + '...');
    console.log('🧪 Full token length:', token.length);

    // 3) Send via edge function
    const { data, error } = await supabase.functions.invoke('process-push-notifications', {
      body: {
        token,
        title: '🧪 Test Notification',
        body: 'If you see this, native push is working! 🎉',
      },
    });

    if (error) {
      console.error('❌ Edge function error:', error);
      alert('❌ Failed: ' + JSON.stringify(error));
      return;
    }

    console.log('✅ Push response:', data);
    alert('✅ Sent! Now background the app and look for the notification.');
  } catch (err) {
    console.error('Test error:', err);
    alert('Error: ' + (err?.message || JSON.stringify(err)));
  }
};

// ✅ Foreground notifications while the app is open
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!WEB_PUSH_ENABLED || isNativeApp()) return;
  let unsub = null;

  (async () => {
    if (!user?.id) return;

    const messaging = await messagingPromise;
    if (!messaging) return;

    unsub = onMessage(messaging, (payload) => {
      console.log('📬 Notification received!', payload);

      // Show browser notification when app is in foreground (WEB ONLY)
      if (
        payload?.notification &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
      ) {
        console.log('🔔 Showing notification:', payload.notification.title);
        
        try {
          const notification = new Notification(payload.notification.title || 'GweffIT', {
            body: payload.notification.body,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'gweffit-' + Date.now(), // Unique tag to allow multiple notifications
            requireInteraction: false,
            silent: false
          });
          
          console.log('✅ Notification created successfully');
          
          // Handle notification click
          notification.onclick = () => {
            window.focus();
            notification.close();
            try {
              const targetUrl =
                payload?.data?.click_action ||
                payload?.data?.url ||
                payload?.notification?.click_action ||
                payload?.fcmOptions?.link ||
                null;
              if (targetUrl) {
                window.dispatchEvent(new CustomEvent('gweffit:urlopen', {
                  detail: { url: targetUrl, source: 'foreground_notification_click' },
                }));
              }
            } catch (err) {
              console.warn('Failed to route foreground notification click:', err);
            }
          };
        } catch (e) {
          console.error('❌ Error creating notification:', e);
        }
      } else {
        console.log('⚠️ Cannot show notification - permission:', Notification.permission, 'has notification:', !!payload?.notification);
      }
    });
    
    console.log('👂 Listening for foreground messages...');
  })();

  return () => {
    if (typeof unsub === 'function') unsub();
  };
}, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- WEB_PUSH_ENABLED handled inside

// ✅ SILENT FCM TOKEN REFRESH - keeps token fresh on every page load
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  // Web-only. Never run browser FCM logic in native/TestFlight or when disabled.
  if (!WEB_PUSH_ENABLED || isNativeApp()) return;

  const silentTokenRefresh = async () => {
    // Only refresh if user is authenticated AND browser has granted permission
    if (!authUser?.id || !user?.id) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;

    try {
      console.log('🔄 Silent FCM token refresh starting...');

      const messaging = await messagingPromise;
      if (!messaging) {
        console.log('⚠️ Silent refresh: messaging not available');
        return;
      }

      // Get existing service worker registration
      const existingReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (!existingReg) {
        // Register service worker if not exists
        await navigator.serviceWorker.register('/firebase-messaging-sw.js?v=2025-10-31');
      }

      const reg = existingReg || await navigator.serviceWorker.ready;

      // Get fresh FCM token
      const token = await getToken(messaging, {
        vapidKey: VAPID_PUBLIC_KEY,
        serviceWorkerRegistration: reg,
      });

      if (!token) {
        console.log('⚠️ Silent refresh: could not get token');
        return;
      }

      console.log('🔄 Silent refresh: got token', token.substring(0, 20) + '...');

      // Update token in database (single web writer path)
      const saveResult = await saveFCMTokenToDatabase(user.id, token);

      if (!saveResult?.success) {
        console.error('❌ Silent refresh: failed to save token', saveResult?.error);
      } else {
        // Update last_login_date separately (non-token field)
        await supabase
          .from('users')
          .update({ last_login_date: new Date().toISOString() })
          .eq('id', user.id);

        console.log('✅ Silent refresh: token updated successfully');
        writePushPref(true);
        setPushStatus('enabled');
      }
    } catch (err) {
      console.error('❌ Silent refresh error:', err);
    }
  };

  // Run after a short delay to let auth settle
  const timer = setTimeout(silentTokenRefresh, 2000);
  return () => clearTimeout(timer);
}, [authUser?.id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- constants handled inside

// eslint-disable-next-line react-hooks/exhaustive-deps
const loadInboxSummons = useCallback(async (reason = 'manual') => {
  const fetchToken = ++inboxFetchTokenRef.current;
  if (!authUser?.id || !user?.id) {
    setInboxSummons([]);
    setInboxError(null);
    setInboxLoading(false);
    return;
  }

  setInboxLoading(true);
  setInboxError(null);
  console.log('[summon] inbox_load_start', {
    reason,
    user_id: user.id,
  });

  const loadByRecipientColumn = async (columnName) => {
    return supabase
      .from('summons')
      .select('id, trial_id, summoner_id, status, created_at, expires_at')
      .eq(columnName, user.id)
      .order('created_at', { ascending: false })
      .limit(25);
  };

  try {
    const inboxRpcLoader = () => supabase.rpc('get_home_inbox', { p_limit: 25 });
    const inboxRpcResult = (reason === 'mount' || reason === 'resume')
      ? await singleFlightCached(`get_home_inbox:${user.id}`, 10_000, inboxRpcLoader)
      : await singleFlight(`get_home_inbox:${user.id}`, inboxRpcLoader);
    const { data: rpcRows, error: rpcError } = inboxRpcResult || {};

    if (!rpcError && Array.isArray(rpcRows)) {
      const items = rpcRows.map((row) => ({
        id: row.inbox_id,
        kind: row.kind || 'summons',
        category: row.category || 'jury_duty',
        cta_url: row.cta_url || null,
        data: row.data || {},
        trial_id: row.data?.trial_id || null,
        trial_title: row.title || row.data?.trial_title || 'Update',
        trial_status: row.data?.trial_status || null,
        created_at: row.created_at,
        read_at: row.read_at || null,
        status: row.data?.summon_status || null,
        summoner_name: row.data?.summoner_name || 'Someone',
        title: row.title || 'Update',
        body: row.body || '',
        is_unread: !!row.is_unread,
      }));

      if (fetchToken !== inboxFetchTokenRef.current) return;
      setInboxSummons(pruneInboxItems(items));
      console.log('[summon] inbox_load_result', {
        reason,
        user_id: user.id,
        source: 'rpc_home_inbox',
        count: items.length,
      });
      return;
    }

    if (rpcError) {
      console.warn('[summon] inbox_load_rpc_fallback', {
        reason,
        user_id: user.id,
        error: rpcError.message || String(rpcError),
      });
    }

    let summonsRows = [];
    let summonsError = null;

    const primary = await loadByRecipientColumn('recipient_id');
    summonsRows = primary.data || [];
    summonsError = primary.error || null;

    if (summonsError && /recipient_id/i.test(String(summonsError.message || ''))) {
      const fallback = await loadByRecipientColumn('summoned_user_id');
      summonsRows = fallback.data || [];
      summonsError = fallback.error || null;
    }

    if (summonsError) throw summonsError;

    const openRows = (summonsRows || []).filter((row) => {
      const status = String(row.status || '').toLowerCase();
      return status === 'pending' || status === 'accepted';
    });

    const trialIds = [...new Set(openRows.map((row) => row.trial_id).filter(Boolean))];
    const summonerIds = [...new Set(openRows.map((row) => row.summoner_id).filter(Boolean))];

    let trialsMap = {};
    if (trialIds.length > 0) {
      const { data: trialRows, error: trialsErr } = await supabase
        .from('trials')
        .select('id, title, status, created_at')
        .in('id', trialIds);
      if (trialsErr) throw trialsErr;
      trialsMap = (trialRows || []).reduce((acc, row) => {
        acc[row.id] = row;
        return acc;
      }, {});
    }

    let usersMap = {};
    if (summonerIds.length > 0) {
      const { data: userRows } = await supabase
        .from('users')
        .select('id, username')
        .in('id', summonerIds);
      usersMap = (userRows || []).reduce((acc, row) => {
        acc[row.id] = row.username || 'Someone';
        return acc;
      }, {});
    }

    const items = openRows
      .map((row) => {
        const trialRow = trialsMap[row.trial_id];
        if (!trialRow) return null;
        if (!LIVE_TRIAL_STATUSES.has(String(trialRow.status || '').toLowerCase())) return null;
        return {
          id: row.id,
          trial_id: row.trial_id,
          trial_title: trialRow.title,
          trial_status: trialRow.status,
          created_at: row.created_at,
          read_at: row.clicked_at || row.created_at || null,
          status: row.status,
          summoner_name: usersMap[row.summoner_id] || 'Someone',
          is_unread: String(row.status || '').toLowerCase() === 'pending',
        };
      })
      .filter(Boolean);

    if (fetchToken !== inboxFetchTokenRef.current) return;
    setInboxSummons(pruneInboxItems(items));
    console.log('[summon] inbox_load_result', {
      reason,
      user_id: user.id,
      source: 'client_fallback',
      count: items.length,
    });
  } catch (err) {
    if (fetchToken !== inboxFetchTokenRef.current) return;
    const msg = err?.message || String(err);
    setInboxError(msg);
    console.warn('[summon] inbox_load_error', {
      reason,
      user_id: user.id,
      error: msg,
    });
  } finally {
    if (fetchToken === inboxFetchTokenRef.current) {
      setInboxLoading(false);
    }
  }
}, [authUser?.id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- pruneInboxItems stable by contract

const handleHomeManualRefresh = useCallback(async (reason = 'manual_pull') => {
  if (homeRefreshInFlightRef.current) return;
  homeRefreshInFlightRef.current = true;
  setHomeRefreshing(true);

  try {
    await Promise.all([
      refetchTrials(),
      authUser?.id && user?.id ? loadInboxSummons(reason) : Promise.resolve(),
      authUser?.id && user?.id ? refreshAtRiskCrews() : Promise.resolve(),
    ]);
  } catch (err) {
    console.warn('[home_manual_refresh_failed]', {
      reason,
      error: err?.message || String(err),
    });
  } finally {
    setHomeRefreshing(false);
    setHomePullDistance(0);
    homeRefreshInFlightRef.current = false;
  }
}, [authUser?.id, user?.id, loadInboxSummons, refetchTrials, refreshAtRiskCrews]);

const recoverRealtimeConnections = useCallback(async (source = 'resume') => {
  const beforeCount = supabase.getChannels().length;
  const startedAt = Date.now();
  try {
    await supabase.removeAllChannels();
  } catch (err) {
    console.warn('[app_resume_step]', {
      step: 'remove_all_channels_error',
      source,
      error: err?.message || String(err),
    });
  }
  try {
    await supabase.realtime.disconnect();
  } catch {}
  await new Promise((resolve) => setTimeout(resolve, 120));
  try {
    await supabase.realtime.connect();
  } catch {}
  setRealtimeResetNonce((prev) => prev + 1);
  console.log('[app_resume_step]', {
    step: 'realtime_recovered',
    source,
    before_channel_count: beforeCount,
    after_channel_count: supabase.getChannels().length,
    duration_ms: Date.now() - startedAt,
  });
}, []);

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleAppResume = useCallback(async (source = 'unknown') => {
  const now = Date.now();
  if (appResumeInFlightRef.current) {
    console.log('[app_resume] skipped_inflight', { source });
    console.log('[app_resume_step]', { step: 'resume_skip_in_flight', source });
    return;
  }
  if (now - appResumeLastRunRef.current < 1200) return;
  appResumeInFlightRef.current = true;
  appResumeLastRunRef.current = now;
  const resumeStartedAt = Date.now();
  const appState = isNativeApp() ? 'native' : 'web';
  console.log('[app_resume] begin', { source, appState });

  console.log('[app_resume_start]', {
    source,
    appState,
    visibilityState: document.visibilityState,
    has_auth_user: !!authUser?.id,
    has_app_user: !!user?.id,
    channel_count: supabase.getChannels().length,
  });

  clearTimeout(appResumeWatchdogRef.current);
  appResumeWatchdogRef.current = setTimeout(() => {
    console.warn('[app_resume_timeout]', {
      source,
      timeout_ms: 10_000,
      visibilityState: document.visibilityState,
    });
    setUserLoading(false);
    stopTrialsLoading();
    if (appResumeRecoveryCountRef.current < 1) {
      appResumeRecoveryCountRef.current += 1;
      setTimeout(() => {
        handleAppResume('watchdog_reinit');
      }, 250);
    }
  }, 10_000);

  try {
    const stepA = Date.now();
    const sessionResult = await withTimeout(
      supabase.auth.getSession(),
      8_000,
      'app_resume_get_session_timeout'
    );
    const resumedSession = sessionResult?.data?.session ?? null;
    console.log('[app_resume_step]', {
      step: 'auth_get_session',
      source,
      has_session: !!resumedSession,
      duration_ms: Date.now() - stepA,
    });
    if (resumedSession?.user && (!authUser?.id || authUser.id !== resumedSession.user.id)) {
      setAuthUser(resumedSession.user);
    }

    const stepB = Date.now();
    await withTimeout(
      recoverRealtimeConnections(source),
      8_000,
      'app_resume_realtime_timeout'
    );
    console.log('[app_resume_step]', {
      step: 'realtime_reconnect',
      source,
      duration_ms: Date.now() - stepB,
    });

    const stepC = Date.now();
    await withTimeout(refetchTrials(), 10_000, 'app_resume_fetch_trials_timeout');
    if (resumedSession?.user && user?.id) {
      await withTimeout(loadInboxSummons('resume'), 8_000, 'app_resume_inbox_timeout');
      setTimeout(() => {
        attemptGuestActivityClaim('app_resume');
      }, 0);
    }
    console.log('[app_resume_step]', {
      step: 'critical_data_refetch',
      source,
      duration_ms: Date.now() - stepC,
    });

    setUserLoading(false);
    stopTrialsLoading();
    appResumeRecoveryCountRef.current = 0;
    console.log('[app_resume_done]', {
      source,
      channel_count: supabase.getChannels().length,
      has_session: !!resumedSession,
      elapsed_ms: Date.now() - resumeStartedAt,
    });
    console.log('[app_resume] end', {
      source,
      elapsed_ms: Date.now() - resumeStartedAt,
    });
    logHealthSnapshot(`resume_${source}_done`);
  } catch (err) {
    console.error('[app_resume_error]', {
      source,
      error: err?.message || String(err),
      channel_count: supabase.getChannels().length,
      elapsed_ms: Date.now() - resumeStartedAt,
    });
    logHealthSnapshot(`resume_${source}_error`);
    setUserLoading(false);
    stopTrialsLoading();
  } finally {
    clearTimeout(appResumeWatchdogRef.current);
    appResumeInFlightRef.current = false;
  }
}, [authUser?.id, user?.id, refetchTrials, loadInboxSummons, recoverRealtimeConnections, logHealthSnapshot, attemptGuestActivityClaim]); // eslint-disable-line react-hooks/exhaustive-deps -- stopTrialsLoading/withTimeout stable

const handleAppResumeRef = useRef(handleAppResume);
useEffect(() => {
  handleAppResumeRef.current = handleAppResume;
}, [handleAppResume]);

// Load trials on mount + subscribe to realtime (single effect)
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
      refetchTrials();
  
  const trialsChannel = supabase
    .channel('trials-changes')

    // ---- UPDATE handler: live counts + show verdict on end ----
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'trials' },
      async (payload) => {
        const t = payload.new;
        console.log('[trial_realtime_update]', {
          trial_id: t?.id || null,
          prior_status: String(payload?.old?.status || '').toLowerCase() || null,
          next_status: String(t?.status || '').toLowerCase() || null,
          expires_at: t?.expires_at || null,
        });
        const nextStatus = String(t?.status || '').toLowerCase();
        if (['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(nextStatus)) {
          finalizeCompletedRef.current.add(t.id);
          setFinalizeErrorTrials(prev => {
            if (!prev.has(t.id)) return prev;
            const next = new Set(prev);
            next.delete(t.id);
            return next;
          });
        } else {
          finalizeCompletedRef.current.delete(t.id);
        }

        const prevTotalRaw =
          payload?.old?.total_votes ??
          ((payload?.old?.votes_a || 0) + (payload?.old?.votes_b || 0)) ??
          null;
        const nextTotalRaw =
          t?.total_votes ??
          ((t?.votes_a || 0) + (t?.votes_b || 0)) ??
          0;
        const deltaVotes = Number.isFinite(Number(prevTotalRaw))
          ? Math.max(0, Number(nextTotalRaw) - Number(prevTotalRaw))
          : (Number(nextTotalRaw) > 0 ? 1 : 0);
        const selfVoteAt = recentSelfVoteRef.current.get(t?.id || '') || 0;
        const suppressSelf = selfVoteAt && (Date.now() - selfVoteAt) < 70000;
        if (deltaVotes > 0 && !suppressSelf) {
          setRecentVotes((prev) => {
            const existing = prev[t.id] || { count: 0 };
            return {
              ...prev,
              [t.id]: {
                count: (existing.count || 0) + deltaVotes,
                source: 'realtime',
                timestamp: Date.now(),
              },
            };
          });
        }
        if (suppressSelf) {
          // keep suppression for the full live-activity window
          if (Date.now() - selfVoteAt >= 70000) {
            recentSelfVoteRef.current.delete(t?.id || '');
          }
        }

        // 1) keep list card live
        setTrials(prev =>
          prev.map(trial => {
            if (trial.id !== t.id) return trial;

            const totalVotes =
              t.total_votes != null ? t.total_votes : (t.votes_a || 0) + (t.votes_b || 0);

            return {
              ...trial,
              trialMode: t.trial_mode ?? trial.trialMode ?? 'debate',
              decisionCategory: t.decision_category ?? trial.decisionCategory ?? null,
              verdict_data: t.verdict_data ?? trial.verdict_data,
              votesA: t.votes_a ?? trial.votesA ?? 0,
              votesB: t.votes_b ?? trial.votesB ?? 0,
              totalVotes,
              jurorsFilled: t.jurors_filled ?? trial.jurorsFilled ?? 0,
              status: t.status ?? trial.status,
              expiresAt: t.expires_at ?? trial.expiresAt,
              closedAt: t.closed_at ? new Date(t.closed_at).getTime() : trial.closedAt,
              hasExtended: (t.has_extended ?? trial.hasExtended) || false,
              lastVoteTime: Date.now(),
              recentVotes: (trial.recentVotes || 0) + 1,
              suddenDeath: String(t.status || '').toLowerCase() === 'sudden_death_open',
              suddenDeathStartedAt: t.sudden_death_started_at ?? trial.suddenDeathStartedAt ?? null,
              suddenDeathEndsAt: t.sudden_death_ends_at ?? t.expires_at ?? trial.suddenDeathEndsAt ?? trial.expiresAt ?? null,
              suddenDeathRound: Number(t.sudden_death_round ?? trial.suddenDeathRound ?? 0),
              suddenDeathResolutionMethod: t.sudden_death_resolution_method ?? trial.suddenDeathResolutionMethod ?? null,
            };
          })
        );
// 1b) If the Verdict modal is open for this trial, sync newly-arrived verdict_data (esp. ai image)
setVerdictData(prev => {
  if (!prev?.trial || prev.trial.id !== t.id) return prev;

  const vd = t.verdict_data || {};

  const nextAiImage =
    vd.aiImage ??
    vd.ai_image ??
    vd.image_url ??
    vd.imageUrl ??
    prev.aiImage ??
    null;

  // prevent render loops: only update if something meaningful changed
  const changed =
    (nextAiImage || null) !== (prev.aiImage || null) ||
    (vd.punchline ?? null) !== (prev.punchline ?? null) ||
    JSON.stringify(vd.aiPunchlines ?? null) !== JSON.stringify(prev.aiPunchlines ?? null);

  if (!changed) return prev;

  const totalVotes =
    t.total_votes != null ? t.total_votes : (t.votes_a || 0) + (t.votes_b || 0);

  return {
    ...prev,
    winner: vd.winner ?? prev.winner,
    verdictType: vd.verdictType ?? vd.verdict_type ?? prev.verdictType,
    punchline: vd.punchline ?? prev.punchline,
    verdictNumber: vd.verdictNumber ?? vd.verdict_number ?? prev.verdictNumber,
    winnerPct: vd.winnerPct ?? vd.winner_pct ?? prev.winnerPct,
    aiImage: nextAiImage,
    aiPunchlines: vd.aiPunchlines ?? prev.aiPunchlines,
    templatePunchlines: vd.templatePunchlines ?? prev.templatePunchlines,
    trial: {
      ...prev.trial,
      verdict_data: vd,
      status: t.status ?? prev.trial.status,
      votesA: t.votes_a ?? prev.trial.votesA,
      votesB: t.votes_b ?? prev.trial.votesB,
      totalVotes,
      closedAt: t.closed_at ? new Date(t.closed_at).getTime() : prev.trial.closedAt,
    },
  };
});
        // 2) show verdict when trial ends (debounced + queued)
        if (['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(t.status) && !shownVerdictsRef.current.has(t.id)) {
          clearTimeout(verdictUpdateDebounce.current);
          verdictUpdateDebounce.current = setTimeout(() => {
            if (shouldAutoOpenVerdictModal(t)) {
              enqueueVerdict(t);
              return;
            }
            setShownVerdicts((prev) => {
              const next = new Set(prev);
              next.add(t.id);
              return next;
            });
          }, 500);
        }
      }
    )

    // ---- INSERT handler: add new trials ----
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'trials' },
      (payload) => {
        const n = payload.new;

        // Calculate time remaining for ALL trials with expires_at
        let timeLeft = null;
        if (n.expires_at) {
          const expiresAt = new Date(n.expires_at).getTime();
          timeLeft = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        }

        // Calculate actual duration
        const timeLimitSeconds = n.time_limit_seconds || 
          (n.expires_at && n.created_at 
            ? Math.floor((new Date(n.expires_at) - new Date(n.created_at)) / 1000) 
            : 120);

        if (Boolean(n.is_seeded) && homeFeedScopeRef.current !== 'all') {
          return;
        }

        const seed = {
          id: n.id,
          type: n.trial_type,
          trialMode: n.trial_mode ?? 'debate',
          decisionCategory: n.decision_category ?? null,
          title: n.title,
          sideA: n.side_a,
          sideB: n.side_b,
          votesA: n.votes_a || 0,
          votesB: n.votes_b || 0,
          totalVotes: n.total_votes || 0,
          status: n.status,
          image: n.image_emoji,
          jurySize: n.jury_size || BETA_JURY_SIZE,
          jurorsFilled: n.jurors_filled || 0,
          timeLeft,
          timeLimitSeconds,
          expiresAt: n.expires_at,
          hasExtended: n.has_extended || false,
          duration: n.trial_type === 'quick' ? '2min' : '24h',
          hasVoted: false,
          recentVotes: 0,
          lastVoteTime: Date.now(),
          featured: true,
          suddenDeath: false,
          createdAt: new Date(n.created_at).getTime(),
          created_by: n.created_by,
          creator_id: n.creator_id,
          is_seeded: Boolean(n.is_seeded),
          seed_source: n.seed_source || null,
        };

        setTrials(prev => {
          const existing = prev.find((t) => t.id === n.id);
          const nextRow = existing
            ? {
                ...seed,
                ...existing,
                ...seed,
                hasVoted: Boolean(existing?.hasVoted),
                userVoteSide: existing?.userVoteSide ?? null,
                userVote: existing?.userVote ?? null,
                creator_username: existing?.creator_username ?? null,
              }
            : seed;

          const nextMap = new Map();
          if (!existing) {
            nextMap.set(n.id, nextRow);
          }
          prev.forEach((trial) => {
            nextMap.set(trial.id, trial.id === n.id ? nextRow : trial);
          });
          return Array.from(nextMap.values()).sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
        });
        scheduleHomeFeedResync('realtime_insert');
      }
    )
    .subscribe();

  // cleanup
  return () => {
    supabase.removeChannel(trialsChannel);
  };
}, [realtimeResetNonce]); // eslint-disable-line react-hooks/exhaustive-deps -- enqueueVerdict/refetchTrials intentionally stable

useEffect(() => {
  loadInboxSummons('mount');
}, [loadInboxSummons]);

useEffect(() => {
  if (verdictMemeRecoveryInFlightRef.current) return;

  const pathname = window.location.pathname || '';
  const routeMatch = pathname.match(/\/t\/([0-9a-f-]{36})/i);
  const routeTrialId = routeMatch?.[1] || null;
  const focusedTrialId = verdictData?.trial?.id || routeTrialId;
  if (!focusedTrialId) return;

  const candidate = (trials || []).find((trial) => {
    if (!trial?.id || trial.id !== focusedTrialId) return false;
    const status = String(trial.status || '').toLowerCase();
    if (!['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(status)) return false;
    const verdictDataRow = trial.verdict_data || {};
    const hasTextVerdict = Boolean(verdictDataRow.punchline || verdictDataRow.verdictType || verdictDataRow.verdict_type);
    const hasImage = Boolean(verdictDataRow.aiImage || verdictDataRow.ai_image || verdictDataRow.image_url);
    return hasTextVerdict && !hasImage && !verdictMemeRecoveryTriggeredRef.current.has(trial.id);
  });
  if (!candidate) return;

  verdictMemeRecoveryInFlightRef.current = true;
  verdictMemeRecoveryTriggeredRef.current.add(candidate.id);
  console.log('[verdict] render_text_fallback', {
    trial_id: candidate.id,
    status: candidate.status || null,
  });

  let cancelled = false;
  (async () => {
    console.log('[verdict] meme_recovery_triggered', { trial_id: candidate.id });
    try {
      const aiVerdict = await fetchAIVerdict(candidate.id);
      if (cancelled || !aiVerdict?.aiImage) {
        if (!cancelled) {
          console.log('[verdict] meme_recovery_failed', {
            trial_id: candidate.id,
            reason: 'no_ai_image',
          });
        }
        return;
      }
      const mergedVerdictData = await persistTrialVerdictData(candidate.id, {
        ...(candidate.verdict_data || {}),
        ...aiVerdict,
        image_url: aiVerdict.aiImage || aiVerdict.image_url || null,
      });
      if (cancelled) return;
      setTrials((prev) => prev.map((trial) => (
        trial.id === candidate.id
          ? { ...trial, verdict_data: mergedVerdictData }
          : trial
      )));
      console.log('[verdict] meme_recovery_succeeded', { trial_id: candidate.id });
    } catch (err) {
      if (!cancelled) {
        console.log('[verdict] meme_recovery_failed', {
          trial_id: candidate.id,
          reason: err?.message || String(err),
        });
      }
    } finally {
      verdictMemeRecoveryInFlightRef.current = false;
    }
  })();

  return () => {
    cancelled = true;
    verdictMemeRecoveryInFlightRef.current = false;
  };
}, [trials, verdictData, fetchAIVerdict, persistTrialVerdictData]); // eslint-disable-line react-hooks/exhaustive-deps -- setTrials stable

useEffect(() => {
  const triggerResumeDebounced = (source) => {
    clearTimeout(appResumeDebounceTimerRef.current);
    appResumeDebounceTimerRef.current = setTimeout(() => {
      handleAppResumeRef.current?.(source);
    }, 250);
  };

  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    triggerResumeDebounced('visibility_visible');
  };
  document.addEventListener('visibilitychange', onVisible);

  if (isNativeApp()) {
    registerOnce('cap_app_lifecycle_listeners', async () => {
      const h1 = await CapacitorApp.addListener('appStateChange', (state) => {
        if (!state?.isActive) {
          invalidateTrialsFetch();
          inboxFetchTokenRef.current += 1;
          return;
        }
        triggerResumeDebounced('app_state_active');
      });

      const h2 = await CapacitorApp.addListener('resume', () => {
        triggerResumeDebounced('capacitor_resume');
      });

      return () => {
        try { h1?.remove?.(); } catch {}
        try { h2?.remove?.(); } catch {}
      };
    }).catch(() => {});
  }

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    clearTimeout(appResumeDebounceTimerRef.current);
  };
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- invalidateTrialsFetch stable

useEffect(() => {
  if (!authUser?.id || !user?.id) return undefined;

  const channel = supabase
    .channel(`summons-inbox-${user.id}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'summons' },
      (payload) => {
        const row = payload?.new || payload?.old || {};
        const recipientId = row.recipient_id || row.summoned_user_id;
        if (recipientId && recipientId === user.id) {
          loadInboxSummons('realtime');
        }
      },
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'inbox_items' },
      (payload) => {
        const row = payload?.new || payload?.old || {};
        if (row.user_id && row.user_id === user.id) {
          loadInboxSummons('realtime_inbox_item');
        }
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}, [authUser?.id, user?.id, loadInboxSummons, realtimeResetNonce]);

useEffect(() => {
  if (view !== 'home' || showHowToPlay) {
    homePullStartYRef.current = null;
    homePullActiveRef.current = false;
    setHomePullDistance(0);
    setHomeRefreshing(false);
  }
}, [showHowToPlay, view]);

const handleHomeTouchStart = useCallback((event) => {
  if (view !== 'home' || showHowToPlay || homeRefreshing || window.scrollY > 0) {
    homePullStartYRef.current = null;
    homePullActiveRef.current = false;
    return;
  }
  homePullStartYRef.current = event.touches?.[0]?.clientY ?? null;
  homePullActiveRef.current = true;
}, [homeRefreshing, showHowToPlay, view]);

const handleHomeTouchMove = useCallback((event) => {
  if (!homePullActiveRef.current || showHowToPlay || homeRefreshing) return;
  const startY = homePullStartYRef.current;
  const currentY = event.touches?.[0]?.clientY ?? null;
  if (startY == null || currentY == null) return;
  const delta = currentY - startY;
  if (delta <= 0 || window.scrollY > 0) {
    setHomePullDistance(0);
    return;
  }
  setHomePullDistance(Math.min(delta * 0.45, 96));
}, [homeRefreshing, showHowToPlay]);

const handleHomeTouchEnd = useCallback(() => {
  if (!homePullActiveRef.current) return;
  const shouldRefresh = homePullDistance >= HOME_PULL_TRIGGER_PX;
  homePullStartYRef.current = null;
  homePullActiveRef.current = false;
  if (shouldRefresh) {
    void handleHomeManualRefresh('pull_to_refresh');
    return;
  }
  setHomePullDistance(0);
}, [handleHomeManualRefresh, homePullDistance]);


 // 🛡️ Debounced Supabase writes + immediate localStorage for local-only fields
const lastWriteRef = useRef(null);
const debouncedWrite = useRef(null);

// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!user?.id) return;

  // local-only persistence (always)
  const localData = pick(user, [
    'trialsJoined',
    'ownedAvatars',
    'selectedAvatar'
  ]);
  safeStorage.set('gweffit_user_local', JSON.stringify(localData));

  // only write coins/points/streak → Supabase, and debounce it
  const payload = pick(user, ['coins', 'points', 'streak']);
  if (JSON.stringify(payload) === JSON.stringify(lastWriteRef.current)) return;

  clearTimeout(debouncedWrite.current);
  debouncedWrite.current = setTimeout(async () => {
    await updateUserInSupabase(user.id, payload);
    lastWriteRef.current = payload;
  }, 800);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  user?.id,
  user?.coins,
  user?.points,
  user?.streak,
  user?.trialsJoined,
  user?.ownedAvatars,
  user?.selectedAvatar
]);


  // Handle tab visibility for performance
  useEffect(() => {
    const onVis = () => setIsTabActive(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

    // Check database for first vote completion status
useEffect(() => {
  async function checkFirstVoteStatus() {
    try {
      const localStatus =
        typeof window !== 'undefined' &&
        safeStorage.get('gweffit_first_vote_completed') === 'true';

      // If not authenticated, just rely on local flag and stop.
      // (We never show the modal unless authUser is set anyway.)
      if (!authUser) {
        setFirstVoteLoading(false);
        return;
      }

      // Wait until the app-level user row is loaded
      if (!user?.id) {
        // Effect will re-run once user.id is set
        return;
      }

      // 1) Check users onboarding state by app user id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('has_completed_first_vote, onboarding_completed_at')
        .eq('id', user.id) // ✅ app user id, not authUser.id
        .single();

      if (userError) {
        console.error('Error checking first vote status:', userError);
        // Fallback: trust localStorage
        setHasCompletedFirstVote(localStatus);
        return;
      }

      const onboardingCompleted =
        Boolean(userData?.onboarding_completed_at) ||
        userData?.has_completed_first_vote === true ||
        localStatus;

      // If DB flag OR local flag is true, mark as completed and sync both
      if (onboardingCompleted) {
        setHasCompletedFirstVote(true);
        safeStorage.set('gweffit_first_vote_completed', 'true');

        // Backfill durable DB marker if missing
        if (!userData?.onboarding_completed_at || !userData?.has_completed_first_vote) {
          await supabase
            .from('users')
            .update({
              has_completed_first_vote: true,
              onboarding_completed_at: userData?.onboarding_completed_at || new Date().toISOString(),
            })
            .eq('id', user.id);
        }
        return;
      }

      // 2) Extra safety: if user already has ANY votes, consider them complete
      const { data: votes, error: votesError } = await supabase
        .from('votes')
        .select('id')
        .eq('user_id', user.id) // ✅ votes.user_id uses app user id
        .limit(1);

      if (votesError) {
        console.error('Error checking votes:', votesError);
        setHasCompletedFirstVote(localStatus);
        return;
      }

      if (votes && votes.length > 0) {
        console.log('🔧 User has votes but first-vote flag not set - fixing…');

        await supabase
          .from('users')
          .update({
            has_completed_first_vote: true,
            onboarding_completed_at: new Date().toISOString(),
          })
          .eq('id', user.id);

        setHasCompletedFirstVote(true);
        safeStorage.set('gweffit_first_vote_completed', 'true');
        console.log('✅ First vote flag fixed for existing user');
      } else {
        // User truly hasn’t voted yet
        setHasCompletedFirstVote(false);
        console.log('📊 User has not completed first vote');
      }
    } catch (err) {
      console.error('Error in checkFirstVoteStatus:', err);
      const localStatus =
        typeof window !== 'undefined' &&
        safeStorage.get('gweffit_first_vote_completed') === 'true';
      setHasCompletedFirstVote(localStatus);
    } finally {
      setFirstVoteLoading(false);
    }
  }

  checkFirstVoteStatus();
}, [authUser, user?.id]);

const notificationClickHandledRef = useRef(null);

  // Deep link handler for notification clicks
useEffect(() => {
  const handleNotificationClick = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const notificationType = urlParams.get('notification_type');
    const trialId = urlParams.get('trial_id');
    const summonerId = urlParams.get('summoner_id');
    const bonusCoins = urlParams.get('bonus_coins');
    const signature = [notificationType || '', trialId || '', summonerId || '', bonusCoins || ''].join('|');

    if (!notificationType || !trialId) return;
    if (notificationClickHandledRef.current === signature) return;
    notificationClickHandledRef.current = signature;

    console.log('📱 Checking for notification click from deep link...');

    if (notificationType && trialId) {
      console.log('🎯 Deep link detected:', {
        type: notificationType,
        trialId,
        summonerId,
        bonusCoins
      });

      // Handle different notification types
      if (notificationType === 'jury_summons') {
        console.log('⚖️ Handling jury summons click');
        
        // Show bonus indicator if there's a bonus
        if (bonusCoins && parseInt(bonusCoins) > 0) {
          setShowBonusCoinsIndicator(parseInt(bonusCoins));
          
          // Auto-hide after 10 seconds
          setTimeout(() => setShowBonusCoinsIndicator(0), 10000);
        }

        // Scroll to trial if it's in the feed
        setTimeout(() => {
          const trialElement = document.getElementById(`trial-${trialId}`);
          if (trialElement) {
            trialElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            console.log('✅ Scrolled to trial:', trialId);
          } else {
            console.log('⚠️ Trial not in current view, user may need to navigate');
          }
        }, 500);

        // Track engagement (optional)
        if (summonerId && user?.id) {
          const clickPayload = {
            status: 'clicked',
            clicked_at: new Date().toISOString()
          };

          supabase
            .from('summons')
            .update(clickPayload)
            .eq('trial_id', trialId)
            .eq('recipient_id', user.id)
            .eq('summoner_id', summonerId)
            .then(({ error }) => {
              if (!error) return console.log('✅ Summons click tracked');
              if (!/recipient_id/i.test(String(error.message || ''))) throw error;
              return supabase
                .from('summons')
                .update(clickPayload)
                .eq('trial_id', trialId)
                .eq('summoned_user_id', user.id)
                .eq('summoner_id', summonerId)
                .then(() => console.log('✅ Summons click tracked'))
                .catch((fallbackErr) => console.warn('Failed to track summons click:', fallbackErr));
            })
            .catch(err => console.warn('Failed to track summons click:', err));
        }
      }

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Run on mount and when auth changes
  if (authUser && !userLoading) {
    handleNotificationClick();
  }
}, [authUser, userLoading, user]);

  // Reset recent votes counter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isTabActive || showVerdict || showPremiumModal || showBuyCoinsModal || showAvatarShop || showHowToPlay || showSuddenDeath) return;

    const interval = setInterval(() => {
      setTrials(prevTrials => prevTrials.map(trial => ({
        ...trial,
        recentVotes: Date.now() - (trial.lastVoteTime || Date.now()) > 10000 ? 0 : (trial.recentVotes || 0)
      })));
    }, 5000);

    return () => clearInterval(interval);
}, [isTabActive, showVerdict, showPremiumModal, showBuyCoinsModal, showAvatarShop, showHowToPlay, showLeaderboard, showSuddenDeath, trials]); // eslint-disable-line react-hooks/exhaustive-deps -- setTrials stable
// Tick down trial timers once and keep countdown/finalize transitions stable.
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const tick = setInterval(() => {
    setTrials(prev => {
      const openStatuses = new Set(['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending']);
      return prev.map((t) => {
        if (!openStatuses.has(String(t.status || '').toLowerCase())) return t;
        if (!t.expiresAt) return t;

        const expiresAt = new Date(t.expiresAt).getTime();
        if (!Number.isFinite(expiresAt)) return t;

        const now = Date.now();
        const newTimeLeft = Math.max(0, Math.floor((expiresAt - now) / 1000));
        const nextStage = newTimeLeft <= 0 ? 'expired' : newTimeLeft <= 10 ? 'critical' : newTimeLeft <= 60 ? 'warning' : 'running';
        const prevStage = trialTimerStageRef.current.get(t.id);
        if (prevStage !== nextStage) {
          trialTimerStageRef.current.set(t.id, nextStage);
          console.log('[trial_timer_tick]', {
            trial_id: t.id,
            status: t.status,
            time_left: newTimeLeft,
            stage: nextStage,
          });
        }

        return {
          ...t,
          timeLeft: newTimeLeft,
        };
      });
    });
  }, 1000);
  
  return () => clearInterval(tick);
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- setTrials stable

// Clean up old vote activity (older than 60 seconds)
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const cleanup = setInterval(() => {
    const now = Date.now();
    setRecentVotes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(trialId => {
        if (now - updated[trialId].timestamp > 60000) { // 60 seconds
          delete updated[trialId];
        }
      });
      return updated;
    });
  }, 5000); // Check every 5 seconds

  return () => clearInterval(cleanup);
}, []);

// Trigger animation when trial votes update
useEffect(() => {
  trials.forEach(trial => {
    if (recentVotes[trial.id]?.count > 0) {
      setAnimatingVotes(prev => new Set([...prev, trial.id]));

      // Remove animation after 1 second
      setTimeout(() => {
        setAnimatingVotes(prev => {
          const newSet = new Set(prev);
          newSet.delete(trial.id);
          return newSet;
        });
      }, 1000);
    }
  });
}, [trials, recentVotes]);

// When any quick trial hits T (timeLeft <= 0), lock & reveal locally
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const run = async () => {
    const allowFeedBulkFinalize = typeof window !== 'undefined' && window.__ALLOW_FEED_BULK_FINALIZE__ === true;
    for (const t of trials) {
      //if (t.type !== 'quick') continue;
      if (shownVerdicts.has(t.id) || showVerdict || showSuddenDeath) continue;

      // Calculate total duration since creation
      const totalDuration = t.createdAt ? (Date.now() - t.createdAt) / 1000 : 0;
      const juryFull = t.totalVotes >= (t.jurySize || BETA_JURY_SIZE);
      
      // Use trial's actual time limit (from timeLimitSeconds or default 2 min for quick, 24h for extended)
      const trialTimeLimit = t.timeLimitSeconds || (t.type === 'quick' ? 120 : 86400);
      const maxDurationReached = totalDuration >= trialTimeLimit;
      
      // timeLeft === 0 means timer expired (null means no timer set)
      const timerExpired = t.timeLeft !== null && t.timeLeft <= 0;
      
      const openStatuses = new Set(['filling', 'ready', 'active', 'closing', 'closed_pending_verdict', 'sudden_death_open', 'sudden_death_closed_pending']);
      const expiresAtMs = t.expiresAt ? new Date(t.expiresAt).getTime() : null;
      const expiresAtReached = Number.isFinite(expiresAtMs) ? expiresAtMs <= Date.now() : false;

      // End trial if: open + jury full OR timer expired OR explicit expires_at threshold reached.
      if (allowFeedBulkFinalize && openStatuses.has(String(t.status || '').toLowerCase()) &&
          (juryFull || timerExpired || maxDurationReached || expiresAtReached)) {
        try {
          // [TRIAL_END_NOTIFY_SERVER_OWNER]
          // Server is the only owner of terminal status + trial-ended notifications.
          const finalizeResult = await maybeFinalizeTrial(t, 'timer_expired');
          if (!finalizeResult?.ok) continue;

          // Compute and show verdict - properly handle ties
          const winner = t.votesA === t.votesB ? 'Tie' : t.votesA > t.votesB ? 'A' : 'B';
          
          // Try AI generation
          const aiVerdict = await fetchAIVerdict(t.id);
          
          let verdictType, punchline, verdictNumber, winnerPct, aiImage;
          
          if (aiVerdict) {
  console.log('📦 Using CACHED AI verdict:', aiVerdict);
  verdictType = aiVerdict.verdict_type || "VERDICT";
  punchline =
    aiVerdict.roast_punchline ??
    aiVerdict.roast_punchlines?.[0] ??
    aiVerdict.internet_punchline ??
    aiVerdict.internet_punchlines?.[0] ??
    aiVerdict.sports_punchline ??
    aiVerdict.sports_punchlines?.[0] ??
    'The jury has spoken.';
  winnerPct = Number(aiVerdict.winner_pct) || undefined;
  aiImage = getPersistedAiImage(t, aiVerdict);

            verdictNumber = Math.floor(Math.random() * 90000) + 10000;
          } else {
              console.log('🆕 No cache - using getVerdictCopy fallback');
            const fallback = getVerdictCopy(t, winner);
            verdictType = fallback.verdictType;
            punchline = fallback.punchline;
            verdictNumber = fallback.verdictNumber;
            winnerPct = fallback.winnerPct;
            aiImage = getPersistedAiImage(t, null);
          }

          setVerdictData({
            trial: t,
            winner,
            verdictType,
            punchline,
            verdictNumber,
            winnerPct,
            snap: true, // badge "SNAP VERDICT"
            aiImage,
            aiPunchlines: aiVerdict ? {
  roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
  sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
  internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline]
} : null
          });

          

          // Save verdict data to database for flip card
const verdictDataToStore = {
  ...(t.verdict_data || {}),   // ✅ preserve whatever is already stored
  winner,
  verdictType,
  punchline,
  verdictNumber,
  winnerPct,
  ...(aiImage ? { aiImage } : {}),                     // ✅ make sure this isn’t null-wiping
  aiPunchlines: aiVerdict ? {
    roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
    sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
    internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline]
  } : (t.verdict_data?.aiPunchlines ?? null)
};

const persistedVerdictData = await persistTrialVerdictData(t.id, verdictDataToStore);

// [TRIAL_END_NOTIFY_SERVER_OWNER]
// Client should not produce trial-ended notifications.
// Server-side finalize/drain pipeline is the single owner to prevent repeat sends on app reopen.

          setShowSuddenDeath(false);
          if (shouldAutoOpenVerdictModal(t)) {
            setShowVerdict(true);
          }
          setShownVerdicts((prev) => {
            const next = new Set(prev);
            next.add(t.id);
            return next;
          });

          // reflect ended status locally
setTrials(prev => prev.map(x => x.id === t.id ? { 
  ...x, 
  status: 'ended',
  verdict_data: persistedVerdictData  // ✅ Include server-merged verdict data
} : x));
        } catch (e) {
          console.error('Auto-reveal error', e);
        }
      }
    }
  };

run();
}, [trials, shownVerdicts, showVerdict, showSuddenDeath, fetchAIVerdict, getVerdictCopy, maybeFinalizeTrial, persistTrialVerdictData, shouldAutoOpenVerdictModal]); // eslint-disable-line react-hooks/exhaustive-deps -- setTrials stable


  // ===== AI image persistence helper =====
// Prefer newest AI verdict image_url, otherwise keep whatever is already stored on the trial row.
function getPersistedAiImage(trialRow, aiVerdict) {
  const fromAI =
    aiVerdict?.image_url ??
    aiVerdict?.imageUrl ??
    aiVerdict?.image ??
    null;

  const vd = trialRow?.verdict_data ?? trialRow?.verdictData ?? null;

  const fromStored =
    vd?.aiImage ??
    vd?.ai_image ??
    vd?.image_url ??
    vd?.imageUrl ??
    null;

  return fromAI || fromStored;
}

// Simple hash function for stable pseudo-random selection
const stableHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

const stablePick = (id, arr) => {
  if (!arr?.length) return '';
  const idx = stableHash(String(id || '')) % arr.length;
  return arr[idx];
};

// eslint-disable-next-line react-hooks/exhaustive-deps
const getStableSpicyPunchline = useCallback((trial, winnerLabel) => {
  const options = [
    `${winnerLabel} absolutely cooked. No crumbs left.`,
    `The people have spoken: ${winnerLabel} clears.`,
    `Not even close. ${winnerLabel} supremacy.`,
    `${winnerLabel} ate and left no crumbs. Case closed.`,
    `Certified W for ${winnerLabel}. Next case.`,
    `${winnerLabel} walked so the other side could crawl.`,
    `The jury said what they said: ${winnerLabel}.`,
    `Big W energy: ${winnerLabel}.`,
  ];

  // Tie the pick to the trial id so it NEVER changes on re-render
  return stablePick(trial?.id, options) || `The jury has spoken: ${winnerLabel}.`;
}, []); // eslint-disable-line react-hooks/exhaustive-deps -- stablePick intentionally stable
  // Utility
  const clamp = useCallback((n, min, max) => Math.max(min, Math.min(max, n)), []);

  // Calculate odds with protection against extremes
  const calculateOdds = useCallback((trial, side) => {
  const votesA = trial.votesA || 0;
  const votesB = trial.votesB || 0;
  const total = Math.max(votesA + votesB, 1);
  const sideVotes = side === 'A' ? votesA : votesB;
  const p = clamp(sideVotes / total, 0.05, 0.95);
  return parseFloat((1 / p).toFixed(2));
}, [clamp]);

  const formatTime = useCallback((seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  }, []);

  const downloadMeme = async () => {
    setIsGeneratingMeme(true);
    try {
      const node = document.getElementById('verdict-share-card');
      const artifact = await buildVerdictModalArtifactBlob(verdictData, node);
      if (!artifact?.blob) {
        showAppNotice('Generating image. Try again in a second.', 'error');
        return;
      }

      const trial = verdictData?.trial;
      const filename = `gweffit-${trial?.title?.toLowerCase?.().replace(/[^a-z0-9]/g, '-') || 'verdict'}.${artifact.extension || 'png'}`;
      const url = URL.createObjectURL(artifact.blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      console.log('[share] verdict_modal_download_asset', {
        trialId: verdictData?.trial?.id || verdictData?.trialId || null,
        source: artifact.source,
        assetUrl: artifact.assetUrl || null,
        assetSource: artifact.assetSource || null,
      });

      try {
        if (verdictData?.trial?.id && user?.id) {
          await supabase.from('shares').insert({
            trial_id: verdictData.trial.id,
            user_id: user.id,
            method: 'download'
          });
        }
      } catch (e) {
        console.warn('Share tracking (download) failed:', e?.message || e);
      }

      try {
        trackEvent('shared_verdict', {
          trial_id: verdictData?.trial?.id,
          method: 'download',
          asset_source: artifact.source,
        });
      } catch {}
    } catch (error) {
      console.error('Error generating meme:', error);
      showAppNotice('Save failed. Try again.', 'error');
    } finally {
      setIsGeneratingMeme(false);
    }
  };

  // ===== REPLACE YOUR shareMeme FUNCTION (around line 3334) =====
// This version includes a deep link to the trial
const waitForImagesInNode = async (node) => {
  const imgs = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    imgs.map(img => (
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise(res => {
            img.addEventListener("load", res, { once: true });
            img.addEventListener("error", res, { once: true });
          })
    ))
  );
};

const buildVerdictModalArtifactBlob = async (verdictData, node) => {
  const mergedVerdictSource = {
    ...(verdictData || {}),
    id: verdictData?.trial?.id || verdictData?.trialId || verdictData?.id || null,
    trial_id: verdictData?.trial?.id || verdictData?.trialId || verdictData?.id || null,
    trial: verdictData?.trial || null,
    verdict_data:
      verdictData?.verdict_data
      || verdictData?.trial?.verdict_data
      || null,
  };

  const canonicalAsset = getCanonicalTrialDetailVerdictUrl(mergedVerdictSource);
  if (canonicalAsset?.url) {
    try {
      const response = await fetch(canonicalAsset.url);
      if (response.ok) {
        const blob = await response.blob();
        const mimeType = String(blob?.type || '').toLowerCase();
        const isRaster = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(mimeType);
        if (blob && blob.size > 0 && isRaster) {
          return {
            blob,
            mimeType,
            extension: mimeType.includes('png')
              ? 'png'
              : mimeType.includes('webp')
                ? 'webp'
                : 'jpg',
            source: 'remote_asset',
            assetUrl: canonicalAsset.url,
            assetSource: canonicalAsset.source || null,
          };
        }
      }
    } catch (err) {
      console.warn('[share] verdict_modal_asset_fetch_failed', {
        trialId: mergedVerdictSource.trial_id || null,
        assetUrl: canonicalAsset.url,
        error: err?.message || String(err),
      });
    }
  }

  if (!node) return null;
  await waitForImagesInNode(node);
  const canvas = await html2canvas(node, { scale: 2, useCORS: true });
  const blob = await new Promise((resolve) => {
    canvas.toBlob((value) => resolve(value || null), 'image/png');
  });
  if (!blob) return null;
  return {
    blob,
    mimeType: 'image/png',
    extension: 'png',
    source: 'canvas_capture',
    assetUrl: null,
    assetSource: null,
  };
};

const shareMeme = async () => {
  try {
    const node = document.getElementById('verdict-share-card');

    // Get the trial ID from the currently shown verdict
    const trialId = verdictData?.trialId || verdictData?.trial?.id;
      const deepLink = trialId
        ? getShareUrl('trial', trialId, user?.id ? {
            ref: user.id,
            ref_src: 'share_link',
            ref_id: trialId,
          } : {})
        : CANONICAL_HOST;
    
    // Build share text with clear CTA
    const shareText = verdictData 
      ? `The jury has spoken! 🔥 Think they got it wrong? Vote yourself → ${deepLink}`
      : `Check out this verdict! Vote on more trials → ${deepLink}`;
    const artifact = await buildVerdictModalArtifactBlob(verdictData, node);
    if (!artifact?.blob) {
      showAppNotice('Generating image. Try again in a second.', 'error');
      return;
    }

    const file = new File([artifact.blob], `gweffit-verdict.${artifact.extension || 'png'}`, {
      type: artifact.mimeType || 'image/png',
    });

    console.log('[share] verdict_modal_share_asset', {
      trialId: trialId || null,
      source: artifact.source,
      assetUrl: artifact.assetUrl || null,
      assetSource: artifact.assetSource || null,
    });

    // Track the share event
    trackEvent('shared_verdict', {
      trial_id: trialId,
      method: 'native_share',
      asset_source: artifact.source,
    });

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'GweffIT Verdict',
          text: shareText,
          url: deepLink
        });
        console.log('✅ Share completed');
      } catch (shareErr) {
        if (shareErr.name !== 'AbortError') {
          console.warn('Share failed, falling back to download:', shareErr);
          downloadFallback(artifact.blob);
        }
      }
    } else {
      // No native share - download + copy link
      downloadFallback(artifact.blob);

      // Also copy the link to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        showAppNotice('Image saved. Share link copied to clipboard.');
      } catch {
        showAppNotice('Image saved. Share link is ready to send.');
      }
    }
  } catch (err) {
    console.error('Share error:', err);
    showAppNotice('Share failed. Try again.', 'error');
  }
};

// Helper for download fallback
const downloadFallback = (blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'gweffit-verdict.png';
  a.click();
  URL.revokeObjectURL(url);
};
  // ========= LEADERBOARD DATA FETCHING =========
  const fetchLeaderboardData = useCallback(async (category = 'xp') => {
    setLeaderboardLoading(true);
    try {
      let orderBy = 'points';
      if (category === 'streak') orderBy = 'streak';
      if (category === 'coins') orderBy = 'coins';

      // Fetch top 50 authenticated users only
      const { data, error } = await supabase
        .from('users')
        .select('id, username, points, streak, coins')
        .not('auth_user_id', 'is', null)
        .order(orderBy, { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching leaderboard:', error);
        return;
      }

      setLeaderboardData(data);

      // Fetch current user's rank (only if authenticated)
      if (user.id && authUser) {
        const userValue = category === 'xp' ? user.points : category === 'streak' ? user.streak : user.coins;
        const { count } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .not('auth_user_id', 'is', null)
          .gt(orderBy, userValue);

        setUserRank((count || 0) + 1);
      } else {
        setUserRank(null);
      }

    } catch (error) {
      console.error('Error in fetchLeaderboardData:', error);
    } finally {
      setLeaderboardLoading(false);
    }
  }, [user.id, user.points, user.streak, user.coins, authUser]);

  // Fetch leaderboard data when modal opens or tab changes
  useEffect(() => {
    if (showLeaderboard) {
      fetchLeaderboardData(leaderboardTab);
    }
  }, [showLeaderboard, leaderboardTab, fetchLeaderboardData]);

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleQuickTrialVote = useCallback(async (trialId, side) => {
  const trial = trials.find(t => t.id === trialId);
  if (!trial || trial.type !== 'quick') return;
  
  // 🆕 CRITICAL: Check authentication before voting
  if (!user?.id) {
    console.warn('❌ Vote blocked: User not authenticated');
    alert('Please log in to vote');
    setShowAuthModal(true);
    return;
  }
  
  // ✅ CHECK FOR SUMMONS BONUS
  const urlParams = new URLSearchParams(window.location.search);
  const summonsToken = urlParams.get('summons');
  let bonusCoins = 0;
  let summonerId = null;
  
  if (summonsToken) {
    try {
      // Check if summons is valid and not expired
      const { data: summonsData, error } = await supabase
        .from('summons')
        .select('*')
        .eq('summons_token', summonsToken)
        .single();
      
      if (!error && summonsData.status === 'accepted') {
        const now = new Date();
        const expires = new Date(summonsData.expires_at);
        
        // If within 2 minutes, give bonus
        if (now <= expires && !summonsData.bonus_claimed) {
          bonusCoins = 100; // Bonus for summoned user
          summonerId = summonsData.summoner_id;
          
          // Mark bonus as claimed
          await supabase
            .from('summons')
            .update({ bonus_claimed: true })
            .eq('id', summonsData.id);
        }
      }
    } catch (err) {
      console.warn('Summons check failed:', err);
    }
  }

  // Prevent voting if trial is ended (but allow during sudden death)
  if (['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(String(trial.status || '').toLowerCase())) return;

  // If not in sudden death mode, prevent voting when jury is full
  if (!trial.suddenDeath && (trial.totalVotes >= (trial.jurySize || BETA_JURY_SIZE) || trial.status === 'ready')) return;

setVotingInProgress(true);

  try {
    const identity = await resolveCanonicalUserIds({
      authUserId: authUser?.id || null,
      hintAppUserId: user?.id || null,
      reason: 'home_quick_vote',
    });
    const canonicalUserId = identity.appUserId || user?.id || null;
    if (!canonicalUserId) {
      alert('We could not confirm your account for this vote. Please sign in again.');
      return;
    }

    const creatorId = trial?.created_by || trial?.creator_id || null;
    const isCreator = !!(canonicalUserId && creatorId && canonicalUserId === creatorId);
    if (isCreator) {
      alert("This is your call. You can't cast a separate jury vote.");
      return;
    }

    // Save vote to database
    const { error: voteError } = await supabase
      .from('votes')
      .insert([
        {
          trial_id: trialId,
          user_id: canonicalUserId,
          vote_side: side
        }
      ])
      .select();

    if (voteError) {
  if (voteError.code === '23505') {
    // User already voted - update local state
    setTrials(prev => prev.map(t => 
      t.id === trialId ? { ...t, hasVoted: true } : t
    ));
    scheduleHomeFeedResync('vote_conflict_already_voted');
    alert('✓ You already voted on this trial! Your original vote stands.');
    return;
  }
  if (voteError.code === '23514' && String(voteError.message || '').includes('creator_jury_vote_forbidden')) {
    alert("This is your call. You can't cast a separate jury vote.");
    return;
  }
  throw voteError;
}
    // Optimistic local update for immediate card feedback.
    setTrials(prev => prev.map(t => {
      if (t.id !== trialId) return t;
      const nextVotesA = side === 'A' ? (t.votesA || 0) + 1 : (t.votesA || 0);
      const nextVotesB = side === 'B' ? (t.votesB || 0) + 1 : (t.votesB || 0);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[home_vote_patch]', {
          trial_id: trialId,
          side,
          nextVotesA,
          nextVotesB,
          nextTotalVotes: (t.totalVotes || 0) + 1,
        });
      }
      return {
        ...t,
        votesA: nextVotesA,
        votesB: nextVotesB,
        totalVotes: (t.totalVotes || 0) + 1,
        hasVoted: true,
        userVoteSide: side,
        userVote: side,
        lastVoteTime: Date.now()
      };
    }));
    recentSelfVoteRef.current.set(trialId, Date.now());
    setRecentVotes(prev => {
      const existing = prev[trialId] || { count: 0 };
      return {
        ...prev,
        [trialId]: {
          count: (existing.count || 0) + 1,
          side,
          source: 'self',
          timestamp: Date.now(),
        },
      };
    });
    scheduleHomeFeedResync('vote_success');
    // 📊 Track vote
    try {
      const isFirstVote = user.trialsJoined === 0;
      trackEvent('vote_cast', { trial_id: trialId, side: side, source: 'home_feed', is_first_vote: isFirstVote });
      if (isFirstVote) {
        trackEvent('first_vote_cast', { trial_id: trialId, side: side });
      }
    } catch {}    

    try {
      await recordReferralMilestone('first_vote_completed');
    } catch (milestoneErr) {
      console.warn('⚠️ Failed to record referral first_vote_completed milestone:', milestoneErr?.message || milestoneErr);
    }

    if (hasCompletedFirstVote === false) {
      await completeFirstVoteAndPromptNextStep();
    }

    // Fetch updated trial from database
    const { data: updatedTrial, error: trialError } = await supabase
      .from('trials')
      .select('*')
      .eq('id', trialId)
      .single();

    if (trialError) throw trialError;
// Early lock if blowout once at least 7 votes and ≥25% margin
{
  const curVotes = (updatedTrial.votes_a ?? 0) + (updatedTrial.votes_b ?? 0);
  const marginPct = Math.abs((updatedTrial.votes_a ?? 0) - (updatedTrial.votes_b ?? 0)) / Math.max(curVotes, 1) * 100;

  if (curVotes >= 7 && marginPct >= 25) {
    const finalizeResult = await maybeFinalizeTrial({ ...trial, id: trialId }, 'vote_blowout');
    if (!finalizeResult?.ok) {
      setVotingInProgress(false);
      return;
    }

    // Update local state
    setTrials(prev => prev.map(t => t.id === trialId ? {
      ...t,
      votesA: updatedTrial.votes_a,
      votesB: updatedTrial.votes_b,
      totalVotes: updatedTrial.total_votes,
      jurorsFilled: updatedTrial.jurors_filled,
      status: 'ended',
      hasVoted: true,
      userVoteSide: side,
      userVote: side
    } : t));

    // Update local state
const coinAward = await awardVoteCoins(50 + bonusCoins, 'trial_vote_blowout');
const newCoinBalance = coinAward.newBalance;

setUser(prev => ({
  ...prev,
  points: prev.points + 10,
  trialsJoined: prev.trialsJoined + 1,
  coins: newCoinBalance
}));

// Also update points
try {
  await supabase
    .from('users')
    .update({ 
      points: user.points + 10,
      updated_at: new Date().toISOString()
    })
    .eq('id', user.id);
} catch (err) {
  console.error('❌ Failed to update points:', err);
}
    
    // ✅ Show push prompt after first vote (DEBUG VERSION)
console.log('🔍 Vote complete - checking if we should show push prompt...');
console.log('  pushPromptShown?', safeStorage.get('pushPromptShown'));
console.log('  Notification.permission:', Notification.permission);
console.log('  pushStatus:', pushStatus);

if (!safeStorage.get('pushPromptShown')) {
  console.log('✅ Condition 1 passed: prompt not shown before');
  
  if (Notification.permission === 'default') {
    console.log('✅ Condition 2 passed: permission is default');
    
    if (pushStatus === 'idle') {
      console.log('✅ Condition 3 passed: pushStatus is idle');
      console.log('🚀 SHOWING PROMPT IN 2 SECONDS...');
      setTimeout(() => {
        console.log('⏰ 2 seconds passed - setting showPushPrompt to true');
        setShowPushPrompt(true);
      }, 2000);
    } else {
      console.log('❌ Condition 3 failed: pushStatus is', pushStatus, 'not idle');
    }
  } else {
    console.log('❌ Condition 2 failed: permission is', Notification.permission);
  }
} else {
  console.log('❌ Condition 1 failed: prompt already shown');
}

    // ✅ REWARD SUMMONER
    if (summonerId && bonusCoins > 0) {
  try {
    const { data: summonerData } = await supabase
      .from('users')
      .select('summons_accepted')
      .eq('id', summonerId)
      .single();
    
    if (summonerData) {
      // Keep summons attribution, but do not mutate coins client-side.
      // Coin awards must flow through server-capped RPCs only.
      await supabase
        .from('users')
        .update({ 
          summons_accepted: (summonerData.summons_accepted || 0) + 1
        })
        .eq('id', summonerId);
      console.log('[coins] summoner_bonus_skipped_client_side', { summoner_id: summonerId });
    }
  } catch (err) {
    console.warn('Failed to reward summoner:', err);
  }
}

        // Show verdict with AI generation
    const votesA = updatedTrial.votes_a || 0;
    const votesB = updatedTrial.votes_b || 0;
    const isTie = votesA === votesB;
    const winner = isTie ? 'Tie' : votesA > votesB ? 'A' : 'B';

    let verdictType, punchline, verdictNumber, winnerPct, aiImage, templatePunchlines;

    // 👉 Handle ties as a "hung jury" and exit early
    if (isTie) {
      verdictType = 'HUNG JURY';
      punchline = 'Deadlocked jury – no winner this time.';
      verdictNumber = Math.floor(Math.random() * 90000) + 10000;
      winnerPct = 50;
      aiImage = getPersistedAiImage(updatedTrial, null);
      templatePunchlines = [
  { text: 'Deadlocked jury — run it back later.', style: 'auto', label: '😵 Deadlock' },
  { text: 'It’s a tie. The internet can’t decide.', style: 'internet', label: '💀 Timeline split' },
  { text: 'Mistrial! Nobody walks away with the W.', style: 'roast', label: '🔥 Mistrial' },
];

      setVerdictData({
        trial: {
          ...trial,
          votesA,
          votesB,
          totalVotes: updatedTrial.total_votes,
          hasVoted: true
        },
        winner,               // 'Tie'
        verdictType,          // 'HUNG JURY'
        punchline,
        verdictNumber,
        winnerPct,
        aiImage,
        aiPunchlines: null,
        templatePunchlines
      });

      setShowSuddenDeath(false);
      if (shouldAutoOpenVerdictModal(trial)) {
        setShowVerdict(true);
      } else {
        setShownVerdicts((prev) => {
          const next = new Set(prev);
          next.add(trial.id);
          return next;
        });
      }
      setVotingInProgress(false);
      return; // ✅ stop here; skip sudden-death path for ties
    }

    // 👇 Normal non-tie path

    // Try AI generation first
    const aiVerdict = await fetchAIVerdict(trialId);

    if (aiVerdict) {
      verdictType = aiVerdict.verdict_type || 'VERDICT';
      punchline =
        aiVerdict.roast_punchline ??
        aiVerdict.roast_punchlines?.[0] ??
        aiVerdict.internet_punchline ??
        aiVerdict.internet_punchlines?.[0] ??
        aiVerdict.sports_punchline ??
        aiVerdict.sports_punchlines?.[0] ??
        'The jury has spoken.';
      winnerPct = Number(aiVerdict.winner_pct) || undefined;
      aiImage = getPersistedAiImage(trial, aiVerdict);
      verdictNumber = Math.floor(Math.random() * 90000) + 10000;
    } else {
      // Fallback to template with 3 diverse options
      const fallback = getVerdictCopy(
        {
          ...trial,
          votesA,
          votesB,
          totalVotes: updatedTrial.total_votes
        },
        winner
      );
      verdictType = fallback.verdictType;
      punchline = fallback.punchline;
      verdictNumber = fallback.verdictNumber;
      winnerPct = fallback.winnerPct;
      aiImage = getPersistedAiImage(trial, null);
      templatePunchlines = fallback.punchlines;
    }

    setVerdictData({
      trial: {
        ...trial,
        votesA,
        votesB,
        totalVotes: updatedTrial.total_votes,
        hasVoted: true
      },
      winner,                // 'A' or 'B'
      verdictType,
      punchline,
      verdictNumber,
      winnerPct,
      aiImage,
      aiPunchlines: aiVerdict
        ? {
            roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
            sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
            internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline]
          }
        : null,
      templatePunchlines: templatePunchlines || null
    });

    setShowSuddenDeath(false);
    if (shouldAutoOpenVerdictModal(trial)) {
      setShowVerdict(true);
    } else {
      setShownVerdicts((prev) => {
        const next = new Set(prev);
        next.add(trial.id);
        return next;
      });
    }
    setVotingInProgress(false);
    return; // ✅ stop here; skip sudden-death path
  }
}

    // Check if we hit sudden death (6-6 tie)
    const isSuddenDeath = updatedTrial.votes_a === 6 && updatedTrial.votes_b === 6;

    if (isSuddenDeath && !trial.suddenDeath) {
      // SUDDEN DEATH MODE!
            
      // Update trial status to 'ready' for sudden death
      await supabase
        .from('trials')
        .update({ status: 'ready' })
        .eq('id', trialId);

      // Update local state
      setTrials(prev => prev.map(t => {
        if (t.id !== trialId) return t;
        return {
          ...t,
          votesA: updatedTrial.votes_a,
          votesB: updatedTrial.votes_b,
          totalVotes: updatedTrial.total_votes,
          jurorsFilled: updatedTrial.jurors_filled,
          hasVoted: true,
          userVoteSide: side,
          userVote: side,
          status: 'ready',
          suddenDeath: true
        };
      }));

      // Show sudden death modal
      setSuddenDeathTrial({
        ...trial,
        votesA: updatedTrial.votes_a,
        votesB: updatedTrial.votes_b,
        totalVotes: updatedTrial.total_votes,
        jurorsFilled: updatedTrial.jurors_filled,
        suddenDeath: true
      });
      setShowSuddenDeath(true);

      // Update user stats
      const coinAward = await awardVoteCoins(50 + bonusCoins, 'trial_vote_sudden_death_entry');
      const newCoinBalance = coinAward.newBalance;
      const newPoints = user.points + 10;
      
      setUser(prev => ({
        ...prev,
        points: newPoints,
        trialsJoined: prev.trialsJoined + 1,
        coins: newCoinBalance
      }));

      try {
        await supabase
          .from('users')
          .update({ 
            points: newPoints,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      } catch (err) {
        console.error('❌ Failed to update points:', err);
      }

      return;
    }

    // Regular vote (not sudden death) or breaking sudden death tie
    if (trial.suddenDeath || updatedTrial.jurors_filled >= (trial.jurySize || BETA_JURY_SIZE)) {
      // Trial is complete - end it
      const finalizeResult = await maybeFinalizeTrial({ ...trial, id: trialId }, 'jury_full_vote');
      if (!finalizeResult?.ok) {
        setVotingInProgress(false);
        return;
      }

      // Update local state
      setTrials(prev => prev.map(t => {
        if (t.id !== trialId) return t;
        return {
          ...t,
          votesA: updatedTrial.votes_a,
          votesB: updatedTrial.votes_b,
          totalVotes: updatedTrial.total_votes,
          jurorsFilled: updatedTrial.jurors_filled,
          hasVoted: true,
          userVoteSide: side,
          userVote: side,
          status: 'ended'
        };
      }));

      // Update user stats
      const coinAward = await awardVoteCoins(50 + bonusCoins, 'trial_vote_jury_full');
      const newCoinBalance = coinAward.newBalance;
      const newPoints = user.points + 10;
      
      setUser(prev => ({
        ...prev,
        points: newPoints,
        trialsJoined: prev.trialsJoined + 1,
        coins: newCoinBalance
      }));

      try {
        await supabase
          .from('users')
          .update({ 
            points: newPoints,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      } catch (err) {
        console.error('❌ Failed to update points:', err);
      }

                /// Show verdict with AI
    setTimeout(async () => {
      const votesA = updatedTrial.votes_a || 0;
      const votesB = updatedTrial.votes_b || 0;
      const isTie = votesA === votesB;

      let winner;
      let verdictType;
      let punchline;
      let verdictNumber;
      let winnerPct;
      let aiImage = getPersistedAiImage(trial, null);
      let templatePunchlines;
      let aiVerdict;
      if (isTie) {
        // 👇 Cheap tie treatment: hung jury / no winner
        winner = 'Tie';
        verdictType = 'HUNG JURY';
        punchline = 'Deadlocked jury – no winner this time.';
        verdictNumber = Math.floor(Math.random() * 90000) + 10000;
        winnerPct = 50;
        templatePunchlines = [
          'Deadlocked jury – run it back later.',
          'It’s a tie. The internet can’t decide.',
          'Mistrial! Nobody walks away with the W.'
        ];
      } else {
        // Normal win path
        winner = votesA > votesB ? 'A' : 'B';

        // Try AI generation
        aiVerdict = await fetchAIVerdict(trialId);

        if (aiVerdict) {
          verdictType = aiVerdict.verdict_type || 'VERDICT';
          punchline =
            aiVerdict.roast_punchline ??
            aiVerdict.roast_punchlines?.[0] ??
            aiVerdict.internet_punchline ??
            aiVerdict.internet_punchlines?.[0] ??
            aiVerdict.sports_punchline ??
            aiVerdict.sports_punchlines?.[0] ??
            'The jury has spoken.';
          winnerPct = Number(aiVerdict.winner_pct) || undefined;
          aiImage = getPersistedAiImage(trial, aiVerdict);
          verdictNumber = Math.floor(Math.random() * 90000) + 10000;
        } else {
          // Fallback to template with 3 diverse options
          const fallback = getVerdictCopy(
            {
              ...trial,
              votesA,
              votesB,
              totalVotes: updatedTrial.total_votes
            },
            winner
          );
          verdictType = fallback.verdictType;
          punchline = fallback.punchline;
          verdictNumber = fallback.verdictNumber;
          winnerPct = fallback.winnerPct;
          aiImage = getPersistedAiImage(trial, null);
          templatePunchlines = fallback.punchlines;
        }

      }

      setVerdictData({
        trial: {
          ...trial,
          votesA,
          votesB,
          totalVotes: updatedTrial.total_votes,
          hasVoted: true
        },
        winner,                     // 'A' | 'B' | 'Tie'
        verdictType,               // 'HUNG JURY' on ties
        punchline,
        verdictNumber,
        winnerPct,
        aiImage,
        aiPunchlines:
          !isTie && aiVerdict
            ? {
                roast: aiVerdict.roast_punchlines || [aiVerdict.roast_punchline],
                sports: aiVerdict.sports_punchlines || [aiVerdict.sports_punchline],
                internet: aiVerdict.internet_punchlines || [aiVerdict.internet_punchline]
              }
            : null,
        templatePunchlines: templatePunchlines || null,
        isTie                     // extra flag if you want it in the modal later
      });

      setShowSuddenDeath(false);
      if (shouldAutoOpenVerdictModal(trial)) {
        setShowVerdict(true);
      } else {
        setShownVerdicts((prev) => {
          const next = new Set(prev);
          next.add(trial.id);
          return next;
        });
      }
    }, 500);

    } else {
      // Jury still filling - just update local state
      setTrials(prev => prev.map(t => {
        if (t.id !== trialId) return t;
        return {
          ...t,
          votesA: updatedTrial.votes_a,
          votesB: updatedTrial.votes_b,
          totalVotes: updatedTrial.total_votes,
          jurorsFilled: updatedTrial.jurors_filled,
          hasVoted: true,
          userVoteSide: side,
          userVote: side
        };
      }));

      // Update user stats
       const coinAward = await awardVoteCoins(50, 'trial_vote_progress');
       const newCoinBalance = coinAward.newBalance;
    const newPoints = user.points + 10;
    
    setUser(prev => ({
      ...prev,
      points: newPoints,
      trialsJoined: prev.trialsJoined + 1,
      coins: newCoinBalance
    }));

    try {
      await supabase
        .from('users')
        .update({ 
          points: newPoints,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
    } catch (err) {
      console.error('❌ Failed to update points:', err);
    }
  }
  } catch (error) {
    console.error('Error saving Quick Trial vote:', error);
    alert('Failed to save vote. Please try again.');
  } finally {
    // 🆕 NEW: Enhanced notification modal trigger
    markSignificantAction('voted');

const shouldShow = shouldShowNotificationPrompt();
if (shouldShow) {
  setTimeout(() => {
    openNotificationPermissionModal('first_vote');
  }, 2000);
}

    setVotingInProgress(false);
  }
}, [trials, user.id, fetchAIVerdict, getVerdictCopy, openNotificationPermissionModal]); // eslint-disable-line react-hooks/exhaustive-deps -- awardVoteCoins/setTrials stable

// ← CORRECT END OF handleQuickTrialVote
// ========= ONBOARDING + SEEDED FEATURED TRIALS =========

// Show first vote modal after user loads (and hasn't completed onboarding)
useEffect(() => {
  // Only show if:
  // 1. User is loaded (not loading)
  // 2. User hasn't completed first vote
  // 3. Not already showing auth modal
  // 4. Not on crew join page
  // 5. User is authenticated (not guest)
  // 6. Database check is complete
  const onCrewJoinPage = window.location.pathname.includes('/crew/join/');
  
  if (!userLoading && 
      !firstVoteLoading && 
      hasCompletedFirstVote === false && // Explicitly false, not null (still loading)
      !showAuthModal && 
      !onCrewJoinPage && 
      authUser) {
    // Small delay to let the app settle
    const timer = setTimeout(() => {
      console.log('🎯 Showing first vote onboarding modal');
      setShowFirstVoteModal(true);
    }, 500);
    return () => clearTimeout(timer);
  }
}, [userLoading, firstVoteLoading, hasCompletedFirstVote, showAuthModal, authUser]);

// Handle onboarding completion
const dismissFirstVoteModal = () => {
  setShowFirstVoteModal(false);
};

const completeFirstVoteAndPromptNextStep = useCallback(async () => {
  if (hasCompletedFirstVote === true) return;

  console.log('✅ first vote completed');
  safeStorage.set('gweffit_first_vote_completed', 'true');
  setHasCompletedFirstVote(true);
  setShowFirstVoteModal(false);
  setShowFirstVoteNextStepModal(true);

  if (!user?.id) return;

  try {
    const { error } = await supabase
      .from('users')
      .update({
        has_completed_first_vote: true,
        onboarding_completed_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (error) {
      console.error('Error updating first vote status:', error);
    } else {
      console.log('✅ First vote status saved to database');
    }
  } catch (err) {
    console.error('Error saving first vote completion:', err);
  }
}, [hasCompletedFirstVote, user?.id]);

const loadReferralStats = useCallback(async () => {
  if (!authUser?.id || !user?.id) return;

  setReferralStatsLoading(true);
  try {
    const { data, error } = await supabase.rpc('get_my_referral_stats');
    if (error) throw error;

    const row = Array.isArray(data) ? data[0] : data;
    setReferralStats({
      totalInvites: Number(row?.total_invites || 0),
      joinedCount: Number(row?.joined_count || 0),
      firstVoteConversions: Number(row?.first_vote_conversions || 0),
      totalReferralRewardsEarned: Number(row?.total_referral_rewards_earned || 0),
    });
  } catch (err) {
    console.warn('⚠️ Failed to load referral stats:', err?.message || err);
  } finally {
    setReferralStatsLoading(false);
  }
}, [authUser?.id, user?.id]);

useEffect(() => {
  if (view !== 'profile') return;
  if (!authUser?.id || !user?.id) return;
  loadReferralStats();
}, [view, authUser?.id, user?.id, loadReferralStats]);

useEffect(() => {
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  const inviterUserId = params.get('ref');
  const sourceType = params.get('ref_src');
  const sourceId = params.get('ref_id');

  if (!inviterUserId || !sourceType) return;

  const signature = [
    window.location.pathname,
    inviterUserId,
    sourceType,
    sourceId || '',
    user?.id || 'anon',
  ].join('|');

  if (processedReferralSignatureRef.current === signature) return;
  processedReferralSignatureRef.current = signature;

  let cancelled = false;
  (async () => {
    try {
      await trackReferralAttribution({
        inviterUserId,
        sourceType,
        sourceId,
        invitedUserId: user?.id || null,
      });
    } catch (err) {
      if (!cancelled) {
        console.warn('⚠️ Failed to capture referral attribution:', err?.message || err);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [user?.id]);

useEffect(() => {
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  const requestedAuthMode = params.get('auth');
  const requestedAuthMessage = params.get('auth_message');
  const requestedAuthContext = params.get('auth_context');

  if (!requestedAuthMode && !requestedAuthMessage && !requestedAuthContext) return;

  if (!authUser?.id) {
    setAuthMode(requestedAuthMode === 'login' ? 'login' : 'signup');
    setAuthError('');
    setAuthMessage(requestedAuthMessage || 'Enter your email and we’ll send a 6-digit code to keep going.');
    setAuthContext(requestedAuthContext || 'general');
    setShowAuthModal(true);
  }

  params.delete('auth');
  params.delete('auth_message');
  params.delete('auth_context');
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, '', nextUrl);
}, [authUser?.id]);

useEffect(() => {
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  const postAuthAction = params.get('post_auth_action');
  if (!postAuthAction) return;
  if (!authUser?.id || !user?.id) return;

  if (postAuthAction === 'create_crew') {
    setShowCreateCrewModal(true);
  }

  params.delete('post_auth_action');
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, '', nextUrl);
}, [authUser?.id, user?.id]);

const createFeaturedTrialAndOpen = async (templateOverride = null, seedSource = 'featured_v1') => {
  const template = templateOverride || null;
  const openingKey = String(template?.id || template?.key || template?.title || seedSource || 'featured');
  const verifyStarterTrialTarget = async (trialId) => {
    const attempts = 5;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      const { data: targetTrial, error: targetTrialError } = await withTimeout(
        supabase
          .from('trials')
          .select('id,status,is_seeded,seed_source')
          .eq('id', trialId)
          .maybeSingle(),
        4000,
        `starter_trial_verify_timeout_${attempt}`
      );
      if (!targetTrialError && targetTrial?.id) {
        const statusNorm = String(targetTrial.status || '').toLowerCase();
        const isOpen = LIVE_TRIAL_STATUSES.has(statusNorm);
        console.log('[starter_trial_target_verified]', {
          trial_id: trialId,
          attempt,
          status: statusNorm || null,
          is_seeded: Boolean(targetTrial.is_seeded),
          seed_source: targetTrial.seed_source || null,
          open_for_vote: isOpen,
        });
        return { ok: true, row: targetTrial };
      }
      if (attempt < attempts) {
        await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
      }
    }
    return { ok: false };
  };
  try {
    if (!authUser?.id || !user?.id) {
      setShowAuthModal(true);
      return { ok: false, reason: 'auth_required' };
    }
    const { data: { session } = {} } = await supabase.auth.getSession();
    const accessToken = session?.access_token || null;
    if (!accessToken) {
      setShowAuthModal(true);
      return { ok: false, reason: 'auth_required' };
    }
    const templateKey = String(template?.key || '').trim();
    const bankItemId = String(template?.id || '').trim();
    console.log('[starter_trial_open_requested]', {
      opening_key: openingKey,
      template_key: templateKey || null,
      bank_item_id: bankItemId || null,
      seed_source: seedSource,
      current_feed_scope: homeFeedScope,
    });
    const response = await withTimeout(fetch(`${supabaseUrl}/functions/v1/create-featured-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        template_key: templateKey || null,
        bank_item_id: bankItemId || null,
        seed_source: seedSource,
      }),
    }), 12000, 'create_featured_trial_timeout');
    const data = await response.json().catch(() => null);
    console.log('[starter_trial_backend_response]', {
      opening_key: openingKey,
      ok: response.ok,
      status: response.status,
      payload: data ? {
        ok: Boolean(data?.ok),
        trial_id: data?.trial_id || null,
        reused: typeof data?.reused === 'boolean' ? data.reused : null,
        request_id: data?.request_id || null,
        build_id: data?.build_id || null,
        error: data?.error || null,
        dedupe_key: data?.dedupe_key || null,
      } : null,
    });
    if (!response.ok) {
      console.warn('[featured] create_featured_trial_http_failed', {
        status: response.status,
        error: data?.error || null,
        request_id: data?.request_id || null,
      });
      throw new Error(data?.error || `create_featured_trial_http_${response.status}`);
    }
    if (!data?.ok || !data?.trial_id) {
      throw new Error(data?.error || 'create_featured_trial_failed');
    }
    const verifiedTarget = await verifyStarterTrialTarget(data.trial_id);
    if (!verifiedTarget.ok) {
      console.warn('[starter_trial_navigation_failed]', {
        opening_key: openingKey,
        trial_id: data.trial_id,
        reason: 'target_not_queryable',
      });
      throw new Error('starter_trial_target_not_ready');
    }
    try {
      localStorage.setItem(STARTER_TRIAL_RETURN_SCOPE_KEY, JSON.stringify({
        scope: 'all',
        trialId: data.trial_id,
        storedAt: Date.now(),
      }));
    } catch {}
    console.log('[starter_trial_open_succeeded]', {
      opening_key: openingKey,
      trial_id: data.trial_id,
      reused: Boolean(data?.reused),
      seed_source: seedSource,
      navigate_to: `/t/${data.trial_id}?source=starter`,
    });
    console.log('[starter_trial_navigation_started]', {
      opening_key: openingKey,
      trial_id: data.trial_id,
      reused: Boolean(data?.reused),
      seed_source: seedSource,
    });
    navigate(`/t/${data.trial_id}?source=starter`);
    return { ok: true, trialId: data.trial_id, reused: Boolean(data?.reused) };
  } catch (error) {
    console.error('❌ createFeaturedTrialAndOpen failed:', error);
    console.warn('[starter_trial_open_failed]', {
      opening_key: openingKey,
      seed_source: seedSource,
      error: error?.message || String(error),
    });
    return { ok: false, reason: error?.message || 'create_featured_trial_failed' };
  }
};

const handleOnboardingVoteStart = async () => {
  const liveTrial = (trials || []).find((trial) => {
    const status = String(trial?.status || '').toLowerCase();
    return LIVE_TRIAL_STATUSES.has(status);
  });
  if (liveTrial?.id) {
    navigate(`/t/${liveTrial.id}`);
    return;
  }
  const result = await createFeaturedTrialAndOpen(null, 'onboarding_v1');
  if (!result?.ok && result?.reason !== 'auth_required') {
    alert('Could not open a starter trial right now. Please try again.');
  }
};

const handleProtectedCreateTrial = useCallback(() => {
  if (authUser?.id && user?.id) {
    navigate('/create');
    return;
  }

  safeStorage.set('gweffit_return_url', '/create');
  setAuthContext('create_trial');
  setAuthMode('signup');
  setAuthError('');
  setAuthMessage('Enter your email and we’ll send a 6-digit code to post your trial.');
  setShowAuthModal(true);
}, [authUser?.id, navigate, user?.id]);

const handleOnboardingCreateStart = async () => {
  handleProtectedCreateTrial();
};

const handleProtectedCreateCrew = useCallback(() => {
  if (authUser?.id && user?.id) {
    setShowCreateCrewModal(true);
    return;
  }

  safeStorage.set('gweffit_return_url', '/?post_auth_action=create_crew');
  setAuthContext('create_crew');
  setAuthMode('signup');
  setAuthError('');
  setAuthMessage('Enter your email and we’ll send a 6-digit code to start your crew.');
  setShowAuthModal(true);
}, [authUser?.id, user?.id]);

// Handle notification permission granted
const handleNotificationPermissionGranted = async () => {
  if (!WEB_PUSH_ENABLED || isNativeApp()) {
    console.log('[push] Web push disabled or native platform; skipping web setup.');
    return;
  }
  const userId = user?.id || safeStorage.get('gweffit_user_id');
  if (!userId) {
    console.error('No user ID found for notification setup');
    return;
  }
  
  try {
    const result = await setupPushNotifications(userId);
    
    if (result.success) {
      console.log('✅ Push notifications configured successfully (no coin award).');
    } else {
      console.warn('Push notification setup incomplete:', result.error);
    }
  } catch (error) {
    console.error('Error in notification setup:', error);
  }
};

const handleCrewChallengeVoteSuccess = async () => {
  await refreshAtRiskCrews();
};

const handleCrewChallengeVoteNow = async () => {
  await crewChallengeVoteNow({
    supabase,
    crewsAtRisk,
    navigate,
    setView,
    setShowSettingsPanel,
  });
};

// eslint-disable-next-line react-hooks/exhaustive-deps
const handleVote = async (trialId, side) => {
  const trial = trials.find(t => t.id === trialId);
  if (trial?.type === 'quick') {
    handleQuickTrialVote(trialId, side);
    return;
  }
// 🚫 Block creators from voting on their own Decide-for-me trials
const identity = await resolveCanonicalUserIds({
  authUserId: authUser?.id || null,
  hintAppUserId: user?.id || null,
  reason: 'home_vote',
});
const canonicalUserId = identity.appUserId || user?.id || null;
if (!canonicalUserId) {
  alert('We could not confirm your account for this vote. Please sign in again.');
  return;
}

const creatorId = trial?.created_by || trial?.creator_id; // ✅ app user id first
const isCreator = !!(canonicalUserId && creatorId && canonicalUserId === creatorId);

if (isCreator) {
  alert("This is your call. You can't cast a separate jury vote.");
  return;
}
  setVotingInProgress(true);

  try {
    const { error: voteError } = await supabase
      .from('votes')
      .insert([
        {
          trial_id: trialId,
          user_id: canonicalUserId,
          vote_side: side
        }
      ])
      .select();

    if (voteError) {
      if (voteError.code === '23505') {
        setTrials(prev => prev.map(t =>
          t.id === trialId ? { ...t, hasVoted: true } : t
        ));
        scheduleHomeFeedResync('vote_conflict_already_voted');
        alert('You already voted on this trial!');
        return;
      }
      if (voteError.code === '23514' && String(voteError.message || '').includes('creator_jury_vote_forbidden')) {
        alert("This is your call. You can't cast a separate jury vote.");
        return;
      }
      throw voteError;
    }

    setTrials(prev => prev.map(t => {
      if (t.id !== trialId) return t;
      const nextVotesA = side === 'A' ? (t.votesA || 0) + 1 : (t.votesA || 0);
      const nextVotesB = side === 'B' ? (t.votesB || 0) + 1 : (t.votesB || 0);
      if (process.env.NODE_ENV !== 'production') {
        console.log('[home_vote_patch]', {
          trial_id: trialId,
          side,
          nextVotesA,
          nextVotesB,
          nextTotalVotes: (t.totalVotes || 0) + 1,
        });
      }
      return {
        ...t,
        votesA: nextVotesA,
        votesB: nextVotesB,
        totalVotes: (t.totalVotes || 0) + 1,
        hasVoted: true,
        userVoteSide: side,
        userVote: side,
        lastVoteTime: Date.now()
      };
    }));
    recentSelfVoteRef.current.set(trialId, Date.now());
    setRecentVotes(prev => {
      const existing = prev[trialId] || { count: 0 };
      return {
        ...prev,
        [trialId]: {
          count: (existing.count || 0) + 1,
          side,
          source: 'self',
          timestamp: Date.now(),
        },
      };
    });
    scheduleHomeFeedResync('vote_success');

     const coinAward = await awardVoteCoins(50, 'trial_vote_standard');
     const newCoinBalance = coinAward.newBalance;
    const newPoints = user.points + 10;

    // 🔥 Update streak for actual activity (voting)
    const streakResult = await updateStreakForActivity(canonicalUserId);

    setUser(prev => ({
      ...prev,
      points: newPoints,
      trialsJoined: prev.trialsJoined + 1,
      coins: newCoinBalance,
      streak: streakResult.updated ? streakResult.streak : prev.streak
    }));

    try {
      await supabase
        .from('users')
        .update({
          points: newPoints,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
    } catch (err) {
      console.error('❌ Failed to update points:', err);
    }

    if (hasCompletedFirstVote === false) {
      await completeFirstVoteAndPromptNextStep();
    }
// Track vote and potentially show notification prompt
    markSignificantAction('voted');
    
    console.log('🔔 Checking shouldShowNotificationPrompt...');
    const shouldShow = shouldShowNotificationPrompt();
    console.log('🔔 shouldShowNotificationPrompt returned:', shouldShow);
    
    if (shouldShow) {
      console.log('🔔 Setting timeout for 2 seconds...');
      setTimeout(() => {
        console.log('🔔 Timeout fired! Setting modal to true...');
        openNotificationPermissionModal('first_vote');
        console.log('🔔 Modal state should now be true');
      }, 2000);
    }
    const pendingCrewChallenge = peekCrewDailyChallengePending();
    if (pendingCrewChallenge) {
      const consumed = consumeCrewDailyChallengePending();
      if (consumed?.crew_name) {
        alert(`✅ You helped keep ${consumed.crew_name} alive today.`);
      }
      await refreshAtRiskCrews();
    }
  } catch (error) {
    console.error('Error saving vote:', error);
    alert('Failed to save vote. Please try again.');
  } finally {
    setVotingInProgress(false);
  }
};
// Add this function with your other handler functions
// (Look for functions like handleVote, downloadMeme, etc.)

const awardVoteCoins = async (requestedAmount, reason = 'trial_vote') => {
  const fallbackAmount = Math.max(Number(requestedAmount || 0), 0);
  try {
    const { data, error } = await supabase.rpc('award_vote_coins', {
      p_amount: fallbackAmount,
      p_reason: reason,
    });
    if (error) throw error;

    const awarded = Number(data?.awarded ?? 0);
    const newBalance = Number(data?.new_balance ?? user.coins ?? 0);
    const remainingToday = Number(data?.remaining_today ?? 0);
    console.log('[coins] award_vote_coins', { requested: fallbackAmount, awarded, new_balance: newBalance, remaining_today: remainingToday, reason });
    return { awarded, newBalance };
  } catch (error) {
    console.warn('[coins] award_vote_coins_failed_no_award', {
      reason,
      requested: fallbackAmount,
      message: error?.message || String(error),
    });
    return { awarded: 0, newBalance: Number(user?.coins || 0) };
  }
};

// 🆕 Helper function to update coins in both database and cache
const updateCoins = async (userId, newCoinBalance) => {
  try {
    // Update database
    await supabase
      .from('users')
      .update({
        coins: newCoinBalance,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    // Update cache
    const cachedUser = safeStorage.get('gweffit_user_cache');
    if (cachedUser) {
      const parsed = JSON.parse(cachedUser);
      parsed.coins = newCoinBalance;
      parsed.updated_at = new Date().toISOString();
      safeStorage.set('gweffit_user_cache', JSON.stringify(parsed));
    }

    console.log('✅ Coins updated:', newCoinBalance);
    return true;
  } catch (error) {
    console.error('❌ Failed to update coins:', error);
    return false;
  }
};

// 🔥 Helper function to update streak on actual activity (vote or create trial)
  const buyAvatar = async (avatarId) => {
    const avatar = AVATARS[avatarId];
    
    // Check preconditions
    if (user.ownedAvatars.includes(avatarId)) return;
    if (user.coins < avatar.price) return;

    const newCoinBalance = user.coins - avatar.price;
    const newOwnedAvatars = [...user.ownedAvatars, avatarId];

    // Update local state
    setUser(prev => ({
      ...prev,
      coins: newCoinBalance,
      ownedAvatars: newOwnedAvatars,
      selectedAvatar: avatarId
    }));

    setPurchaseSuccess(avatar);
    setTimeout(() => setPurchaseSuccess(null), 3000);

    // 🆕 CRITICAL: Persist to database and cache
const success = await updateCoins(user.id, newCoinBalance);

if (success) {
  // Also update selected avatar
  try {
    await supabase
      .from('users')
      .update({ 
        selected_avatar: avatarId,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);
    
    console.log('✅ Avatar purchase saved to database');
  } catch (err) {
    console.error('❌ Failed to save avatar selection:', err);
  }
} else {
  // Rollback on error
  setUser(prev => ({
    ...prev,
    coins: prev.coins + avatar.price,
    ownedAvatars: prev.ownedAvatars.filter(id => id !== avatarId),
    selectedAvatar: prev.selectedAvatar === avatarId ? 'default' : prev.selectedAvatar
  }));
  alert('Purchase failed. Please try again.');
}
  };

  const equipAvatar = (avatarId) => {
    setUser(prev => (prev.ownedAvatars.includes(avatarId) ? { ...prev, selectedAvatar: avatarId } : prev));
  };

  
  const SuddenDeathModal = () => {
    if (!suddenDeathTrial) return null;

    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
        <div className="max-w-xl w-full">
          <div className="bg-gradient-to-br from-red-600 via-orange-600 to-yellow-600 rounded-3xl p-8 text-white border-4 border-yellow-400 animate-pulse-slow">
            <div className="text-center">
              <div className="text-8xl mb-4 animate-bounce">🔥</div>
              <h2 className="text-5xl font-black mb-4 text-yellow-300">SUDDEN DEATH</h2>
              <div className="text-2xl font-bold mb-4">THE COURT IS DEADLOCKED</div>
              <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-4">
                <div className="text-4xl font-black mb-2">6 - 6</div>
                <div className="text-lg">Perfect Split. First to 7 votes WINS!</div>
              </div>
              <div className="text-xl mb-2 font-bold">{suddenDeathTrial.title}</div>
              <div className="text-lg mb-6 italic">
                "You're the tiebreaker. Choose wisely..."
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    handleQuickTrialVote(suddenDeathTrial.id, 'A');
                  }}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-300 hover:from-yellow-300 hover:to-yellow-200 text-orange-900 px-8 py-4 rounded-xl font-black text-xl transition-all transform hover:scale-105"
                >
                  {suddenDeathTrial.sideA}
                </button>
                <button
                  onClick={() => {
                    handleQuickTrialVote(suddenDeathTrial.id, 'B');
                  }}
                  className="w-full bg-gradient-to-r from-orange-400 to-orange-300 hover:from-orange-300 hover:to-orange-200 text-orange-900 px-8 py-4 rounded-xl font-black text-xl transition-all transform hover:scale-105"
                >
                  {suddenDeathTrial.sideB}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

   // Watch Quick Trials for sudden-death or verdict
useEffect(() => {
  trials.forEach(trial => {
    if (shownVerdicts.has(trial.id)) return;
    if (!trial.hasVoted) return;
    
    if (trial.type === 'quick' && trial.status === 'ready' && trial.suddenDeath && !showSuddenDeath) {
      setSuddenDeathTrial(trial);
      setShowSuddenDeath(true);
    } else if (trial.type === 'quick' && trial.status === 'ready' && !trial.suddenDeath && !showVerdict) {
      const winner = trial.votesA === trial.votesB ? 'Tie' : trial.votesA > trial.votesB ? 'A' : 'B';
      const { verdictType, punchline, verdictNumber, } = getVerdictCopy(trial, winner);

      setVerdictData({
        trial,
        winner,
        verdictType,
        punchline,
        verdictNumber,
      });
      if (shouldAutoOpenVerdictModal(trial)) {
        setShowVerdict(true);
      } else {
        setShownVerdicts((prev) => {
          const next = new Set(prev);
          next.add(trial.id);
          return next;
        });
      }
    }
  });
}, [trials, showSuddenDeath, showVerdict, shownVerdicts, getVerdictCopy, shouldAutoOpenVerdictModal]);

  const AvatarShopModal = () => {
    const tierColors = {
  free: 'from-slate-700 to-slate-600 border-slate-500',
  common: 'from-green-900 to-green-800 border-green-600',
  rare: 'from-blue-900 to-blue-800 border-blue-600',
  epic: 'from-purple-900 to-purple-800 border-purple-600',
  legendary: 'from-yellow-900 to-orange-900 border-yellow-600'
};

    const tierLabels = {
      free: 'FREE',
      common: 'COMMON',
      rare: 'RARE',
      epic: 'EPIC',
      legendary: 'LEGENDARY'
    };

    const groupedAvatars = {
      free: Object.values(AVATARS).filter(a => a.tier === 'free'),
      common: Object.values(AVATARS).filter(a => a.tier === 'common'),
      rare: Object.values(AVATARS).filter(a => a.tier === 'rare'),
      epic: Object.values(AVATARS).filter(a => a.tier === 'epic'),
      legendary: Object.values(AVATARS).filter(a => a.tier === 'legendary')
    };

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-3xl p-8 max-w-2xl w-full relative max-h-screen overflow-y-auto border-2 border-slate-700">
          <button
            onClick={() => setShowAvatarShop(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-slate-300 z-10"
          >
            <X size={24} />
          </button>

          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎨</div>
            <h2 className="text-3xl font-black text-slate-50 mb-2">Avatar Shop</h2>
            <p className="text-slate-300">Unlock avatars with your coins</p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold mt-3">
              💰 Your Balance: {user.coins.toLocaleString()} Coins
            </div>
          </div>

          {/* Purchase success notification */}
          {purchaseSuccess && (
            <div className="mb-4 bg-green-500 text-white rounded-xl p-4 flex items-center gap-3 animate-bounce-in">
              <div className="text-3xl">{purchaseSuccess.emoji}</div>
              <div className="flex-1">
                <div className="font-bold">Avatar Unlocked!</div>
                <div className="text-sm">You now own {purchaseSuccess.name}</div>
              </div>
              <div className="text-2xl">✅</div>
            </div>
          )}

          {Object.entries(groupedAvatars).map(([tier, avatars]) => (
            <div key={tier} className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className={`text-xs font-black px-3 py-1 rounded-full ${
                  tier === 'free' ? 'bg-slate-600 text-slate-200'
                    : tier === 'common' ? 'bg-green-200 text-green-700'
                      : tier === 'rare' ? 'bg-blue-200 text-blue-700'
                        : tier === 'epic' ? 'bg-purple-200 text-purple-700'
                          : 'bg-yellow-200 text-orange-700'
                }`}>
                  {tierLabels[tier]}
                </div>
              </div>

              <div className="grid grid-cols-5 gap-3">
                {avatars.map(avatar => {
                  const isOwned = user.ownedAvatars.includes(avatar.id);
                  const isEquipped = user.selectedAvatar === avatar.id;
                  const canAfford = user.coins >= avatar.price;

                  return (
                    <button
                      key={avatar.id}
                      onClick={() => isOwned ? equipAvatar(avatar.id) : buyAvatar(avatar.id)}
                      disabled={!isOwned && !canAfford}
                      className={`bg-gradient-to-br ${tierColors[tier]} rounded-xl p-4 border-2 transition-all ${
                        isEquipped ? 'ring-4 ring-purple-500'
                          : isOwned ? 'hover:scale-105 cursor-pointer'
                            : canAfford ? 'hover:scale-105 cursor-pointer'
                              : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="text-5xl mb-2">{avatar.emoji}</div>
                      <div className="text-sm font-bold text-slate-50 mb-1">{avatar.name}</div>
                      {isEquipped ? (
                        <div className="text-xs font-bold text-purple-600">EQUIPPED ✓</div>
                      ) : isOwned ? (
                        <div className="text-xs font-bold text-green-600">OWNED</div>
                      ) : (
                        <div className={`text-xs font-bold ${canAfford ? 'text-slate-300' : 'text-red-600'}`}>
                          {avatar.price === 0 ? 'FREE' : `${avatar.price.toLocaleString()} 💰`}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-6 bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-blue-800">
                <div className="font-bold mb-1">How it works:</div>
                Click an avatar to purchase (if you have enough coins) or equip (if you already own it). Your selected avatar appears on your profile!
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowAvatarShop(false)}
            className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition-all"
          >
            Close Shop
          </button>
        </div>
      </div>
    );
  };

  const BuyCoinsModal = () => (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-3xl p-8 max-w-2xl w-full relative max-h-screen overflow-y-auto border-2 border-slate-700">
        <button
          onClick={() => setShowBuyCoinsModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-slate-300"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <div className="text-6xl mb-4">💰</div>
          <h2 className="text-3xl font-black text-slate-50 mb-2">Buy GweffIT Coins</h2>
          <p className="text-slate-300 flex items-center justify-center gap-2">
            Spend on avatars and future upgrades
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold" title="Larger packs = better value per coin">
              💡 Bigger = Better Value
            </span>
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <button className="w-full bg-slate-700 hover:bg-slate-600 rounded-xl p-4 border-2 border-gray-200 hover:border-purple-400 transition-all">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="font-black text-lg text-slate-50">5,000 Coins</div>
                <div className="text-sm text-slate-400">Starter Pack</div>
              </div>
              <div className="text-2xl font-black text-purple-600">$4.99</div>
            </div>
          </button>

          <button className="w-full bg-purple-900/20 hover:bg-purple-900/30 rounded-xl p-4 border-2 border-purple-400 transition-all relative">
            <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              POPULAR
            </div>
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="font-black text-lg text-slate-50">12,000 Coins</div>
                <div className="text-sm text-green-600 font-bold">+20% Bonus!</div>
              </div>
              <div className="text-2xl font-black text-purple-600">$9.99</div>
            </div>
          </button>

          <button className="w-full bg-slate-700 hover:bg-slate-600 rounded-xl p-4 border-2 border-gray-200 hover:border-purple-400 transition-all relative">
            <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-purple-900 text-xs font-bold px-2 py-1 rounded-full">
              💎 BEST VALUE
            </div>
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="font-black text-lg text-slate-50">35,000 Coins</div>
                <div className="text-sm text-green-600 font-bold">+40% Bonus!</div>
              </div>
              <div className="text-2xl font-black text-purple-600">$24.99</div>
            </div>
          </button>

          <button className="w-full bg-slate-700 hover:bg-slate-600 rounded-xl p-4 border-2 border-gray-200 hover:border-purple-400 transition-all">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="font-black text-lg text-slate-50">85,000 Coins</div>
                <div className="text-sm text-green-600 font-bold">+70% Bonus!</div>
              </div>
              <div className="text-2xl font-black text-purple-600">$49.99</div>
            </div>
          </button>

          <button className="w-full bg-gradient-to-r from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 rounded-xl p-4 border-2 border-purple-300 transition-all">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="font-black text-lg text-slate-50">200,000 Coins</div>
                <div className="text-sm text-green-600 font-bold">+100% Bonus! 🔥</div>
              </div>
              <div className="text-2xl font-black text-purple-600">$99.99</div>
            </div>
          </button>
        </div>

        <div className="bg-blue-900/20 border-2 border-blue-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-blue-300">
              <div className="font-bold mb-1">Demo Mode</div>
              You earn coins by voting! This purchase flow will be activated once we integrate Stripe payments. For now, just keep voting to earn more coins.
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="text-xs text-slate-400 mb-4">
            Secure payments via Stripe • 30-day money back guarantee
          </div>
          <button
            onClick={() => setShowBuyCoinsModal(false)}
            className="text-purple-600 font-bold hover:text-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const PremiumModal = () => (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 rounded-3xl p-8 max-w-md w-full text-white relative">
        <button
          onClick={() => setShowPremiumModal(false)}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <Crown size={64} className="mx-auto mb-4 text-yellow-200" />
          <h2 className="text-3xl font-black mb-2">Upgrade to Premium</h2>
          <p className="text-yellow-100">Unlock legendary trial features</p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="bg-white/20 rounded-xl p-4 flex items-start gap-3">
            <Sparkles className="text-yellow-200 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold">AI Lawyer Arguments</div>
              <div className="text-sm text-yellow-100">Generate hilarious case arguments</div>
            </div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 flex items-start gap-3">
            <Crown className="text-yellow-200 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold">Celebrity Judge Voices</div>
              <div className="text-sm text-yellow-100">Get verdicts from parody judges</div>
            </div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 flex items-start gap-3">
            <Zap className="text-yellow-200 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold">Custom Verdict Cards</div>
              <div className="text-sm text-yellow-100">Design epic shareable memes</div>
            </div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 flex items-start gap-3">
            <TrendingUp className="text-yellow-200 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold">Advanced Analytics</div>
              <div className="text-sm text-yellow-100">Geo/time trends & aggregated insights</div>
            </div>
          </div>
          <div className="bg-white/20 rounded-xl p-4 flex items-start gap-3">
            <Users className="text-yellow-200 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold">Custom Username</div>
              <div className="text-sm text-yellow-100">Reveal yourself or stay anonymous</div>
            </div>
          </div>
        </div>

        <div className="text-center mb-6">
          <div className="text-4xl font-black mb-2">$4.99<span className="text-lg">/month</span></div>
          <div className="text-yellow-100 text-sm">Cancel anytime • 7-day free trial</div>
        </div>

        <button className="w-full bg-white text-orange-600 py-4 rounded-xl font-black text-lg hover:bg-yellow-900/20 transition-all">
          Join Premium Waitlist
        </button>

        <p className="text-center text-xs text-yellow-100 mt-4">
          Coming soon! Be first to know when we launch.
        </p>
      </div>
    </div>
  );

 
  // Updated TrialCard component - Add this to your App.js
// Replace your existing TrialCard component with this

// eslint-disable-next-line react-hooks/exhaustive-deps
const TrialCard = useMemo(() => createTrialCard({
  votingInProgress,
  BETA_JURY_SIZE,
  calculateOdds,
  user,
  canSeeQuarantinedTrials,
  recentVotes,
  formatTime,
  finalizeInProgressTrials,
  finalizeErrorTrials,
  animatingVotes,
  handleVote,
  setFlippedVerdicts,
  flippedVerdicts,
  getStableSpicyPunchline,
  getVerdictCopy,
  setVerdictData,
  setShowVerdict,
}), [ // eslint-disable-line react-hooks/exhaustive-deps -- handleVote stable enough for memo
  votingInProgress,
  calculateOdds,
  user,
  canSeeQuarantinedTrials,
  recentVotes,
  formatTime,
  finalizeInProgressTrials,
  finalizeErrorTrials,
  animatingVotes,
  handleVote,
  setFlippedVerdicts,
  flippedVerdicts,
  getStableSpicyPunchline,
  getVerdictCopy,
  setVerdictData,
  setShowVerdict,
]);
const activeVisibleTrials = Array.from(
  (Array.isArray(trials) ? trials : []).reduce((acc, trial) => {
    if (!LIVE_TRIAL_STATUSES.has(String(trial?.status || '').toLowerCase())) return acc;
    const stableId = String(trial?.id || '');
    if (!stableId || acc.has(stableId)) return acc;
    acc.set(stableId, trial);
    return acc;
  }, new Map()).values()
);
const liveNowTrials = [...activeVisibleTrials]
  .sort((a, b) => {
    const aStatus = String(a?.status || '').toLowerCase();
    const bStatus = String(b?.status || '').toLowerCase();
    const aSudden = aStatus === 'sudden_death_open' ? 1 : 0;
    const bSudden = bStatus === 'sudden_death_open' ? 1 : 0;
    if (aSudden !== bSudden) return bSudden - aSudden;
    const aTime = Number(a?.timeLeft ?? Infinity);
    const bTime = Number(b?.timeLeft ?? Infinity);
    if (aSudden && bSudden && aTime !== bTime) return aTime - bTime;
    return Number(b?.createdAt || 0) - Number(a?.createdAt || 0);
  })
  .slice(0, 8);
const recentTrialsBeforeDedupe = [...activeVisibleTrials]
  .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0))
  .slice(0, 8);
const liveNowIds = new Set(liveNowTrials.map((trial) => String(trial?.id || '')));
const recentTrials = recentTrialsBeforeDedupe.filter((trial) => !liveNowIds.has(String(trial?.id || '')));
if (process.env.NODE_ENV !== 'production') {
  console.log('[home] dedupe_live_recent', {
    liveCount: liveNowTrials.length,
    recentCountBefore: recentTrialsBeforeDedupe.length,
    recentCountAfter: recentTrials.length,
    removedCount: recentTrialsBeforeDedupe.length - recentTrials.length,
  });
}

const crewsAtRisk = Array.isArray(atRiskCrews) ? atRiskCrews : [];
const resolvedHomeCrewCount = Math.max(Number(homeCrewCount || 0), crewsAtRisk.length);
const showCrewStreakRiskBanner = !!user?.id && crewsAtRisk.length > 0;
const crewRiskBannerLabel = crewsAtRisk.length <= 2
  ? crewsAtRisk.map((c) => c.crew_name).filter(Boolean).join(', ')
  : `${crewsAtRisk.length} crews`;

 return (
  <div
    className="min-h-screen bg-slate-900"
    style={{ overflowX: 'hidden', width: '100%' }}
    onTouchStart={handleHomeTouchStart}
    onTouchMove={handleHomeTouchMove}
    onTouchEnd={handleHomeTouchEnd}
    onTouchCancel={handleHomeTouchEnd}
  >
  {/* ADD THIS DEBUG */}
    {userLoading ? (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="text-6xl mb-4">⚖️</div>
          <div className="text-2xl font-black text-slate-50 mb-2">Loading GweffIT...</div>
          <div className="text-slate-400">Setting up your anonymous jury profile</div>
           {/* ADD THIS */}
          </div>
      </div>
    ) : (
      <>
        {/* 🆕 Crew Join Check */}
        {crewJoinToken && !suppressCrewJoin ? (
          <CrewJoinPage
            token={crewJoinToken}
            currentUser={user}
            authUser={authUser}  // Pass Supabase auth user to check if truly authenticated
            onAuthRequired={() => {
              console.log('🔐 [App] Opening auth in signup mode');
              setAuthContext('join_crew');
              setAuthMode('signup');
              setAuthError('');
              setAuthMessage('Enter your email and we’ll send a 6-digit code to join this crew.');
              setShowAuthModal(true);
            }}
            onJoinSuccess={(crew) => {
              safeStorage.remove('pendingCrewJoinToken'); // defensive
              setCrewJoinToken(null);
              setSuppressCrewJoin(true);
              if (crew?.id) {
                safeStorage.set('crewJustJoinedId', crew.id);
              }
              setCrewRefreshKey((v) => v + 1);
              navigate('/profile', { replace: true });
            }}
          />
        ) : (
          <>
  {guestClaimPending && authUser?.id && user?.id && (
    <div className="max-w-6xl mx-auto px-2 md:px-4 pt-3">
      <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/8 px-3 py-2 text-sm text-cyan-100">
        <span className="font-semibold">Finishing setup…</span>{' '}
        We&apos;re syncing your recent guest activity.
      </div>
    </div>
  )}
  <div className="max-w-6xl mx-auto px-2 md:px-4">
    <div className="flex items-center justify-between gap-2">
      {/* Left: brand */}
      <div className="flex items-center min-w-0 flex-shrink">
        <img
          src="/icon-1024.png"
          alt="GweffIT"
          className="h-9 md:h-10 w-auto object-contain"
          loading="eager"
          decoding="async"
          onError={(e) => {
            console.error('[brand] logo load failed', e?.currentTarget?.src);
            e.currentTarget.style.display = 'none';
          }}
        />
      </div>

      {/* Middle: action buttons (desktop inline, mobile dropdown) */}
{/* Desktop (md+): inline buttons */}
<div className="hidden md:flex items-center gap-2">
  <Link
    to="/leaderboard"
    className="flex items-center gap-2 bg-pink-600 hover:bg-pink-500 text-white px-3 py-2 rounded-full font-bold text-xs"
    title="Leaderboard"
  >
    <Trophy className="w-4 h-4" />
    <span>Leaderboard</span>
  </Link>

  {!BETA_MODE && (
    <button
      onClick={() => setShowUpgrade(true)}
      className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 px-3 py-2 rounded-full font-bold text-xs"
      title="Upgrade"
    >
      <Crown className="w-4 h-4" />
      <span>Upgrade</span>
    </button>
  )}

  <button
    onClick={openUnreadComebacks}
    className="relative flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-full font-bold text-xs"
    title={unreadComebackCount > 0 ? `${unreadComebackCount} unread updates` : 'Open recent updates'}
  >
    <span className="relative inline-flex">
      <Bell className="w-4 h-4" />
      {unreadComebackCount > 0 && (
        <span className="absolute -right-2 -top-2 inline-flex min-w-[18px] items-center justify-center rounded-full bg-purple-500 px-1.5 py-0.5 text-[10px] font-black text-white">
          {unreadComebackBadge}
        </span>
      )}
    </span>
    <span>{unreadComebackCount > 0 ? 'Updates' : 'Recent'}</span>
  </button>

  {/* Existing “More” menu stays for desktop */}
  <div className="relative">
    <button
      onClick={() => setShowMore(prev => !prev)}
      className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 px-3 py-2 rounded-full font-bold text-xs"
      title="More"
    >
      <span className="text-lg leading-none">⋯</span>
      <span>More</span>
    </button>
    {showMore && (
      <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-700 bg-slate-800 shadow-lg z-50">
        <div className="p-2 flex flex-col">
          <button
            onClick={() => { setShowHowToPlay(true); setShowMore(false); }}
            className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm w-full"
          >
            How to Play
          </button>
          {!BETA_MODE && (
            <button
              onClick={() => { setShowBuyCoins(true); setShowMore(false); }}
              className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
            >
              Buy Coins
            </button>
          )}
          {!BETA_MODE && (
            <button
              onClick={openSeeder}
              className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-300 text-left text-sm"
            >
              Seeder Tools
            </button>
          )}
          {runtimeDebugEnabled && (
            <>
              <Link
                to="/diagnostics"
                onClick={() => setShowMore(false)}
                className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
              >
                Diagnostics
              </Link>
              <Link
                to="/admin/reports"
                onClick={() => setShowMore(false)}
                className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
              >
                Admin Reports
              </Link>
            </>
          )}
          {BETA_MODE && (
            <button
              onClick={() => {
                setShowMore(false);
                handleOpenSettingsPanel();
              }}
              className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
            >
              ⚙️ Settings / About (beta)
            </button>
          )}
       <button
  onClick={() => {
    setShowMore(false);
    handleLogout();
  }}
  className="w-full text-left px-4 py-3 hover:bg-slate-700 text-slate-200"
>
  🚪 Logout
</button>
 </div>
      </div>
    )}
  </div>
</div>

{/* Mobile (<md): single Actions dropdown */}
<div className="md:hidden relative">
  <button
    onClick={() => setShowActions(prev => !prev)}
    className="relative flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 px-3 py-2 rounded-full font-bold text-xs"
    title="Actions"
  >
    <span className="text-lg leading-none">☰</span>
    <span>Actions</span>
    {unreadComebackCount > 0 && (
      <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-purple-500 px-1.5 py-0.5 text-[10px] font-black text-white">
        {unreadComebackBadge}
      </span>
    )}
  </button>

  {showActions && (
    <div className="absolute left-0 mt-2 w-64 rounded-xl border border-slate-700 bg-slate-800 shadow-lg z-50">
      <div className="p-2 flex flex-col">
        <Link
          to="/leaderboard"
          onClick={() => setShowActions(false)}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-sm"
        >
          🏆 Leaderboard
        </Link>

        <button
          onClick={openUnreadComebacks}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
        >
          {unreadComebackCount > 0 ? `🔔 What Changed (${unreadComebackBadge})` : '🔔 What Changed'}
        </button>

         {!BETA_MODE && (
          <button
            onClick={() => { setShowUpgrade(true); setShowActions(false); }}
            className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
          >
            👑 Upgrade
          </button>
        )}

          <button
          onClick={() => {
  if (pushStatus === 'enabled') {
    alert('✅ Notifications are already enabled!');
  } else if (pushStatus === 'blocked') {
    alert('❌ Notifications are blocked. To enable:\n1. Click the lock icon in address bar\n2. Click Notifications\n3. Select "Allow"\n4. Refresh the page');
  } else {
    setShowPushPrompt(true);
  }
}}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
        >
          🔔 Notifications ({pushStatus === 'enabled' ? 'On' : pushStatus === 'blocked' ? 'Blocked' : pushStatus === 'unsupported' ? 'N/A' : 'Off'})
        </button>
{!BETA_MODE && (
  <button
    onClick={() => {
      setShowActions(false);
      testFCM();
    }}
    className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
  >
    🧪 Send Test Push
  </button>
)}
        <button
          type="button"
          onClick={() => {
            setShowActions(false);
            setShowHowToPlay(true);
          }}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-sm text-left w-full"
        >
          ❓ How to Play
        </button>

        <Link
          to="/coin-shop"
          onClick={() => setShowActions(false)}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-sm"
        >
          💰 Coin Shop
        </Link>

        {runtimeDebugEnabled && (
          <>
            <Link
              to="/diagnostics"
              onClick={() => setShowActions(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-sm"
            >
              🩺 Diagnostics
            </Link>
            <Link
              to="/admin/reports"
              onClick={() => setShowActions(false)}
              className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-sm"
            >
              🛡️ Admin Reports
            </Link>
          </>
        )}

        {!BETA_MODE && (
          <button
            onClick={() => { setShowBuyCoins(true); setShowActions(false); }}
            className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
          >
            💰 Buy Coins
          </button>
        )}

        {!BETA_MODE && (
          <button
            onClick={() => { openSeeder(); setShowActions(false); }}
            className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-300 text-left text-sm"
          >
            🧪 Seeder Tools
          </button>
        )}
        {/* 👇 New Settings / About item for beta mode */}
      {BETA_MODE && (
        <button
          onClick={() => {
            setShowActions(false);
            handleOpenSettingsPanel();
          }}
          className="px-3 py-2 rounded-lg hover:bg-slate-700 text-slate-100 text-left text-sm"
        >
          ⚙️ Settings / About (beta)
        </button>
      )}
    </div>
  </div>
)}
</div>

      {/* Right: user summary */}
      <div className="text-right">
        <div className="font-bold flex items-center justify-end gap-2 text-slate-100">
          <span className={`${view === 'home' ? 'text-xl' : 'text-2xl'}`}>{(AVATARS?.[user?.selectedAvatar] || AVATARS?.default || { emoji: '🙂' }).emoji}</span>
          {user?.username || 'User'}
        </div>
        {view === 'home' ? (
          <div className="mt-1 text-[11px] text-slate-400">
            {Number(user?.points || 0)} XP • {Number(user?.streak || 0)} streak • {Number(user?.coins || 0).toLocaleString()} coins
          </div>
        ) : (
          <div className="text-xs text-purple-200 flex items-center justify-end gap-3">
            <span>{Number(user?.points || 0)} XP</span>
            <span>•</span>
            <span>{Number(user?.streak || 0)}🔥</span>
            <span>•</span>
            <Link
              to="/coin-shop"
              className="font-bold text-yellow-300 flex items-center gap-1 hover:text-yellow-200"
              title="Open Coin Shop"
            >
              💰 {Number(user?.coins || 0).toLocaleString()}
            </Link>
          </div>
        )}
      </div>
    </div>
  </div>

      {/* Navigation */}
<div className="bg-slate-800/90 border-b-2 border-slate-700 sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:backdrop-blur">
  <div className="max-w-6xl mx-auto px-4 py-2">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <button
        onClick={() => setView('home')}
        className={`inline-flex items-center justify-center rounded-lg transition-all h-10 text-sm font-semibold
          ${view === 'home'
            ? 'bg-purple-600 text-white shadow-md'
            : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
        aria-current={view === 'home' ? 'page' : undefined}
      >
        Active Trials
      </button>

      <button
        type="button"
        onClick={handleProtectedCreateTrial}
        className="inline-flex items-center justify-center rounded-lg transition-all h-10 px-4 text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-md transform hover:scale-105"
      >
        ⚖️ Create Trial
      </button>

      <button
        onClick={() => setView('profile')}
        className={`inline-flex items-center justify-center rounded-lg transition-all h-10 text-sm font-semibold
          ${view === 'profile'
            ? 'bg-blue-500 text-white shadow-md'
            : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}
      >
        Profile
      </button>

      <Link
        to="/verdict-vault"
        className="inline-flex items-center justify-center rounded-lg transition-all h-10 text-sm font-semibold bg-slate-700 text-slate-200 hover:bg-slate-600 text-center"
      >
        Verdict Vault
      </Link>
    </div>
  </div>
</div>

      {/* Main Content */}
<div className="max-w-6xl mx-auto p-4">
        {view === 'home' && (
  <div>
    <div
      className="pointer-events-none sticky top-0 z-20 flex justify-center"
      style={{
        height: `${homeRefreshing ? 56 : Math.max(0, homePullDistance)}px`,
        marginBottom: homeRefreshing || homePullDistance > 0 ? '8px' : '0',
        transition: homeRefreshing ? 'height 160ms ease' : 'none',
      }}
      aria-hidden="true"
    >
      {(homeRefreshing || homePullDistance > 0) && (
        <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-slate-800/95 px-3 py-2 text-xs font-semibold text-cyan-100 shadow-lg">
          <span className={`inline-block h-3 w-3 rounded-full border-2 border-cyan-300 border-t-transparent ${homeRefreshing ? 'animate-spin' : ''}`} />
          <span>
            {homeRefreshing
              ? 'Refreshing Home…'
              : homePullDistance >= HOME_PULL_TRIGGER_PX
                ? 'Release to refresh'
                : 'Pull to refresh'}
          </span>
        </div>
      )}
    </div>
    {(() => {
      const homeHasLiveHero = liveNowTrials.length > 0;
      const homeWhatChangedItems = homeWhatChangedPreviewItems;
      const showHomeWhatChanged = !!authUser?.id && (inboxLoading || inboxError || homeWhatChangedItems.length > 0);
      const dailyDecisionNode = (
        <div id="home-daily-decision">
          <InlineErrorBoundary>
            <DailyDecisionVote
              authUser={authUser}
              userId={user?.id}
              onRequireAuth={() => setShowAuthModal(true)}
              onCrewChallengeVoteSuccess={handleCrewChallengeVoteSuccess}
              homeHeroMode
            />
          </InlineErrorBoundary>
        </div>
      );
      const dailyFallbackNode = (
        <div className="max-w-3xl rounded-xl border border-slate-800/80 bg-slate-950/30 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Daily Decision
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-300">
            One fresh call for everyone to weigh in on.
          </div>
          <div className="mt-3">
            {dailyDecisionNode}
          </div>
        </div>
      );

      return (
        <>
          {authUser && !firstVoteLoading && hasCompletedFirstVote === false && !showAuthModal && (
            <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-cyan-500/12 via-slate-900 to-purple-500/12 p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    Welcome to GweffIT
                  </div>
                  <div className="mt-1 text-xl font-black text-white">
                    Settle arguments with a jury, then send the verdict.
                  </div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-300">
                    Vote on a live trial or create your own call. Once the jury lands, you get a receipt worth sharing.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => document.getElementById('home-live-trials')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-cyan-400"
                  >
                    Vote on a Trial
                  </button>
                  <button
                    onClick={handleProtectedCreateTrial}
                    className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-bold text-slate-100 hover:bg-slate-700"
                  >
                    Create a Trial
                  </button>
                </div>
              </div>
            </div>
          )}
          {trialsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4" />
                <div className="text-slate-300 font-bold">Loading trials...</div>
              </div>
            </div>
          ) : (
            <>
              {/* Welcome Banner 
              {showWelcomeBanner && (
                    <div className="mb-6 bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl p-6 border-4 border-orange-600 relative">
                      <button
                        onClick={() => setShowWelcomeBanner(false)}
                        className="absolute top-3 right-3 text-white/80 hover:text-white"
                      >
                        <X size={20} />
                      </button>
                      <div className="flex items-start gap-4">
                        <div className="text-5xl">⚡</div>
                        <div className="flex-1">
                          <h3 className="text-3xl font-black text-white mb-2">Trials are LIVE!</h3>
                          <p className="text-white/90 mb-3">Settle arguments in 2 minutes with a 6-person jury (beta). If it's 3-3? SUDDEN DEATH! 🔥</p>
                          <button
                            onClick={() => setHidePushBanner(true)}
                            className="bg-white text-orange-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-50 transition-all"
                          >
                            Let's Go!
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                    */}
      {/* === Push Nudge Banner (Step 5D) — paste right before the Live Trials <div className="mb-6"> === */}

      {/* === end 5D === */}

              <div className="space-y-6">
                {homeHasLiveHero && (
                  <InlineErrorBoundary>
                    <HomeDiscoverySection
                      homeCrewCount={resolvedHomeCrewCount}
                      homeSearchTerm={homeSearchTerm}
                      setHomeSearchTerm={setHomeSearchTerm}
                      searchLoading={searchLoading}
                      searchResults={searchResults}
                      onOpenTrial={(row) => navigate(`/t/${row.id}`)}
                      homeFeedScope={homeFeedScope}
                      setHomeFeedScope={setHomeFeedScope}
                      followingCount={follows.size}
                      liveNowTrials={liveNowTrials}
                      recentTrials={recentTrials}
                      newTrialIds={newTrialIds}
                      TrialCard={TrialCard}
                      featuredBankCards={featuredBankCards}
                      FEATURED_TRIAL_TEMPLATES={FEATURED_TRIAL_TEMPLATES}
                      createFeaturedTrialAndOpen={createFeaturedTrialAndOpen}
                      featuredBankLoading={featuredBankLoading}
                      locallyHiddenTrialIds={locallyHiddenTrialIds}
                      authUser={authUser}
                      loadFeaturedBankCards={loadFeaturedBankCards}
                      isFirstSession={hasCompletedFirstVote === false}
                      showLiveHero
                      showBrowseSections={false}
                    />
                  </InlineErrorBoundary>
                )}

                <div id="home-what-changed-anchor" className="max-w-4xl space-y-4">
                  <HomeWhatChangedSection
                    show={showHomeWhatChanged}
                    items={homeWhatChangedItems}
                    loading={inboxLoading}
                    error={inboxError}
                    isFirstSession={hasCompletedFirstVote === false}
                    onOpenItem={async (item) => {
                      try {
                        if (item.is_unread) {
                          if (item.kind === 'inbox_item') {
                            await supabase
                              .from('inbox_items')
                              .update({ is_read: true, read_at: new Date().toISOString() })
                              .eq('id', item.id);
                          } else if (item.kind === 'summons') {
                            const summonId = String(item.id || '').replace('summon:', '');
                            await supabase
                              .from('summons')
                              .update({ status: 'clicked', clicked_at: new Date().toISOString() })
                              .eq('id', summonId);
                          }
                          setInboxSummons((prev) => prev.map((row) => (
                            row.id === item.id
                              ? {
                                  ...row,
                                  status: 'clicked',
                                  is_unread: false,
                                  read_at: new Date().toISOString(),
                                }
                              : row
                          )));
                        }
                      } catch (err) {
                        console.warn('[summon] inbox_mark_seen_error', {
                          summon_id: item.id,
                          trial_id: item.trial_id,
                          error: err?.message || String(err),
                        });
                      }
                      const fallbackPath = item.trial_id ? `/t/${item.trial_id}` : '/';
                      navigate(item.cta_url || fallbackPath, {
                        state: {
                          comebackRole: item.verdictRole || null,
                          comebackSource: 'what_changed',
                        },
                      });
                    }}
                  />

                  {!!authUser?.id && (
                    <HomeCrewBanner
                      show={showCrewStreakRiskBanner}
                      crewsAtRisk={crewsAtRisk}
                      crewRiskBannerLabel={crewRiskBannerLabel}
                      onVoteNow={handleCrewChallengeVoteNow}
                      homeCrewCount={resolvedHomeCrewCount}
                      onCreateCrew={handleProtectedCreateCrew}
                      onOpenMyCrews={() => setView('profile')}
                      onOpenCrewArena={() => {
                        if (crewsAtRisk[0]?.crew_id) {
                          navigate(`/crew/${crewsAtRisk[0].crew_id}`);
                          return;
                        }
                        if (!homeSearchTerm.trim()) setHomeFeedScope('crews');
                      }}
                      onViewCrewTrials={() => !homeSearchTerm.trim() && setHomeFeedScope('crews')}
                    />
                  )}

                  {!homeHasLiveHero && dailyFallbackNode}
                </div>

                <div className="border-t border-slate-800/70 pt-5">
                  <div className="mb-3 max-w-4xl">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                      More to vote on
                    </div>
                  </div>
                  <InlineErrorBoundary>
                    <HomeDiscoverySection
                      homeCrewCount={resolvedHomeCrewCount}
                      homeSearchTerm={homeSearchTerm}
                      setHomeSearchTerm={setHomeSearchTerm}
                      searchLoading={searchLoading}
                      searchResults={searchResults}
                      onOpenTrial={(row) => navigate(`/t/${row.id}`)}
                      homeFeedScope={homeFeedScope}
                      setHomeFeedScope={setHomeFeedScope}
                      followingCount={follows.size}
                      liveNowTrials={liveNowTrials}
                      recentTrials={recentTrials}
                      newTrialIds={newTrialIds}
                      TrialCard={TrialCard}
                      featuredBankCards={featuredBankCards}
                      FEATURED_TRIAL_TEMPLATES={FEATURED_TRIAL_TEMPLATES}
                      createFeaturedTrialAndOpen={createFeaturedTrialAndOpen}
                      featuredBankLoading={featuredBankLoading}
                      locallyHiddenTrialIds={locallyHiddenTrialIds}
                      authUser={authUser}
                      loadFeaturedBankCards={loadFeaturedBankCards}
                      isFirstSession={hasCompletedFirstVote === false}
                      showLiveHero={false}
                      showBrowseSections
                      secondaryMode
                    />
                  </InlineErrorBoundary>
                </div>
              </div>
            </>
          )}
        </>
      );
    })()}
  </div>
)}

        
        {view === 'profile' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800 rounded-2xl shadow-2xl p-8 border-2 border-purple-500/30">
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full mx-auto mb-4 flex items-center justify-center text-4xl">
                  {AVATARS[user.selectedAvatar].emoji}
                </div>
                <h2 className="text-3xl font-black text-slate-50">{user.username}</h2>
                <button
                  type="button"
                  className="mt-1 text-[11px] text-slate-400 hover:text-slate-300"
                  onMouseDown={() => {
                    clearTimeout(profileVersionLongPressRef.current);
                    profileVersionLongPressRef.current = setTimeout(() => navigate('/diagnostics'), 800);
                  }}
                  onMouseUp={() => clearTimeout(profileVersionLongPressRef.current)}
                  onMouseLeave={() => clearTimeout(profileVersionLongPressRef.current)}
                  onTouchStart={() => {
                    clearTimeout(profileVersionLongPressRef.current);
                    profileVersionLongPressRef.current = setTimeout(() => navigate('/diagnostics'), 800);
                  }}
                  onTouchEnd={() => clearTimeout(profileVersionLongPressRef.current)}
                >
                  v{process.env.REACT_APP_VERSION || 'beta'} ({process.env.REACT_APP_BUILD || 'dev'})
                </button>
                <div className="text-purple-600 font-bold text-lg mt-2">Juror Rank: Legendary</div>

                <div className="mt-3 text-sm text-slate-300">
                  Avatar: <span className="font-bold text-purple-600">{AVATARS[user.selectedAvatar].name}</span>
                </div>
                                
                {!BETA_MODE && (
                  <button
                    onClick={() => setShowAvatarShop(true)}
                    className="mt-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-2 rounded-full font-bold text-sm hover:from-pink-600 hover:to-purple-600 inline-flex items-center gap-2"
                  >
                    🎨 Avatar Shop
                  </button>
                )}

                {!BETA_MODE && !user.isPremium && (
                  <button
                    onClick={() => setShowPremiumModal(true)}
                    className="mt-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-purple-900 px-6 py-2 rounded-full font-bold text-sm hover:from-yellow-300 hover:to-orange-300 inline-flex items-center gap-2"
                  >
                    <Crown size={16} />
                    Upgrade to Premium
                  </button>
                )}
              </div>

              {/* Compact user chips (mobile-first) */}
<div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-4 md:mb-6">
  {/* XP */}
  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  bg-slate-700/60 border border-slate-600/60 text-[12px] md:text-sm text-slate-100">
    <span>✨</span>
    <span className="font-semibold">{user.points}</span>
    <span className="opacity-70">XP</span>
  </div>

  {/* Streak */}
  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  bg-slate-700/60 border border-slate-600/60 text-[12px] md:text-sm text-slate-100">
    <span>🔥</span>
    <span className="font-semibold">{user.streak}</span>
    <span className="opacity-70">streak</span>
  </div>

  {/* Trials */}
  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  bg-slate-700/60 border border-slate-600/60 text-[12px] md:text-sm text-slate-100">
    <span>⚖️</span>
    <span className="font-semibold">{user.trialsJoined}</span>
    <span className="opacity-70">trials</span>
  </div>

  {/* Coins */}
  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  bg-slate-700/60 border border-slate-600/60 text-[12px] md:text-sm text-slate-100">
    <span>🪙</span>
    <span className="font-semibold">{user.coins.toLocaleString()}</span>
  </div>
</div>

              {/* Summons Stats */}
<div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-6 mb-8 border-2 border-orange-300">
  <h3 className="font-black text-lg mb-4 text-gray-900">👥 Jury Summons Stats</h3>
  <div className="grid grid-cols-3 gap-4">
    <div className="text-center">
      <div className="text-3xl font-black text-orange-600">
        {user.summons_sent || 0}
      </div>
      <div className="text-xs text-gray-600">Sent</div>
    </div>
    <div className="text-center">
      <div className="text-3xl font-black text-green-600">
        {user.summons_accepted || 0}
      </div>
      <div className="text-xs text-gray-600">Accepted</div>
    </div>
    <div className="text-center">
      <div className="text-3xl font-black text-purple-600">
        {user.summons_accepted > 0 
          ? ((user.summons_accepted || 0) / user.summons_sent * 100).toFixed(0)
          : 0}%
      </div>
      <div className="text-xs text-gray-600">Accept Rate</div>
    </div>
  </div>
</div>

              {/* Creator Dashboard Button */}
              <div className="mb-8">
                <button
                  onClick={() => setShowCreatorAnalytics(true)}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                >
                  <Trophy size={24} />
                  View Creator Dashboard
                </button>
                <p className="text-xs text-slate-400 text-center mt-2">
                  See your trials, earnings, and creator tier
                </p>
              </div>

              <div className="mb-8 bg-slate-900/70 rounded-xl p-5 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-lg text-slate-50">Referral Progress</h3>
                  {referralStatsLoading ? (
                    <span className="text-xs text-slate-400">Loading…</span>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-700 bg-slate-800/80 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">Total Invites</div>
                    <div className="mt-1 text-2xl font-black text-slate-50">{referralStats.totalInvites}</div>
                  </div>
                  <div className="rounded-xl border border-slate-700 bg-slate-800/80 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">Joined</div>
                    <div className="mt-1 text-2xl font-black text-slate-50">{referralStats.joinedCount}</div>
                  </div>
                  <div className="rounded-xl border border-slate-700 bg-slate-800/80 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">First Vote</div>
                    <div className="mt-1 text-2xl font-black text-slate-50">{referralStats.firstVoteConversions}</div>
                  </div>
                  <div className="rounded-xl border border-slate-700 bg-slate-800/80 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">Rewards Earned</div>
                    <div className="mt-1 text-2xl font-black text-yellow-300">{referralStats.totalReferralRewardsEarned}</div>
                  </div>
                </div>
              </div>

              {/* ADD THIS - Crews Section */}
<div className="mb-8">
  <CrewsList 
    currentUserId={user.id}
    memberUserId={authUser?.id}
    refreshKey={crewRefreshKey}
    onCreateCrew={handleProtectedCreateCrew}
  />
</div>

              <div className="space-y-4">
                <h3 className="font-black text-xl text-slate-50 mb-4">Achievements</h3>
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-4 border-2 border-purple-500/30">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">🎯</div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-50">First Vote</div>
                      <div className="text-sm text-slate-300">Cast your first verdict</div>
                    </div>
                    <div className="text-green-600 font-bold">✓</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-4 border-2 border-purple-500/30">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">🔥</div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-50">Week Warrior</div>
                      <div className="text-sm text-slate-300">7 day voting streak</div>
                    </div>
                    <div className="text-green-600 font-bold">✓</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-4 border-2 border-purple-500/30 opacity-50">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">👑</div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-50">Century Club</div>
                      <div className="text-sm text-slate-300">Vote in 100 trials</div>
                    </div>
                    <div className="text-gray-400 text-sm">23/100</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 rounded-xl border border-purple-500/60 shadow-lg">
  <h3 className="font-black text-base sm:text-lg text-purple-100 mb-3 flex items-center gap-2">
    <Sparkles className="text-amber-300" />
    Spend Your Coins
  </h3>

  <div className="grid grid-cols-3 gap-3 mb-4">
    {/* Avatar Shop */}
    {!BETA_MODE && (
      <button
        onClick={() => setShowAvatarShop(true)}
        className="bg-slate-900/80 hover:bg-purple-800 rounded-lg p-3 text-center transition-all border border-purple-500/40 hover:border-purple-300/80 shadow-md"
      >
        <div className="text-3xl mb-1">🎨</div>
        <div className="text-[11px] font-semibold text-slate-50">Avatar Shop</div>
        <div className="text-[11px] text-emerald-300 font-bold mt-1 tracking-wide">
          AVAILABLE!
        </div>
      </button>
    )}

    {/* Gavel Effects – disabled */}
    <div className="bg-slate-900/60 rounded-lg p-3 text-center border border-slate-700/70 opacity-70">
      <div className="text-3xl mb-1">⚡</div>
      <div className="text-[11px] font-semibold text-slate-200">Gavel Effects</div>
      <div className="text-[11px] text-slate-400 mt-1">Coming Soon</div>
    </div>
  </div>

  <div className="text-[11px] text-slate-200 text-center">
    You own{' '}
    <span
      className="font-bold text-purple-300"
      title="Collect all 25 unique avatars!"
    >
      {user.ownedAvatars.length}/{Object.keys(AVATARS).length}
    </span>{' '}
    avatars
  </div>
</div>
            </div>
          </div>
        )}
      </div>

      {/* ======= Modals & Styles INSIDE return ======= */}
            {showSuddenDeath && <SuddenDeathModal />}
            {showVerdict && (
        <>
          {/* HIDDEN VIRAL SHARE CARD — THIS IS WHAT GETS SHARED */}
          <VerdictShareCard verdictData={verdictData} user={user} />

          {/* YOUR CURRENT VERDICT MODAL — LOOKS EXACTLY THE SAME TO THE USER */}
          <VerdictModal
            verdictData={verdictData}
            setVerdictData={setVerdictData}
            setShowVerdict={setShowVerdict}
            user={user}
            shownVerdicts={shownVerdicts}
            setShownVerdicts={setShownVerdicts}
            downloadMeme={downloadMeme}
            shareMeme={shareMeme}
            isGeneratingMeme={isGeneratingMeme}
            verdictRef={verdictRef}
            setShowPremiumModal={setShowPremiumModal}
            openMemeCreator={openMemeCreator}
          />
        </>
      )}

      {showHowToPlay && <HowToPlayModal onClose={() => setShowHowToPlay(false)} />}
      {showLeaderboard && (
        <LeaderboardModal
          leaderboardData={leaderboardData}
          leaderboardLoading={leaderboardLoading}
          leaderboardTab={leaderboardTab}
          setLeaderboardTab={setLeaderboardTab}
          user={user}
          userRank={userRank}
          leaderboardRows={leaderboardRows}
          dedupedLeaderboardData={dedupedLeaderboardData}
          getLeaderboardMetricValue={getLeaderboardMetricValue}
          getLeaderboardMetricLabel={getLeaderboardMetricLabel}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
      {showGrandSeeder && (
        <GrandTrialSeeder
          authUser={authUser}
          onClose={() => setShowGrandSeeder(false)}
        />
      )}
      {showQuickSeeder && (
        <QuickTrialSeeder
          authUser={authUser}
          onClose={() => setShowQuickSeeder(false)}
        />
      )}
      {showPremiumModal && <PremiumModal />}
      {showBuyCoinsModal && <BuyCoinsModal />}
      {showAvatarShop && <AvatarShopModal />}
      {showCreatorAnalytics && (
        <CreatorAnalytics
          user={user}
          trials={trials}
          authUser={authUser}
          onClose={() => setShowCreatorAnalytics(false)}
        />
      )}
      
      {/* Push notification permission prompt */}
      {showPushPrompt && (
        <PushPermissionPrompt
          onClose={handleDismissPushPrompt}
          onEnable={handleEnablePushPrompt}
        />
      )}
      {/* Enhanced Notification Permission Modal */}
      {showNotificationModal && !isNativeApp() && (
        <NotificationPermissionModal
          onClose={() => setShowNotificationModal(false)}
          onPermissionGranted={handleNotificationPermissionGranted}
          onPermissionDenied={() => setShowNotificationModal(false)}
          triggerContext={notificationTriggerContext}
          coinReward={100}
        />
      )}
      {appNotice?.message && (
        <div className="fixed inset-x-0 top-3 z-[2200] px-4">
          <div
            className={`mx-auto max-w-md rounded-xl border px-4 py-3 text-sm font-semibold shadow-2xl ${
              appNotice.tone === 'error'
                ? 'border-red-500/60 bg-red-950/95 text-red-100'
                : 'border-emerald-500/50 bg-emerald-950/95 text-emerald-100'
            }`}
          >
            {appNotice.message}
          </div>
        </div>
      )}
      
      {/* Create Crew Modal */}
{showCreateCrewModal && (
  <CreateCrewModal
    onClose={() => setShowCreateCrewModal(false)}
    onSuccess={(crew) => {
      setShowCreateCrewModal(false);
      showAppNotice(`Created crew: ${crew.name}!`);
      // Optionally navigate to crew page or refresh data
    }}
    currentUserId={user?.id}
    onRequireAuth={handleProtectedCreateCrew}
  />
)}
      {BETA_MODE && (
        <SettingsPanel
          open={showSettingsPanel}
          onClose={() => setShowSettingsPanel(false)}
          onEnableNotifications={handleEnableNotificationsFromSettings}
          notificationPrefs={notificationPrefs}
          onTogglePref={handleToggleNotificationPref}
          prefsSaving={prefsSaving}
          streakFreezeCount={streakFreezeCount}
          appUserId={user?.id}
          onNavigate={(path) => navigate(path)}
        />
      )}
     </>
  )}
  
    {/* ======= Auth Modal - Available on ALL pages (crew join, main app, etc) ======= */}
  {showAuthModal && (
    <AuthModal
      authMode={authMode}
      setAuthMode={setAuthMode}
      authEmail={authEmail}
      setAuthEmail={setAuthEmail}
      authMessage={authMessage}
      setAuthMessage={setAuthMessage}
      authError={authError}
      setAuthError={setAuthError}
      setShowAuthModal={setShowAuthModal}
      authContext={authContext}
      currentAuthUserId={authUser?.id || null}
      currentAppUserId={user?.id || null}
    />
  )}

  {/* ======= First Vote Onboarding Modal ======= */}
  {showFirstVoteModal && (
    <FirstVoteModal
      onDismiss={dismissFirstVoteModal}
      onStartVoting={handleOnboardingVoteStart}
      onGoCreateTrial={handleOnboardingCreateStart}
    />
  )}

  {showFirstVoteNextStepModal && (
    <FirstVoteModal
      mode="next_step"
      hasCrews={homeCrewCount > 0}
      onDismiss={() => setShowFirstVoteNextStepModal(false)}
      onPrimaryAction={async () => {
        setShowFirstVoteNextStepModal(false);
        if (homeCrewCount > 0) {
          setView('profile');
          return;
        }
        handleProtectedCreateCrew();
      }}
      onGoCreateTrial={async () => {
        setShowFirstVoteNextStepModal(false);
        await handleOnboardingCreateStart();
      }}
    />
  )}
  
{/* Bonus coins indicator from notification clicks */}
{/* Bonus coins indicator from notification clicks */}
{showBonusCoinsIndicator > 0 && (
  <BonusCoinsIndicator 
    bonusCoins={showBonusCoinsIndicator}
    onClose={() => setShowBonusCoinsIndicator(0)}
  />
)}

{/* 🎨 MEME SYSTEM MODALS */}
{showTemplateSelector && verdictForMeme && (
  <TemplateSelectorModal
    trial={verdictForMeme.trial}
    verdict={verdictForMeme}
    onClose={() => {
      setShowTemplateSelector(false);
      setVerdictForMeme(null);
    }}
  />
)}

{showShareModal && sharedImageBlob && verdictForMeme && (
  <AdvancedShareModal
    imageBlob={sharedImageBlob}
    trial={verdictForMeme.trial}
    verdict={verdictForMeme}
    onClose={() => {
      setShowShareModal(false);
      setSharedImageBlob(null);
    }}
  />
)}
{/* PWA Install Prompt */}
{/*<PWAInstallPrompt />*/}

</>
    )}
  </div>
);
}

/// ========= ROUTING SETUP =========

// Redirect legacy /trial/:id to canonical /t/:id
function TrialRedirect() {
  const { id } = useParams();
  console.log('[TrialRedirect] Redirecting /trial/' + id + ' to /t/' + id);
  return <Navigate to={`/t/${id}`} replace />;
}

// Redirect /invite/:token to canonical crew join path
function InviteRedirect() {
  const { token } = useParams();
  console.log('[InviteRedirect] Redirecting /invite/' + token + ' to /c/' + token);
  return <Navigate to={`/c/${token}`} replace />;
}

function LeaderboardModal({
  leaderboardData,
  leaderboardLoading,
  leaderboardTab,
  setLeaderboardTab,
  user,
  userRank,
  leaderboardRows,
  dedupedLeaderboardData,
  getLeaderboardMetricValue,
  getLeaderboardMetricLabel,
  onClose,
}) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!leaderboardData && leaderboardLoading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="text-white text-2xl font-black">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div
        className="bg-slate-800 rounded-3xl p-8 max-w-4xl w-full relative max-h-[90vh] overflow-y-auto border-2 border-purple-500"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leaderboard-title"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-300"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <div className="text-6xl mb-4">🏆</div>
          <h2 id="leaderboard-title" className="text-3xl font-black text-slate-50">Leaderboards</h2>
          <p className="text-slate-300">Top jurors on GweffIT</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setLeaderboardTab('xp')}
            disabled={leaderboardLoading}
            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
              leaderboardTab === 'xp'
                ? 'bg-purple-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            } ${leaderboardLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            ⚡ XP Leaders
          </button>
          <button
            onClick={() => setLeaderboardTab('streak')}
            disabled={leaderboardLoading}
            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
              leaderboardTab === 'streak'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            } ${leaderboardLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            🔥 Streak Leaders
          </button>
          <button
            onClick={() => setLeaderboardTab('coins')}
            disabled={leaderboardLoading}
            className={`flex-1 py-3 rounded-xl font-bold transition-all ${
              leaderboardTab === 'coins'
                ? 'bg-yellow-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            } ${leaderboardLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            💰 Coin Leaders
          </button>
        </div>

        {userRank && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 mb-6 border-2 border-purple-400">
            <div className="flex items-center gap-3">
              <div className="text-3xl shrink-0">👤</div>
              <div className="min-w-0 flex-1">
                <div className="font-black text-white truncate">{user.username}</div>
                <div className="text-sm text-purple-200">Your Rank: #{userRank}</div>
              </div>
              <div className="shrink-0 w-24 text-right">
                <div className="text-xl font-black text-white leading-tight">
                  {getLeaderboardMetricValue(user)}
                </div>
                <div className="text-[11px] text-purple-200 leading-tight">
                  {leaderboardTab === 'xp' && 'XP'}
                  {leaderboardTab === 'streak' && 'Streak'}
                  {leaderboardTab === 'coins' && 'Coins'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {leaderboardRows && leaderboardRows.length > 0 ? (
            leaderboardRows.map((entry, index) => {
              const originalRank = dedupedLeaderboardData.findIndex((row) => String(row?.id || '') === String(entry?.id || ''));
              const displayRank = originalRank >= 0 ? originalRank + 1 : index + 1;
              const isCurrentUser = String(entry?.id || '') === String(user?.id || '');
              const rankEmoji = displayRank === 1 ? '🥇' : displayRank === 2 ? '🥈' : displayRank === 3 ? '🥉' : '';

              return (
                <div
                  key={entry.id}
                  className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                    isCurrentUser
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 border-2 border-purple-400'
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="shrink-0 w-10 text-base font-black text-slate-300 text-center">
                      {rankEmoji || `#${displayRank}`}
                    </div>
                    <div className="text-2xl shrink-0">👤</div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold text-sm truncate ${isCurrentUser ? 'text-white' : 'text-slate-200'}`}>
                        {entry.username}
                      </div>
                      <div className={`text-[11px] truncate ${isCurrentUser ? 'text-purple-200' : 'text-slate-400'}`}>
                        {getLeaderboardMetricLabel(entry)}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 w-20 text-right pl-3">
                    <div className={`text-lg font-black leading-tight ${isCurrentUser ? 'text-white' : 'text-slate-200'}`}>
                      {getLeaderboardMetricValue(entry)}
                    </div>
                    <div className={`text-[10px] leading-tight ${isCurrentUser ? 'text-purple-200' : 'text-slate-500'}`}>
                      {leaderboardTab === 'xp' && 'XP'}
                      {leaderboardTab === 'streak' && 'Streak'}
                      {leaderboardTab === 'coins' && 'Coins'}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-slate-400">
              <div className="text-6xl mb-4">🏆</div>
              <p className="text-xl font-bold mb-2">No rankings yet!</p>
              <p className="text-sm">Be the first to vote and claim #1! 🚀</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  // keep the last-login ping (safe no-op if user isn't signed in)
  useUpdateLastLogin();

  return (
    <FollowsProvider>
      <BrowserRouter>
        <InlineErrorBoundary>
          <Routes>
            {/* Main app shell */}
            <Route path="/" element={<GweffIT initialView="home" />} />
            <Route path="/profile" element={<GweffIT initialView="profile" />} />
            <Route path="/user/:username" element={<PublicUserProfile />} />

            {/* Trial detail - canonical share path */}
            <Route path="/t/:id" element={<TrialDetail />} />
            {/* Trial detail - legacy redirect */}
            <Route path="/trial/:id" element={<TrialRedirect />} />

            {/* Verdict Vault (canonical path) + redirects */}
            <Route path="/verdict-vault" element={<VerdictVault />} />
            <Route path="/vault" element={<Navigate to="/verdict-vault" replace />} />
            <Route path="/archive" element={<Navigate to="/verdict-vault" replace />} />

            {/* Daily Decision detail */}
            <Route path="/daily/:id" element={<DailyDecisionVerdict />} />

            {/* Create Trial page */}
            <Route path="/create" element={<CreateTrial />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/how-it-works" element={<Navigate to="/about" replace />} />
            <Route path="/coin-shop" element={<CoinShop />} />
            <Route path="/crew/:id" element={<CrewPage />} />
            <Route path="/diagnostics" element={<Diagnostics />} />
            <Route path="/admin/reports" element={<AdminReports />} />

            {/* Leaderboard */}
            <Route path="/leaderboard" element={<LeaderboardRedirect />} />

            {/* Summons deep link */}
            <Route path="/summons/:token" element={<SummonsLanding />} />

            {/* Crew join - canonical short share path */}
            <Route path="/c/:token" element={<CrewJoinPage />} />
            {/* Crew join - legacy path */}
            <Route path="/crew/join/:token" element={<CrewJoinPage />} />
            {/* Invite redirect */}
            <Route path="/invite/:token" element={<InviteRedirect />} />

            {/* Catch-all for any other paths */}
            <Route path="*" element={<GweffIT />} />
          </Routes>
        </InlineErrorBoundary>
      </BrowserRouter>
    </FollowsProvider>
  );
}

export default App;
